import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getValidToken, API_URL } from '../../utils/authUtils';
import { useAdminPermissions, canAccessSectionSync, ADMIN_SECTIONS } from '../../utils/adminPermissions';

const BACKEND_URL = API_URL;

// Anchor color mapping
const ANCHOR_COLORS = {
  'Pipeline Insurance':    { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  'Skills-First Pivot':    { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  'Operational Re-Org':    { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
  'The Human Premium':     { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
  'Geographic Arbitrage':  { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  'Compensation Shift':    { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-300' },
};

// Signal source badge colors (for multi-source "Latest Signals" column)
const SOURCE_COLORS = {
  'SWEE':          'bg-emerald-100 text-emerald-800 border-emerald-300',
  'WI':            'bg-indigo-100 text-indigo-800 border-indigo-300',
  'WARN':          'bg-red-100 text-red-800 border-red-300',
  'SEC 8-K':       'bg-amber-100 text-amber-800 border-amber-300',
  'FMP News':      'bg-sky-100 text-sky-800 border-sky-300',
  'FMP Press':     'bg-cyan-100 text-cyan-800 border-cyan-300',
  'FMP Employees': 'bg-violet-100 text-violet-800 border-violet-300',
  'TheirStack':    'bg-blue-100 text-blue-800 border-blue-300',
  'Layoffs.fyi':   'bg-rose-100 text-rose-800 border-rose-300',
};

// Verdict badge colors
const VERDICT_COLORS = {
  'Genuine Pivot':                   'bg-green-100 text-green-800',
  'Developing Strategy':             'bg-yellow-100 text-yellow-800',
  'AI-Washing':                      'bg-red-100 text-red-800',
  'Strategy Reversal':               'bg-red-200 text-red-900',
  'Cosmetic':                        'bg-gray-100 text-gray-600',
  'Cosmetic Signal':                 'bg-gray-100 text-gray-600',
  'Early Indicator':                 'bg-blue-100 text-blue-700',
  'Emerging Pivot':                  'bg-blue-100 text-blue-800',
  'Bold Claim - Verify Next Quarter':'bg-amber-100 text-amber-800',
  'Off-Topic Match':                 'bg-red-50 text-red-400 line-through',
  'Insufficient Data':               'bg-gray-100 text-gray-500',
};

// Score color helper
const getScoreColor = (score) => {
  if (score >= 7) return 'text-green-700 bg-green-50';
  if (score >= 4) return 'text-yellow-700 bg-yellow-50';
  if (score >= 1) return 'text-gray-600 bg-gray-50';
  return 'text-gray-400';
};

const getScoreBadge = (score) => {
  if (score >= 7) return 'bg-green-500';
  if (score >= 4) return 'bg-yellow-500';
  if (score >= 1) return 'bg-gray-400';
  return 'bg-gray-200';
};

// Alert tier colors
const TIER_COLORS = {
  'CRITICAL':  'bg-red-600 text-white',
  'HIGH':      'bg-orange-500 text-white',
  'STANDARD':  'bg-blue-500 text-white',
  'LOW':       'bg-gray-400 text-white',
};

// WI Category colors
const WI_CATEGORY_COLORS = {
  'WORKFORCE_SIZING':      { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  'RETENTION':             { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  'COMPENSATION':          { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  'WORKFORCE_COMPOSITION': { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-300' },
  'WORKFORCE_RISK':        { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  'LEARNING_DEVELOPMENT':  { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  'EXECUTIVE_COMMENTARY':  { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
};

const WI_CATEGORY_LABELS = {
  'WORKFORCE_SIZING':      'Workforce Sizing',
  'RETENTION':             'Retention & Attrition',
  'COMPENSATION':          'Compensation & Benefits',
  'WORKFORCE_COMPOSITION': 'Composition & Diversity',
  'WORKFORCE_RISK':        'Risk & Compliance',
  'LEARNING_DEVELOPMENT':  'Learning & Development',
  'EXECUTIVE_COMMENTARY':  'Executive Commentary',
};

const WI_SIGNIFICANCE_COLORS = {
  'MATERIAL_EVENT':  'bg-red-100 text-red-800 border-red-300',
  'NOTABLE_CHANGE':  'bg-amber-100 text-amber-800 border-amber-300',
  'ROUTINE':         'bg-gray-100 text-gray-600 border-gray-300',
};

const WI_DIRECTION_COLORS = {
  'POSITIVE': 'bg-green-100 text-green-700',
  'NEGATIVE': 'bg-red-100 text-red-700',
  'NEUTRAL':  'bg-gray-100 text-gray-600',
  'MIXED':    'bg-purple-100 text-purple-700',
};

const AdminSWEE = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions, loading: permLoading } = useAdminPermissions();

  // Tab state
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

  // Dashboard state
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  // Companies state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [companySortDir, setCompanySortDir] = useState(null); // null | 'desc' | 'asc'
  const [signalSortDir, setSignalSortDir] = useState(null); // null | 'asc' | 'desc'

  // Trajectory state (single company deep-dive)
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [trajectoryData, setTrajectoryData] = useState(null);
  const [trajectoryLoading, setTrajectoryLoading] = useState(false);

  // Heatmap state
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  // Search state
  const [searchKeywords, setSearchKeywords] = useState('');
  const [searchSemantic, setSearchSemantic] = useState('');
  const [searchTickers, setSearchTickers] = useState('');
  const [searchAnchors, setSearchAnchors] = useState([]);
  const [searchMinScore, setSearchMinScore] = useState('');
  const [searchVerdicts, setSearchVerdicts] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Pipeline state
  const [backfillTickers, setBackfillTickers] = useState('');
  const [backfillStartYear, setBackfillStartYear] = useState(2024);
  const [backfillStartQ, setBackfillStartQ] = useState(1);
  const [backfillEndYear, setBackfillEndYear] = useState(2025);
  const [backfillEndQ, setBackfillEndQ] = useState(4);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [backfillStatusLoading, setBackfillStatusLoading] = useState(false);

  // Pipeline run state
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineResult, setPipelineResult] = useState(null);
  const pipelinePollingRef = useRef(null);

  // Populate & background backfill state
  const [populatingSp500, setPopulatingSp500] = useState(false);
  const [populatingAlumni, setPopulatingAlumni] = useState(false);
  const [populateResult, setPopulateResult] = useState(null);
  const [bgBackfillTarget, setBgBackfillTarget] = useState('all');
  const [bgBackfillStartYear, setBgBackfillStartYear] = useState(2024);
  const [bgBackfillStartQ, setBgBackfillStartQ] = useState(1);
  const [bgBackfillEndYear, setBgBackfillEndYear] = useState(2025);
  const [bgBackfillEndQ, setBgBackfillEndQ] = useState(4);
  const [bgBackfillStarting, setBgBackfillStarting] = useState(false);
  const [bgBackfillJobs, setBgBackfillJobs] = useState([]);

  // Transcript viewer state
  const [transcriptData, setTranscriptData] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptSearch, setTranscriptSearch] = useState('');
  const [expandedTranscript, setExpandedTranscript] = useState(null); // fiscal_period key

  // Financial data state
  const [financialData, setFinancialData] = useState(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialFetching, setFinancialFetching] = useState(false);
  const [financialChartMode, setFinancialChartMode] = useState('revenue'); // revenue | profitability | cashflow | efficiency | workforce | eps
  const [financialChartRange, setFinancialChartRange] = useState('recent'); // 'recent' = 2020+, 'full' = all data (2015+)

  // Centroids state
  const [centroids, setCentroids] = useState(null);
  const [centroidsLoading, setCentroidsLoading] = useState(false);
  const [centroidInitRunning, setCentroidInitRunning] = useState(false);

  // WI Centroids state
  const [wiCentroids, setWiCentroids] = useState(null);
  const [wiCentroidsLoading, setWiCentroidsLoading] = useState(false);
  const [wiCentroidInitRunning, setWiCentroidInitRunning] = useState(false);

  // Subscribers state
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const defaultSubForm = {
    label: '',
    watch_all_sp500: false,
    watch_tickers: '',
    watch_sectors: '',
    watch_alumni_companies: false,
    min_alert_tier: 'STANDARD',
    delivery_email: true,
    delivery_sms: false,
    digest_frequency: 'daily',
  };
  const [subForm, setSubForm] = useState(defaultSubForm);
  const [subSaving, setSubSaving] = useState(false);
  const [editingSubId, setEditingSubId] = useState(null);  // null = new, uuid = editing
  const [showSubForm, setShowSubForm] = useState(false);

  // Calendar state
  const [calendarData, setCalendarData] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarRange, setCalendarRange] = useState('this_week');
  const [calendarStartDate, setCalendarStartDate] = useState('');
  const [calendarEndDate, setCalendarEndDate] = useState('');
  const [calendarFilter, setCalendarFilter] = useState('all');
  const [calendarSearch, setCalendarSearch] = useState('');
  const [addingToWatchlist, setAddingToWatchlist] = useState(null);
  const [backfillingFromCal, setBackfillingFromCal] = useState(null);
  const [downloadingSlides, setDownloadingSlides] = useState(null); // "TICKER-YEAR-Q" key while loading
  const [audioLoading, setAudioLoading] = useState(null);   // "TICKER-YEAR-Q" key while fetching
  const [audioUrl, setAudioUrl] = useState(null);            // presigned S3 URL for active audio
  const [audioKey, setAudioKey] = useState(null);             // which transcript's audio is active

  // Pipeline help modal state
  const [showPipelineHelp, setShowPipelineHelp] = useState(false);

  // Workforce Intelligence state
  const [wiSummary, setWiSummary] = useState(null);
  const [wiSummaryLoading, setWiSummaryLoading] = useState(false);
  const [wiChunks, setWiChunks] = useState(null);
  const [wiChunksLoading, setWiChunksLoading] = useState(false);
  const [wiFilterCategory, setWiFilterCategory] = useState('');
  const [wiFilterSignificance, setWiFilterSignificance] = useState('');
  const [wiFilterDirection, setWiFilterDirection] = useState('');
  const [wiFilterTicker, setWiFilterTicker] = useState('');
  const [wiFilterMinScore, setWiFilterMinScore] = useState('');
  const [wiFilterTemporal, setWiFilterTemporal] = useState('');
  const [wiPage, setWiPage] = useState(1);
  const [wiExpandedChunk, setWiExpandedChunk] = useState(null);
  const [showWIHelp, setShowWIHelp] = useState(false);

  // Hiring Tracker state
  const [trackerCompanies, setTrackerCompanies] = useState([]);
  const [trackerCredits, setTrackerCredits] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerSnapshotLoading, setTrackerSnapshotLoading] = useState(null); // ticker being collected
  const [trackerDetail, setTrackerDetail] = useState(null); // {ticker, snapshots, trends}
  const [trackerDomainEditing, setTrackerDomainEditing] = useState(null); // {ticker, domain}
  const [backfillMonths, setBackfillMonths] = useState(3); // 1-6
  const [backfillTarget, setBackfillTarget] = useState(null); // ticker or 'ALL'
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResults, setBackfillResults] = useState(null);

  // External Signals state
  const [signalsSummary, setSignalsSummary] = useState(null);
  const [signalsSummaryLoading, setSignalsSummaryLoading] = useState(false);
  const [signalsList, setSignalsList] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsSourceFilter, setSignalsSourceFilter] = useState('');
  const [signalsTickerFilter, setSignalsTickerFilter] = useState('');
  const [signalsCategoryFilter, setSignalsCategoryFilter] = useState('');
  const [signalsRelevanceFilter, setSignalsRelevanceFilter] = useState('');
  const [signalsDirectionFilter, setSignalsDirectionFilter] = useState('');
  const [signalsDaysBack, setSignalsDaysBack] = useState(90);
  const [unresolvedList, setUnresolvedList] = useState([]);
  const [unresolvedLoading, setUnresolvedLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveTickerInputs, setResolveTickerInputs] = useState({});  // {unresolvedId: ticker}
  const [scanRunning, setScanRunning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [scanProgress, setScanProgress] = useState(null);
  const [secScope, setSecScope] = useState('tracked');
  const scanProgressRef = useRef(null);

  // Layoffs.fyi CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [csvPreviewCount, setCsvPreviewCount] = useState(null);

  // Recent alerts state
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);

  // Reports tab state (Phase 4c)
  const [reportStats, setReportStats] = useState(null);
  const [reportStatsLoading, setReportStatsLoading] = useState(false);
  const [reportsList, setReportsList] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsTickerFilter, setReportsTickerFilter] = useState('');
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentEventsLoading, setRecentEventsLoading] = useState(false);
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [processingEvents, setProcessingEvents] = useState(false);
  const [enrichTicker, setEnrichTicker] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReportsHelp, setShowReportsHelp] = useState(false);
  const [showCompaniesHelp, setShowCompaniesHelp] = useState(false);
  const [showTrackerHelp, setShowTrackerHelp] = useState(false);

  // LinkedIn Insights state
  const [liStats, setLiStats] = useState(null);
  const [liStatsLoading, setLiStatsLoading] = useState(false);
  const [liLookupTicker, setLiLookupTicker] = useState('');
  const [liLookupResult, setLiLookupResult] = useState(null);
  const [liLookupLoading, setLiLookupLoading] = useState(false);
  const [liUntrackedName, setLiUntrackedName] = useState('');
  const [liUntrackedDomain, setLiUntrackedDomain] = useState('');
  const [liUntrackedResult, setLiUntrackedResult] = useState(null);
  const [liUntrackedLoading, setLiUntrackedLoading] = useState(false);
  const [liHistoryTicker, setLiHistoryTicker] = useState('');
  const [liHistory, setLiHistory] = useState(null);
  const [liHistoryLoading, setLiHistoryLoading] = useState(false);
  const [liBreakdown, setLiBreakdown] = useState(null);
  const [liBreakdownLoading, setLiBreakdownLoading] = useState(false);
  const [liRefreshRunning, setLiRefreshRunning] = useState(false);
  const [liRefreshResult, setLiRefreshResult] = useState(null);
  const [showLiHelp, setShowLiHelp] = useState(false);

  // General
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Auth headers helper
  const getHeaders = useCallback(() => {
    const token = getValidToken();
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Tab definitions
  const TABS = [
    { key: 'dashboard', label: 'Dashboard', icon: '◈' },
    { key: 'calendar', label: 'Calendar', icon: '📅' },
    { key: 'companies', label: 'Companies', icon: '◉' },
    { key: 'trajectory', label: 'Trajectory', icon: '◬' },
    { key: 'heatmap', label: 'Heatmap', icon: '▦' },
    { key: 'search', label: 'Search', icon: '⌕' },
    { key: 'wi', label: 'Workforce Intel', icon: '⊞' },
    { key: 'tracker', label: 'Hiring Tracker', icon: '⬡' },
    { key: 'signals', label: 'Signals', icon: '⚡' },
    { key: 'alerts', label: 'Alerts', icon: '⚑' },
    { key: 'pipeline', label: 'Pipeline', icon: '⚙' },
    { key: 'reports', label: 'Reports', icon: '⊘' },
    { key: 'linkedin-insights', label: 'LinkedIn Insights', icon: '◎' },
    { key: 'calibration', label: 'Calibration', icon: '⚖' },
  ];

  // Tab change handler
  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setError(null);
    setSuccessMsg(null);
  };

  // Sync URL to state
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'dashboard') fetchDashboard();
    else if (activeTab === 'companies') fetchCompanies();
    else if (activeTab === 'heatmap') fetchHeatmap();
    else if (activeTab === 'pipeline') { fetchBackfillStatus(); fetchBackfillJobs(); fetchCentroids(); fetchWiCentroids(); }
    else if (activeTab === 'alerts') { fetchSubscribers(); fetchRecentAlerts(); }
    else if (activeTab === 'calendar') fetchCalendar();
    else if (activeTab === 'wi') { fetchWISummary(); fetchWIChunks(); }
    else if (activeTab === 'tracker') { fetchTrackerCompanies(); }
    else if (activeTab === 'signals') { fetchSignalsSummary(); fetchSignalsList(); fetchUnresolved(); }
    else if (activeTab === 'reports') { fetchReportStats(); fetchReportsList(); fetchRecentEventsList(); }
    else if (activeTab === 'linkedin-insights') { fetchLiStats(); }
    else if (activeTab === 'calibration') { fetchCentroids(); fetchWiCentroids(); }
  }, [activeTab]);

  // =============================================
  // API Fetch Functions
  // =============================================

  const fetchDashboard = async () => {
    setDashboardLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/dashboard`, { headers: getHeaders() });
      setDashboardData(data);
    } catch (err) {
      setError('Failed to load dashboard: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDashboardLoading(false);
    }
  };

  // LinkedIn Insights functions
  const fetchLiStats = async () => {
    setLiStatsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/linkedin-insights/stats`, { headers: getHeaders() });
      setLiStats(data);
    } catch (err) {
      setError('Failed to load LinkedIn Insights stats: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLiStatsLoading(false);
    }
  };

  const fetchLiLookup = async () => {
    if (!liLookupTicker.trim()) return;
    setLiLookupLoading(true);
    setLiLookupResult(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/linkedin-insights/${liLookupTicker.trim().toUpperCase()}`, { headers: getHeaders() });
      setLiLookupResult(data);
      setSuccessMsg(`Fetched LinkedIn insights for ${liLookupTicker.toUpperCase()} (~$0.19)`);
      fetchLiStats();
    } catch (err) {
      setError('LinkedIn lookup failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLiLookupLoading(false);
    }
  };

  const fetchLiUntracked = async () => {
    if (!liUntrackedName.trim()) return;
    setLiUntrackedLoading(true);
    setLiUntrackedResult(null);
    try {
      const params = { company_name: liUntrackedName.trim() };
      if (liUntrackedDomain.trim()) params.domain = liUntrackedDomain.trim();
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/linkedin-insights/untracked-lookup`, { headers: getHeaders(), params });
      setLiUntrackedResult(data);
    } catch (err) {
      setError('Untracked lookup failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLiUntrackedLoading(false);
    }
  };

  const fetchLiHistory = async (ticker) => {
    const t = (ticker || liHistoryTicker).trim().toUpperCase();
    if (!t) return;
    setLiHistoryTicker(t);
    setLiHistoryLoading(true);
    setLiBreakdownLoading(true);
    try {
      const [histRes, breakRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/swee/linkedin-insights/${t}/history`, { headers: getHeaders() }),
        axios.get(`${BACKEND_URL}/api/swee/linkedin-insights/${t}/breakdown`, { headers: getHeaders() }),
      ]);
      setLiHistory(histRes.data);
      setLiBreakdown(breakRes.data);
    } catch (err) {
      setError('Failed to load history: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLiHistoryLoading(false);
      setLiBreakdownLoading(false);
    }
  };

  const handleLiMonthlyRefresh = async () => {
    setLiRefreshRunning(true);
    setLiRefreshResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/linkedin-insights/monthly-refresh`, {}, { headers: getHeaders() });
      setLiRefreshResult(data);
      setSuccessMsg(`Monthly refresh complete: ${data.refreshed} companies refreshed, $${data.total_cost?.toFixed(2) || '0.00'} total cost`);
      fetchLiStats();
    } catch (err) {
      setError('Monthly refresh failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLiRefreshRunning(false);
    }
  };

  const fetchCompanies = async (search = '') => {
    setCompaniesLoading(true);
    try {
      const params = search ? { search } : {};
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/companies`, { headers: getHeaders(), params });
      setCompanies(data.companies || []);
    } catch (err) {
      setError('Failed to load companies: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchTrajectory = async (ticker) => {
    setSelectedTicker(ticker);
    setTrajectoryLoading(true);
    setActiveTab('trajectory');
    setSearchParams({ tab: 'trajectory', ticker });
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/company/${ticker}`, { headers: getHeaders() });
      setTrajectoryData(data);
      // Also load financial data for this company
      fetchFinancials(ticker);
    } catch (err) {
      setError('Failed to load trajectory: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTrajectoryLoading(false);
    }
  };

  const fetchTranscript = async (ticker, fiscalPeriod) => {
    const key = `${ticker}-${fiscalPeriod}`;
    if (expandedTranscript === key && transcriptData) {
      // Toggle collapse
      setExpandedTranscript(null);
      return;
    }
    setExpandedTranscript(key);
    setTranscriptLoading(true);
    setTranscriptSearch('');
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/transcript/${ticker}/${fiscalPeriod}`, { headers: getHeaders() });
      setTranscriptData(data);
    } catch (err) {
      setError('Failed to load transcript: ' + (err.response?.data?.detail || err.message));
      setExpandedTranscript(null);
    } finally {
      setTranscriptLoading(false);
    }
  };

  // Fetch stored financial data for the current company
  const fetchFinancials = async (ticker) => {
    if (!ticker) return;
    setFinancialLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/financials/${ticker}`, { headers: getHeaders() });
      setFinancialData(data);
    } catch (err) {
      console.error('Failed to load financials:', err);
      setFinancialData({ ticker, data: [], total: 0, has_data: false });
    } finally {
      setFinancialLoading(false);
    }
  };

  // Pull fresh financial data from SEC EDGAR
  const pullEdgarFinancials = async (ticker) => {
    if (!ticker) return;
    setFinancialFetching(true);
    try {
      await axios.post(`${BACKEND_URL}/api/swee/financials/fetch/${ticker}`, null, { headers: getHeaders() });
      setSuccessMsg(`Financial data fetched from SEC EDGAR for ${ticker}`);
      // Refresh the stored data
      await fetchFinancials(ticker);
    } catch (err) {
      setError('Failed to fetch from EDGAR: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFinancialFetching(false);
    }
  };

  const fetchHeatmap = async () => {
    setHeatmapLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/heatmap`, { headers: getHeaders() });
      // Transform flat rows into matrix format: [{ticker, company_name, scores: {anchor: score}}, ...]
      const byTicker = {};
      (data.data || []).forEach(row => {
        if (!byTicker[row.ticker]) {
          byTicker[row.ticker] = { ticker: row.ticker, company_name: row.company_name, scores: {} };
        }
        byTicker[row.ticker].scores[row.anchor] = row.avg_score;
      });
      setHeatmapData({
        matrix: Object.values(byTicker),
        anchors: Object.keys(ANCHOR_COLORS),
        available_periods: data.available_periods || [],
      });
    } catch (err) {
      setError('Failed to load heatmap: ' + (err.response?.data?.detail || err.message));
    } finally {
      setHeatmapLoading(false);
    }
  };

  const runSearch = async () => {
    setSearchLoading(true);
    try {
      const body = {};
      if (searchKeywords.trim()) body.keywords = searchKeywords.trim();
      if (searchSemantic.trim()) body.semantic_query = searchSemantic.trim();
      if (searchTickers.trim()) body.tickers = searchTickers.split(',').map(t => t.trim()).filter(Boolean);
      if (searchAnchors.length) body.anchors = searchAnchors;
      if (searchMinScore) body.min_score = parseInt(searchMinScore);
      if (searchVerdicts.length) body.verdicts = searchVerdicts;
      body.limit = 50;
      body.offset = 0;

      const { data } = await axios.post(`${BACKEND_URL}/api/swee/search`, body, { headers: getHeaders() });
      setSearchResults(data);
    } catch (err) {
      setError('Search failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSearchLoading(false);
    }
  };

  const startBackfill = async () => {
    const tickers = backfillTickers.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
    if (!tickers.length) { setError('Enter at least one ticker'); return; }

    setBackfillRunning(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/backfill/start`, {
        tickers,
        start_year: backfillStartYear,
        start_quarter: backfillStartQ,
        end_year: backfillEndYear,
        end_quarter: backfillEndQ,
      }, { headers: getHeaders() });
      const ingested = data.total_ingested || 0;
      const skipped = data.total_skipped || 0;
      if (data.pipeline_status === 'started_in_background') {
        setSuccessMsg(`Ingested ${ingested} transcript(s) (${skipped} skipped). Pipeline processing started in background — status will update automatically.`);
        startStatusPolling();
      } else {
        setSuccessMsg(`Ingested ${ingested} transcript(s), ${skipped} skipped. No new transcripts to process.`);
      }
      fetchBackfillStatus();
    } catch (err) {
      setError('Backfill failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBackfillRunning(false);
    }
  };

  const fetchBackfillStatus = async () => {
    setBackfillStatusLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/backfill/status`, { headers: getHeaders() });
      setBackfillStatus(data);
      return data;
    } catch (err) {
      console.error('Failed to load backfill status:', err);
      return null;
    } finally {
      setBackfillStatusLoading(false);
    }
  };

  // Auto-poll backfill status every 5s while pipeline is processing in background
  const startStatusPolling = () => {
    // Clear any existing polling
    if (pipelinePollingRef.current) clearInterval(pipelinePollingRef.current);

    setPipelineRunning(true);
    let unchangedCount = 0;
    let lastScored = -1;

    pipelinePollingRef.current = setInterval(async () => {
      const data = await fetchBackfillStatus();
      if (!data) return;

      const scored = data.chunks?.scored || 0;
      const total = data.chunks?.total || 0;

      // Detect completion: all chunks scored, or no change for 3 consecutive polls
      if (scored === total && total > 0) {
        clearInterval(pipelinePollingRef.current);
        pipelinePollingRef.current = null;
        setPipelineRunning(false);
        setSuccessMsg(`Pipeline complete: ${total} chunks scored.`);
      } else if (scored === lastScored) {
        unchangedCount++;
        if (unchangedCount >= 6) {
          // No progress for 30s — likely done or stalled
          clearInterval(pipelinePollingRef.current);
          pipelinePollingRef.current = null;
          setPipelineRunning(false);
        }
      } else {
        unchangedCount = 0;
      }
      lastScored = scored;
    }, 5000);
  };

  const runPipeline = async () => {
    setPipelineRunning(true);
    setPipelineResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/pipeline/run`, {}, { headers: getHeaders() });
      setPipelineResult(data);
      if (data.status === 'started_in_background') {
        setSuccessMsg(data.message || 'Pipeline processing started in background.');
        startStatusPolling();
      } else if (data.status === 'nothing_to_process') {
        setSuccessMsg(data.message || 'Nothing to process.');
      } else {
        setSuccessMsg(`Pipeline complete: ${data.total_chunked || 0} chunked, ${data.total_embedded || 0} embedded, ${data.total_scored || 0} scored`);
      }
      fetchBackfillStatus();
    } catch (err) {
      setError('Pipeline run failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPipelineRunning(false);
    }
  };

  // Populate & background backfill functions
  const populateSp500 = async () => {
    setPopulatingSp500(true);
    setPopulateResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/companies/populate-sp500`, {}, { headers: getHeaders() });
      setPopulateResult({ type: 'sp500', ...data });
      setSuccessMsg(`S&P 500: ${data.added} added, ${data.updated} updated (${data.total_sp500} total)`);
      fetchCompanies();
    } catch (err) {
      setError('Populate S&P 500 failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPopulatingSp500(false);
    }
  };

  const populateAlumniEmployers = async () => {
    setPopulatingAlumni(true);
    setPopulateResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/companies/populate-alumni-employers`, {}, { headers: getHeaders() });
      setPopulateResult({ type: 'alumni', ...data });
      setSuccessMsg(`Alumni Employers: ${data.added} added, ${data.updated} updated (${data.total_watchlist} watchlist, ${data.alumni_employer_matches} matches found)`);
      fetchCompanies();
    } catch (err) {
      setError('Populate Alumni Employers failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPopulatingAlumni(false);
    }
  };

  const startBackgroundBackfill = async () => {
    setBgBackfillStarting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/backfill/start-background`, {
        target: bgBackfillTarget,
        start_year: bgBackfillStartYear,
        start_quarter: bgBackfillStartQ,
        end_year: bgBackfillEndYear,
        end_quarter: bgBackfillEndQ,
      }, { headers: getHeaders() });
      setSuccessMsg(`Background backfill started: ${data.companies_queued} companies queued (Job ID: ${data.job_id.slice(0, 8)}...)`);
      fetchBackfillJobs();
    } catch (err) {
      setError('Background backfill failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBgBackfillStarting(false);
    }
  };

  const fetchBackfillJobs = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/backfill/jobs`, { headers: getHeaders() });
      setBgBackfillJobs(data.jobs || []);
    } catch (err) {
      console.error('Failed to load backfill jobs:', err);
    }
  };

  // Poll for background backfill progress
  useEffect(() => {
    const hasRunningJob = bgBackfillJobs.some(j => j.status === 'running' || j.status === 'queued');
    if (!hasRunningJob || activeTab !== 'pipeline') return;

    const interval = setInterval(fetchBackfillJobs, 10000);
    return () => clearInterval(interval);
  }, [bgBackfillJobs, activeTab]);

  // Cleanup pipeline status polling on unmount
  useEffect(() => {
    return () => {
      if (pipelinePollingRef.current) clearInterval(pipelinePollingRef.current);
    };
  }, []);

  const initializeCentroids = async () => {
    setCentroidInitRunning(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/centroids/initialize`, {}, { headers: getHeaders() });
      setSuccessMsg(`Centroids initialized: ${data.stored} anchors stored`);
      fetchCentroids();
    } catch (err) {
      setError('Centroid initialization failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCentroidInitRunning(false);
    }
  };

  const fetchCentroids = async () => {
    setCentroidsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/centroids`, { headers: getHeaders() });
      setCentroids(data.centroids || []);
    } catch (err) {
      console.error('Failed to load centroids:', err);
    } finally {
      setCentroidsLoading(false);
    }
  };

  // WI Centroid functions
  const initializeWiCentroids = async () => {
    setWiCentroidInitRunning(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/wi-centroids/initialize`, {}, { headers: getHeaders() });
      setSuccessMsg(`WI centroids initialized: ${data.total_centroids} categories stored`);
      fetchWiCentroids();
    } catch (err) {
      setError('WI centroid initialization failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setWiCentroidInitRunning(false);
    }
  };

  const fetchWiCentroids = async () => {
    setWiCentroidsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/wi-centroids`, { headers: getHeaders() });
      setWiCentroids(data.centroids || []);
    } catch (err) {
      console.error('Failed to load WI centroids:', err);
    } finally {
      setWiCentroidsLoading(false);
    }
  };

  // ── External Signals fetch functions ────────────────────

  const fetchSignalsSummary = async () => {
    setSignalsSummaryLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/swee/signals/summary`, { headers: getHeaders() });
      setSignalsSummary(res.data);
    } catch (e) {
      console.error('Failed to fetch signals summary:', e);
    } finally {
      setSignalsSummaryLoading(false);
    }
  };

  const fetchSignalsList = async () => {
    setSignalsLoading(true);
    try {
      const params = { days_back: signalsDaysBack, limit: 200 };
      if (signalsSourceFilter) params.source_type = signalsSourceFilter;
      if (signalsTickerFilter) params.ticker = signalsTickerFilter.toUpperCase();
      if (signalsCategoryFilter) params.signal_category = signalsCategoryFilter;
      if (signalsRelevanceFilter) params.relevance = signalsRelevanceFilter;
      if (signalsDirectionFilter) params.direction = signalsDirectionFilter;
      const res = await axios.get(`${BACKEND_URL}/api/swee/signals`, { headers: getHeaders(), params });
      setSignalsList(res.data || []);
    } catch (e) {
      console.error('Failed to fetch signals:', e);
    } finally {
      setSignalsLoading(false);
    }
  };

  const fetchUnresolved = async () => {
    setUnresolvedLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/swee/signals/unresolved`, { headers: getHeaders() });
      setUnresolvedList(res.data || []);
    } catch (e) {
      console.error('Failed to fetch unresolved:', e);
    } finally {
      setUnresolvedLoading(false);
    }
  };

  const handleResolveUnresolved = async (unresolvedId) => {
    const ticker = (resolveTickerInputs[unresolvedId] || '').trim();
    if (!ticker) return;
    setResolvingId(unresolvedId);
    try {
      await axios.post(`${BACKEND_URL}/api/swee/signals/unresolved/${unresolvedId}/resolve`,
        { ticker },
        { headers: getHeaders() }
      );
      setResolveTickerInputs(prev => { const next = { ...prev }; delete next[unresolvedId]; return next; });
      setResolvingId(null);
      fetchUnresolved();
      setSuccessMsg('Company resolved and alias created');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to resolve');
      setResolvingId(null);
    }
  };

  const handleManualScan = async (source = 'all') => {
    setScanRunning(true);
    setScanResults(null);
    setScanProgress(null);

    const needsProgressPolling = ['all', 'sec_8k', 'fmp_news'].includes(source);
    const isBackgroundScan = ['fmp_news'].includes(source);

    // Start polling progress
    if (needsProgressPolling) {
      scanProgressRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/swee/signals/scan-progress`, { headers: getHeaders() });
          setScanProgress(res.data);
          // For background scans, stop polling when backend reports done
          if (isBackgroundScan && res.data && !res.data.running && res.data.phase === 'done') {
            setScanRunning(false);
            setScanProgress(null);
            fetchSignalsSummary();
            fetchSignalsList();
            fetchUnresolved();
            if (scanProgressRef.current) {
              clearInterval(scanProgressRef.current);
              scanProgressRef.current = null;
            }
          }
        } catch (_) {}
      }, 1500);
    }

    try {
      const res = await axios.post(`${BACKEND_URL}/api/swee/signals/scan`,
        { source, days_back: 7, sec_scope: secScope },
        { headers: getHeaders() }
      );
      setScanResults(res.data);
      // For background scans, don't stop polling yet — let the interval handle it
      if (!isBackgroundScan) {
        fetchSignalsSummary();
        fetchSignalsList();
        fetchUnresolved();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Scan failed');
    } finally {
      if (!isBackgroundScan) {
        setScanRunning(false);
        setScanProgress(null);
        if (scanProgressRef.current) {
          clearInterval(scanProgressRef.current);
          scanProgressRef.current = null;
        }
      }
    }
  };

  // ── Layoffs.fyi CSV Upload ──────────────────────────────

  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    setCsvResults(null);

    // Preview: count rows
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      setCsvPreviewCount(Math.max(0, lines.length - 1)); // minus header
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setCsvUploading(true);
    setCsvResults(null);

    try {
      const text = await csvFile.text();
      const lines = text.split('\n');
      if (lines.length < 2) {
        setCsvResults({ status: 'error', message: 'CSV file is empty or has no data rows' });
        return;
      }

      // Parse CSV header
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));

      // Parse rows into objects
      const records = [];
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Handle quoted CSV fields (commas inside quotes)
        const values = [];
        let current = '';
        let inQuotes = false;
        for (let c = 0; c < line.length; c++) {
          if (line[c] === '"') {
            inQuotes = !inQuotes;
          } else if (line[c] === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += line[c];
          }
        }
        values.push(current.trim());

        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });
        records.push(row);
      }

      if (records.length === 0) {
        setCsvResults({ status: 'error', message: 'No valid data rows found' });
        return;
      }

      // Send to backend in batches of 500 to avoid timeout
      const batchSize = 500;
      let totalMatched = 0, totalUnresolved = 0, totalDuplicates = 0, totalErrors = 0, totalSkippedIntl = 0;
      const allUnresolvedCompanies = [];

      for (let start = 0; start < records.length; start += batchSize) {
        const batch = records.slice(start, start + batchSize);
        const res = await axios.post(
          `${BACKEND_URL}/api/swee/layoffs-fyi/manual-ingest`,
          { records: batch },
          { headers: getHeaders(), timeout: 120000 }
        );
        const d = res.data;
        totalMatched += d.matched || 0;
        totalUnresolved += d.unresolved || 0;
        totalDuplicates += d.duplicates || 0;
        totalErrors += d.errors || 0;
        totalSkippedIntl += d.skipped_international || 0;
        if (d.unresolved_companies) allUnresolvedCompanies.push(...d.unresolved_companies);
      }

      setCsvResults({
        status: 'completed',
        total: records.length,
        matched: totalMatched,
        unresolved: totalUnresolved,
        duplicates: totalDuplicates,
        errors: totalErrors,
        skipped_international: totalSkippedIntl,
        unresolved_companies: [...new Set(allUnresolvedCompanies)],
      });

      // Refresh signals data
      fetchSignalsSummary();
      fetchSignalsList();
      fetchUnresolved();

    } catch (e) {
      setCsvResults({ status: 'error', message: e.response?.data?.detail || e.message || 'Upload failed' });
    } finally {
      setCsvUploading(false);
    }
  };

  // ── Hiring Tracker fetch functions ──────────────────────

  const fetchTrackerCompanies = async () => {
    setTrackerLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/swee/tracker/companies`, { headers: getHeaders() });
      setTrackerCompanies(res.data.companies || []);
      setTrackerCredits(res.data.credit_budget || null);
    } catch (err) {
      console.error('Error fetching tracker companies:', err);
    } finally {
      setTrackerLoading(false);
    }
  };

  const handleTrackerToggle = async (ticker, currentDomain) => {
    try {
      const body = currentDomain ? { company_domain: currentDomain } : {};
      await axios.post(`${BACKEND_URL}/api/swee/tracker/toggle/${ticker}`, body, { headers: getHeaders() });
      fetchCompanies();
      fetchTrackerCompanies();
      setSuccessMsg(`TheirStack tracking toggled for ${ticker}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to toggle tracking');
    }
  };

  const handleTrackerSnapshot = async (ticker) => {
    setTrackerSnapshotLoading(ticker);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/swee/tracker/snapshot/${ticker}`, {}, { headers: getHeaders() });
      setSuccessMsg(`Snapshot collected for ${ticker}: ${res.data.num_jobs || 0} open jobs, ${res.data.credits_used} credits used`);
      fetchTrackerCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to collect snapshot for ${ticker}`);
    } finally {
      setTrackerSnapshotLoading(null);
    }
  };

  const handleTrackerViewDetail = async (ticker) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/swee/tracker/snapshots/${ticker}?limit=12`, { headers: getHeaders() });
      setTrackerDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load snapshot history');
    }
  };

  const handleSetDomain = async (ticker, domain) => {
    try {
      await axios.put(`${BACKEND_URL}/api/swee/tracker/domain/${ticker}`, { company_domain: domain }, { headers: getHeaders() });
      setTrackerDomainEditing(null);
      setSuccessMsg(`Domain set for ${ticker}: ${domain}`);
      fetchCompanies();
      fetchTrackerCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set domain');
    }
  };

  const handleCollectAll = async () => {
    setTrackerSnapshotLoading('ALL');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/swee/tracker/collect-all`, {}, { headers: getHeaders() });
      setSuccessMsg(`Collected ${res.data.collected}/${res.data.total} snapshots (${res.data.credits_used} credits used)`);
      if (res.data.errors?.length) {
        setError(`${res.data.errors.length} errors: ${res.data.errors.map(e => e.ticker).join(', ')}`);
      }
      fetchTrackerCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to collect snapshots');
    } finally {
      setTrackerSnapshotLoading(null);
    }
  };

  // ── Backfill functions ──────────────────────────────────

  const handleBackfillCompany = async (ticker) => {
    setBackfillLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/swee/tracker/backfill/${ticker}`,
        { months_back: backfillMonths },
        { headers: getHeaders() }
      );
      const data = res.data;
      const collected = (data.snapshots || []).filter(s => s.status === 'collected').length;
      const skipped = (data.snapshots || []).filter(s => s.status === 'already_exists').length;
      setSuccessMsg(`Backfill ${ticker}: ${collected} months collected, ${skipped} skipped (already existed), ${data.total_credits_used} credits used`);
      if (data.errors?.length) {
        setError(`${data.errors.length} error(s): ${data.errors.map(e => e.month + ': ' + e.error).join('; ')}`);
      }
      setBackfillResults(data);
      fetchTrackerCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to backfill ${ticker}`);
    } finally {
      setBackfillLoading(false);
      setBackfillTarget(null);
    }
  };

  const handleBackfillAll = async () => {
    setBackfillLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/swee/tracker/backfill-all`,
        { months_back: backfillMonths },
        { headers: getHeaders() }
      );
      const data = res.data;
      setSuccessMsg(`Backfill All: ${data.completed}/${data.total_companies} companies, ${data.total_credits_used} credits used`);
      if (data.errors?.length) {
        setError(`${data.errors.length} error(s): ${data.errors.map(e => (e.ticker || e.month) + ': ' + e.error).join('; ')}`);
      }
      setBackfillResults(data);
      fetchTrackerCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to backfill all companies');
    } finally {
      setBackfillLoading(false);
      setBackfillTarget(null);
    }
  };

  // WI fetch functions
  const fetchWISummary = async () => {
    setWiSummaryLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/wi/summary`, { headers: getHeaders() });
      setWiSummary(data);
    } catch (err) {
      console.error('Failed to load WI summary:', err);
    } finally {
      setWiSummaryLoading(false);
    }
  };

  const fetchWIChunks = async (page = 1) => {
    setWiChunksLoading(true);
    try {
      const params = { page, per_page: 25 };
      if (wiFilterCategory) params.primary_category = wiFilterCategory;
      if (wiFilterSignificance) params.operational_significance = wiFilterSignificance;
      if (wiFilterDirection) params.signal_direction = wiFilterDirection;
      if (wiFilterTicker) params.ticker = wiFilterTicker.toUpperCase();
      if (wiFilterMinScore) params.min_score = wiFilterMinScore;
      if (wiFilterTemporal) params.temporal_stance = wiFilterTemporal;
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/wi/chunks`, { headers: getHeaders(), params });
      setWiChunks(data);
      setWiPage(page);
    } catch (err) {
      console.error('Failed to load WI chunks:', err);
    } finally {
      setWiChunksLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/subscribers`, { headers: getHeaders() });
      setSubscribers(data.subscribers || []);
    } catch (err) {
      console.error('Failed to load subscribers:', err);
    } finally {
      setSubscribersLoading(false);
    }
  };

  const saveSubscription = async () => {
    if (!subForm.label?.trim()) {
      setError('Please enter a label for this alert (e.g. ticker or description)');
      return;
    }
    setSubSaving(true);
    try {
      const body = {
        ...subForm,
        watch_tickers: subForm.watch_tickers ? subForm.watch_tickers.split(',').map(t => t.trim().toUpperCase()).filter(Boolean) : null,
        watch_sectors: subForm.watch_sectors ? subForm.watch_sectors.split(',').map(s => s.trim()).filter(Boolean) : null,
      };
      if (editingSubId) {
        await axios.put(`${BACKEND_URL}/api/swee/subscribers/${editingSubId}`, body, { headers: getHeaders() });
        setSuccessMsg('Alert updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/swee/subscribers`, body, { headers: getHeaders() });
        setSuccessMsg('Alert created');
      }
      setShowSubForm(false);
      setEditingSubId(null);
      setSubForm(defaultSubForm);
      fetchSubscribers();
    } catch (err) {
      setError('Failed to save alert: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubSaving(false);
    }
  };

  const editSubscription = (sub) => {
    setSubForm({
      label: sub.label || 'Default',
      watch_all_sp500: sub.watch_all_sp500 || false,
      watch_tickers: Array.isArray(sub.watch_tickers) ? sub.watch_tickers.join(', ') : (sub.watch_tickers || ''),
      watch_sectors: Array.isArray(sub.watch_sectors) ? sub.watch_sectors.join(', ') : (sub.watch_sectors || ''),
      watch_alumni_companies: sub.watch_alumni_companies || false,
      min_alert_tier: sub.min_alert_tier || 'STANDARD',
      delivery_email: sub.delivery_email ?? true,
      delivery_sms: sub.delivery_sms ?? false,
      digest_frequency: sub.digest_frequency || 'daily',
    });
    setEditingSubId(sub.id);
    setShowSubForm(true);
  };

  const deleteSubscription = async (subId) => {
    if (!window.confirm('Delete this alert subscription?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/swee/subscribers/${subId}`, { headers: getHeaders() });
      setSuccessMsg('Alert deleted');
      fetchSubscribers();
    } catch (err) {
      setError('Failed to delete alert: ' + (err.response?.data?.detail || err.message));
    }
  };

  const fetchRecentAlerts = async () => {
    setAlertsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/alerts/recent`, { headers: getHeaders() });
      setRecentAlerts(data.alerts || []);
    } catch (err) {
      console.error('Failed to load recent alerts:', err);
    } finally {
      setAlertsLoading(false);
    }
  };

  // =============================================
  // Reports Tab: Fetch Functions (Phase 4c)
  // =============================================

  const fetchReportStats = async () => {
    setReportStatsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/events/stats`, { headers: getHeaders() });
      setReportStats(data);
    } catch (err) {
      console.error('Failed to load report stats:', err);
    } finally {
      setReportStatsLoading(false);
    }
  };

  const fetchReportsList = async () => {
    setReportsLoading(true);
    try {
      const params = { limit: 50 };
      if (reportsTickerFilter) params.ticker = reportsTickerFilter;
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/reports`, { headers: getHeaders(), params });
      setReportsList(data.reports || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setReportsLoading(false);
    }
  };

  const fetchRecentEventsList = async () => {
    setRecentEventsLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/events`, { headers: getHeaders(), params: { limit: 50 } });
      setRecentEvents(data.events || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setRecentEventsLoading(false);
    }
  };

  const handleProcessEvents = async () => {
    setProcessingEvents(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/events/process`, {}, { headers: getHeaders() });
      setSuccessMsg(`Event Processor: ${data.reports_generated || 0} report(s) generated from ${data.processed || 0} event(s)${data.reports_failed ? ` (${data.reports_failed} failed)` : ''}`);
      fetchReportStats();
      fetchReportsList();
      fetchRecentEventsList();
    } catch (err) {
      setError('Failed to process events: ' + (err.response?.data?.detail || err.message));
    } finally {
      setProcessingEvents(false);
    }
  };

  const handleEnrichCompany = async () => {
    if (!enrichTicker.trim()) return;
    setEnriching(true);
    setEnrichResult(null);
    setError(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/enrich-company`, {
        ticker: enrichTicker.trim().toUpperCase(),
        sources: ['all'],
      }, { headers: getHeaders() });
      setEnrichResult(data);
      setSuccessMsg(`Enriched ${data.ticker}: ${(data.enriched || []).length} source(s) updated`);
    } catch (err) {
      setError('Enrich failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setEnriching(false);
    }
  };

  const handleGenerateReportOnDemand = async () => {
    if (!enrichTicker.trim()) return;
    setGeneratingReport(true);
    setError(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/reports/generate-on-demand`, {
        ticker: enrichTicker.trim().toUpperCase(),
      }, { headers: getHeaders() });
      setSuccessMsg(`Report generated for ${data.report?.ticker || enrichTicker.toUpperCase()}`);
      fetchReportsList();
    } catch (err) {
      setError('Report generation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingReport(false);
    }
  };

  // =============================================
  // Calendar: Helpers & API Functions
  // =============================================

  const getCalendarDateRange = (rangeType) => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ...
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sun or Sat

    const formatISO = (d) => d.toISOString().split('T')[0];

    if (rangeType === 'last_week') {
      if (isWeekend) {
        // On weekends, "Last Week" = the Mon-Fri that just ended
        const daysSinceFri = dayOfWeek === 0 ? 2 : 1; // Sun→-2 to Fri, Sat→-1 to Fri
        const friday = new Date(today);
        friday.setDate(today.getDate() - daysSinceFri);
        const monday = new Date(friday);
        monday.setDate(friday.getDate() - 4);
        return { start: formatISO(monday), end: formatISO(friday) };
      } else {
        // On weekdays, "Last Week" = previous Mon-Fri
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek - 1) - 7);
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);
        return { start: formatISO(monday), end: formatISO(friday) };
      }
    }
    if (rangeType === 'this_week') {
      const monday = new Date(today);
      if (isWeekend) {
        // On weekends, "This Week" = upcoming Mon-Fri
        const daysUntilMon = dayOfWeek === 0 ? 1 : 2; // Sun→+1, Sat→+2
        monday.setDate(today.getDate() + daysUntilMon);
      } else {
        monday.setDate(today.getDate() - (dayOfWeek - 1));
      }
      const friday = new Date(monday);
      friday.setDate(monday.getDate() + 4);
      return { start: formatISO(monday), end: formatISO(friday) };
    }
    if (rangeType === 'next_week') {
      const nextMonday = new Date(today);
      if (isWeekend) {
        // On weekends, "Next Week" = the week AFTER the upcoming one
        const daysUntilMon = dayOfWeek === 0 ? 1 : 2;
        nextMonday.setDate(today.getDate() + daysUntilMon + 7);
      } else {
        nextMonday.setDate(today.getDate() + (8 - dayOfWeek));
      }
      const nextFriday = new Date(nextMonday);
      nextFriday.setDate(nextMonday.getDate() + 4);
      return { start: formatISO(nextMonday), end: formatISO(nextFriday) };
    }
    if (rangeType === 'next_30_days') {
      const end = new Date(today);
      end.setDate(today.getDate() + 29);
      return { start: formatISO(today), end: formatISO(end) };
    }
    // custom
    return { start: calendarStartDate, end: calendarEndDate };
  };

  const fetchCalendar = async (range) => {
    const rangeType = range || calendarRange;
    const { start, end } = getCalendarDateRange(rangeType);
    if (!start || !end) return;

    setCalendarLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/calendar`, {
        params: { start_date: start, end_date: end },
        headers: getHeaders(),
      });
      setCalendarData(data);
    } catch (err) {
      setError('Failed to load calendar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCalendarLoading(false);
    }
  };

  const addToWatchlist = async (ticker, companyName, exchange) => {
    setAddingToWatchlist(ticker);
    try {
      await axios.post(`${BACKEND_URL}/api/swee/companies/add-watchlist`,
        { ticker, company_name: companyName, exchange },
        { headers: getHeaders() }
      );
      setSuccessMsg(`${ticker} added to watchlist`);
      // Re-fetch calendar to update badges
      fetchCalendar();
    } catch (err) {
      setError('Failed to add to watchlist: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAddingToWatchlist(null);
    }
  };

  const backfillFromCalendar = async (ticker, year, quarter) => {
    setBackfillingFromCal(ticker);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/swee/calendar/backfill-from-calendar`,
        { ticker, year, quarter },
        { headers: getHeaders() }
      );
      if (data.status === 'ingested') {
        setSuccessMsg(data.message);
      } else if (data.status === 'skipped') {
        setSuccessMsg(data.message);
      } else {
        setError(data.message);
      }
      // Re-fetch calendar to update has_transcript status
      fetchCalendar();
    } catch (err) {
      setError('Failed to backfill: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBackfillingFromCal(null);
    }
  };

  const downloadSlides = async (ticker, year, quarter) => {
    const key = `${ticker}-${year}-Q${quarter}`;
    setDownloadingSlides(key);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/slides/${ticker}/${year}/${quarter}`, {
        headers: getHeaders(),
      });
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError(`No slide deck available for ${ticker} Q${quarter} ${year}`);
      } else if (status === 403) {
        setError('API plan does not include slide decks');
      } else {
        setError('Failed to download slides: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setDownloadingSlides(null);
    }
  };

  const fetchAudio = async (ticker, year, quarter) => {
    const key = `${ticker}-${year}-Q${quarter}`;
    // Toggle off if already playing this transcript's audio
    if (audioKey === key) {
      setAudioUrl(null);
      setAudioKey(null);
      return;
    }
    setAudioLoading(key);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/swee/audio/${ticker}/${year}/${quarter}`, {
        headers: getHeaders(),
      });
      if (data.url) {
        setAudioUrl(data.url);
        setAudioKey(key);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError(`No audio available for ${ticker} Q${quarter} ${year}`);
      } else if (status === 403) {
        setError('API plan does not include audio files');
      } else {
        setError('Failed to load audio: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setAudioLoading(null);
    }
  };

  const changeCalendarRange = (newRange) => {
    setCalendarRange(newRange);
    fetchCalendar(newRange);
  };

  // =============================================
  // Render: Calendar Tab
  // =============================================

  const renderCalendar = () => {
    const summary = calendarData?.summary || {};
    const eventsByDate = calendarData?.events_by_date || {};

    // Client-side filtering
    const filteredEventsByDate = {};
    let filteredTotal = 0;
    for (const [dateStr, events] of Object.entries(eventsByDate)) {
      const filtered = events.filter(evt => {
        // Filter by tracked status
        if (calendarFilter === 'tracked' && !evt.is_tracked) return false;
        if (calendarFilter === 'untracked' && evt.is_tracked) return false;
        if (calendarFilter === 'transcript_ready' && !evt.transcript_ready) return false;
        // Search by ticker or company name
        if (calendarSearch) {
          const q = calendarSearch.toLowerCase();
          if (!evt.symbol?.toLowerCase().includes(q) && !evt.company_name?.toLowerCase().includes(q)) return false;
        }
        return true;
      });
      if (filtered.length > 0) {
        filteredEventsByDate[dateStr] = filtered;
        filteredTotal += filtered.length;
      }
    }

    const formatDate = (dateStr) => {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (isoStr) => {
      if (!isoStr) return '';
      try {
        const d = new Date(isoStr);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' });
      } catch { return ''; }
    };

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        {calendarData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Events" value={summary.total_events || 0} color="emerald" />
            <StatCard label="Tracked" value={summary.tracked_companies || 0} color="blue" />
            <StatCard label="Transcripts Ready" value={summary.transcripts_available || 0} color="purple" />
            <StatCard label="Untracked" value={summary.untracked_companies || 0} sub="Potential additions" color="cyan" />
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Date Range Buttons */}
            <div className="flex rounded-lg border overflow-hidden">
              {[
                { key: 'last_week', label: 'Last Week' },
                { key: 'this_week', label: 'This Week' },
                { key: 'next_week', label: 'Next Week' },
                { key: 'next_30_days', label: 'Next 30 Days' },
                { key: 'custom', label: 'Custom' },
              ].map(opt => (
                <button key={opt.key} onClick={() => changeCalendarRange(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium border-r last:border-r-0 transition-colors ${
                    calendarRange === opt.key
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Custom date inputs */}
            {calendarRange === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={calendarStartDate}
                  onChange={(e) => setCalendarStartDate(e.target.value)}
                  className="px-2 py-1.5 border rounded text-xs" />
                <span className="text-xs text-gray-400">to</span>
                <input type="date" value={calendarEndDate}
                  onChange={(e) => setCalendarEndDate(e.target.value)}
                  className="px-2 py-1.5 border rounded text-xs" />
                <button onClick={() => fetchCalendar('custom')}
                  disabled={!calendarStartDate || !calendarEndDate}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 disabled:opacity-50">
                  Go
                </button>
              </div>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Filter */}
            <select value={calendarFilter} onChange={(e) => setCalendarFilter(e.target.value)}
              className="px-2 py-1.5 border rounded text-xs">
              <option value="all">All Companies</option>
              <option value="tracked">Tracked Only</option>
              <option value="untracked">Untracked Only</option>
              <option value="transcript_ready">Transcript Ready</option>
            </select>

            {/* Search */}
            <input type="text" value={calendarSearch}
              onChange={(e) => setCalendarSearch(e.target.value)}
              placeholder="Search ticker or company..."
              className="px-3 py-1.5 border rounded text-xs w-48" />
          </div>

          {/* Cache info */}
          {calendarData && (
            <div className="mt-2 text-xs text-gray-400">
              {summary.date_range?.start} to {summary.date_range?.end} — {summary.api_calls_made || 0} API calls, {summary.api_calls_cached || 0} cached
              {(calendarSearch || calendarFilter !== 'all') && ` — Showing ${filteredTotal} of ${summary.total_events || 0}`}
            </div>
          )}
        </div>

        {/* Loading */}
        {calendarLoading && <LoadingSpinner text="Loading earnings calendar..." />}

        {/* Empty state */}
        {!calendarLoading && calendarData && Object.keys(filteredEventsByDate).length === 0 && (
          <EmptyState text={calendarSearch || calendarFilter !== 'all'
            ? "No events match your filters."
            : "No earnings events found for this date range."
          } />
        )}

        {/* Events grouped by date */}
        {!calendarLoading && Object.entries(filteredEventsByDate).map(([dateStr, events]) => (
          <div key={dateStr} className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {/* Date header */}
            <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">{formatDate(dateStr)}</h3>
              <span className="text-xs text-gray-500">{events.length} event{events.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Events table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ticker</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Exchange</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quarter</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Transcript</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map((evt, idx) => (
                    <tr key={`${evt.symbol}-${idx}`} className={evt.is_tracked ? 'bg-emerald-50/40' : ''}>
                      <td className="px-3 py-2">
                        <span className="font-mono font-bold text-sm text-emerald-700">{evt.symbol}</span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-800 max-w-[200px] truncate">{evt.company_name}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{evt.exchange}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">Q{evt.quarter} {evt.year}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{formatTime(evt.conference_date)}</td>
                      <td className="px-3 py-2">
                        {evt.transcript_ready
                          ? <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">Ready</span>
                          : <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Pending</span>
                        }
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {evt.is_sp500 && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">S&P 500</span>}
                          {evt.is_watchlist && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Watchlist</span>}
                          {evt.has_transcript && <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Ingested</span>}
                          {evt.sector && <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{evt.sector}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {evt.is_tracked && (
                            <button onClick={() => { setSelectedTicker(evt.symbol); changeTab('trajectory'); }}
                              className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 whitespace-nowrap">
                              Trajectory
                            </button>
                          )}
                          {!evt.is_tracked && (
                            <button onClick={() => addToWatchlist(evt.symbol, evt.company_name, evt.exchange)}
                              disabled={addingToWatchlist === evt.symbol}
                              className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 disabled:opacity-50 whitespace-nowrap">
                              {addingToWatchlist === evt.symbol ? 'Adding...' : '+ Watchlist'}
                            </button>
                          )}
                          {evt.transcript_ready && !evt.has_transcript && (
                            <button onClick={() => backfillFromCalendar(evt.symbol, evt.year, evt.quarter)}
                              disabled={backfillingFromCal === evt.symbol}
                              className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 whitespace-nowrap">
                              {backfillingFromCal === evt.symbol ? 'Backfilling...' : 'Backfill'}
                            </button>
                          )}
                          {evt.transcript_ready && (
                            <button onClick={() => downloadSlides(evt.symbol, evt.year, evt.quarter)}
                              disabled={downloadingSlides === `${evt.symbol}-${evt.year}-Q${evt.quarter}`}
                              className="text-xs px-2 py-1 bg-cyan-100 text-cyan-700 rounded hover:bg-cyan-200 disabled:opacity-50 whitespace-nowrap">
                              {downloadingSlides === `${evt.symbol}-${evt.year}-Q${evt.quarter}` ? 'Loading...' : 'Slides'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // =============================================
  // Render: Dashboard Tab
  // =============================================

  const renderDashboard = () => {
    if (dashboardLoading) return <LoadingSpinner text="Loading dashboard..." />;
    if (!dashboardData) return <EmptyState text="No dashboard data yet. Initialize centroids and run a backfill to get started." />;

    const { pipeline, recent_high_priority, score_distribution, verdict_distribution } = dashboardData;

    return (
      <div className="space-y-6">
        {/* Pipeline Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Transcripts" value={pipeline?.transcripts?.total || 0} sub={`${pipeline?.transcripts?.ingested || 0} ingested`} color="emerald" />
          <StatCard label="Chunks" value={pipeline?.chunks?.total || 0} sub={`${pipeline?.chunks?.scored || 0} scored`} color="blue" />
          <StatCard label="Embedded" value={pipeline?.chunks?.embedded || 0} sub={`${pipeline?.chunks?.above_threshold || 0} above threshold`} color="purple" />
          <StatCard label="Failed" value={pipeline?.chunks?.failed || 0} sub="need retry" color="red" />
        </div>

        {/* Recent High-Priority Signals */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent High-Priority Signals (Score 7+)</h3>
          {recent_high_priority && recent_high_priority.length > 0 ? (
            <div className="space-y-3">
              {recent_high_priority.map((signal, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => fetchTrajectory(signal.ticker)}
                      className="font-mono font-bold text-emerald-700 hover:underline"
                    >
                      {signal.ticker}
                    </button>
                    <span className="text-xs text-gray-500">{signal.fiscal_period}</span>
                    <AnchorBadge anchor={signal.primary_anchor} />
                    <VerdictBadge verdict={signal.verdict} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold px-2 py-0.5 rounded ${getScoreColor(signal.specificity_score)}`}>
                      {signal.specificity_score}
                    </span>
                    <span className="text-xs text-gray-400">{signal.signal_direction}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No high-priority signals yet. Run a backfill to populate data.</p>
          )}
        </div>

        {/* Score and Verdict Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Score Distribution</h3>
            {score_distribution && Object.keys(score_distribution).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(score_distribution).sort(([a], [b]) => Number(a) - Number(b)).map(([score, count]) => (
                  <div key={score} className="flex items-center gap-2">
                    <span className="text-xs font-mono w-4 text-right">{score}</span>
                    <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                      <div
                        className={`h-full ${getScoreBadge(Number(score))} rounded`}
                        style={{ width: `${Math.min(100, (count / Math.max(...Object.values(score_distribution))) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-8">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-xs">No scores yet</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Verdict Distribution</h3>
            {verdict_distribution && Object.keys(verdict_distribution).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(verdict_distribution).map(([verdict, count]) => (
                  <div key={verdict} className="flex items-center justify-between">
                    <VerdictBadge verdict={verdict} />
                    <span className="text-sm font-medium text-gray-700">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-xs">No verdicts yet</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // =============================================
  // Render: Companies Tab
  // =============================================

  const renderCompanies = () => {
    return (
      <div className="space-y-4">
        {/* Search bar */}
        <div className="flex gap-2">
          <input
            type="text"
            value={companySearch}
            onChange={(e) => setCompanySearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchCompanies(companySearch)}
            placeholder="Search by ticker or company name..."
            className="flex-1 px-4 py-2 border rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500"
          />
          <button
            onClick={() => fetchCompanies(companySearch)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
          >
            Search
          </button>
          <button
            onClick={() => setShowCompaniesHelp(true)}
            className="px-3 py-2 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 border border-gray-200"
            title="How to use Companies"
          >
            ? Help
          </button>
        </div>

        {companiesLoading ? <LoadingSpinner text="Loading companies..." /> : (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticker</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sector</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Transcripts</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Scored</th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-emerald-700"
                    onClick={() => { setSignalSortDir(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'); setCompanySortDir(null); }}
                  >
                    Signals {signalSortDir === 'asc' ? '▲' : signalSortDir === 'desc' ? '▼' : '⇅'}
                  </th>
                  <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-emerald-700"
                    onClick={() => { setCompanySortDir(prev => prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc'); setSignalSortDir(null); }}
                  >
                    Score {companySortDir === 'desc' ? '▼' : companySortDir === 'asc' ? '▲' : '⇅'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-blue-500 uppercase">TheirStack</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {companies.length > 0 ? [...companies].sort((a, b) => {
                  if (companySortDir) {
                    const aScore = a.latest_score != null ? a.latest_score : -Infinity;
                    const bScore = b.latest_score != null ? b.latest_score : -Infinity;
                    return companySortDir === 'desc' ? bScore - aScore : aScore - bScore;
                  }
                  if (signalSortDir) {
                    const aCount = (a.signals || []).length;
                    const bCount = (b.signals || []).length;
                    return signalSortDir === 'desc' ? bCount - aCount : aCount - bCount;
                  }
                  return 0;
                }).map((c) => (
                  <tr key={c.ticker} className="hover:bg-gray-50 cursor-pointer" onClick={() => fetchTrajectory(c.ticker)}>
                    <td className="px-4 py-3 text-sm font-mono font-bold text-emerald-700">{c.ticker}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{c.company_name}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{c.sector || '—'}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{c.transcript_count}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-700">{c.scored_chunk_count}</td>
                    <td className="px-4 py-3">
                      {(c.signals && c.signals.length > 0) ? (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {c.signals.map((s, i) => <SignalSourceBadge key={i} signal={s} />)}
                        </div>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {c.latest_score != null ? (
                        <span className={`text-sm font-bold px-2 py-0.5 rounded ${getScoreColor(c.latest_score)}`}>
                          {c.latest_score}
                        </span>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {c.is_sp500 && <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">S&P 500</span>}
                        {c.is_watchlist && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Watchlist</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      {c.theirstack_tracked ? (
                        <button
                          onClick={() => handleTrackerToggle(c.ticker, c.company_domain)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium hover:bg-red-100 hover:text-red-700 transition-colors"
                          title="Click to disable TheirStack tracking"
                        >
                          Tracked
                        </button>
                      ) : c.company_domain ? (
                        <button
                          onClick={() => handleTrackerToggle(c.ticker, c.company_domain)}
                          className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-blue-100 hover:text-blue-700"
                          title={`Enable tracking (domain: ${c.company_domain})`}
                        >
                          Enable
                        </button>
                      ) : (
                        <button
                          onClick={() => setTrackerDomainEditing({ ticker: c.ticker, domain: '' })}
                          className="text-xs px-2 py-0.5 text-gray-400 hover:text-blue-600"
                          title="Set company domain first"
                        >
                          Set domain
                        </button>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No companies tracked yet. Add companies via backfill.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Companies Help Modal */}
        {showCompaniesHelp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowCompaniesHelp(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
                <h2 className="text-lg font-bold text-gray-900">Companies &mdash; How It Works</h2>
                <button onClick={() => setShowCompaniesHelp(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
              </div>
              <div className="px-6 py-5 space-y-5 text-sm text-gray-700">

                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Overview</h3>
                  <p>
                    The Companies tab lists all companies in the SWEE system. Each company has transcript data, scored strategy signals,
                    and optionally TheirStack job posting tracking enabled.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Finding Companies</h3>
                  <p className="text-gray-600">
                    Use the search bar to find any company by ticker symbol (e.g., "CVX") or name (e.g., "Chevron").
                    Companies are loaded from the <span className="font-mono text-xs bg-gray-100 px-1 rounded">swee_company_profile</span> table,
                    which is populated during pipeline backfills.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Viewing a Company's Trajectory</h3>
                  <p className="text-gray-600">
                    Click any company row to load its full trajectory on the <strong>Trajectory</strong> tab. This shows scored transcript chunks,
                    anchor distribution, financial charts, and executive roster over time.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Enabling TheirStack Tracking</h3>
                  <p className="text-gray-600 mb-2">
                    The TheirStack column on the right controls job posting tracking. To enable it for a company:
                  </p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-600">
                    <li>If the company has no domain set, click <span className="font-mono text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Set domain</span> and enter the company's website (e.g., "chevron.com").</li>
                    <li>Once a domain is set, click <span className="font-mono text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Enable</span> to activate TheirStack tracking.</li>
                    <li>The button changes to <span className="font-mono text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Tracked</span> when active. Click it again to disable.</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Table Columns</h3>
                  <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">Column</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr><td className="px-3 py-1.5 font-medium">Ticker</td><td className="px-3 py-1.5 text-gray-600">Stock ticker symbol</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">Company</td><td className="px-3 py-1.5 text-gray-600">Full company name</td></tr>
                      <tr><td className="px-3 py-1.5 font-medium">Sector</td><td className="px-3 py-1.5 text-gray-600">GICS sector classification</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">Transcripts</td><td className="px-3 py-1.5 text-gray-600">Number of earnings call transcripts ingested</td></tr>
                      <tr><td className="px-3 py-1.5 font-medium">Scored</td><td className="px-3 py-1.5 text-gray-600">Number of transcript chunks that passed the scoring gate</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">Signals</td><td className="px-3 py-1.5 text-gray-600">Active data sources for this company — SWEE, WI, WARN, SEC 8-K, FMP News/Press/Employees, TheirStack (sortable by count)</td></tr>
                      <tr><td className="px-3 py-1.5 font-medium">Score</td><td className="px-3 py-1.5 text-gray-600">Latest specificity score from Chain-of-Truth analysis (sortable)</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">Tags</td><td className="px-3 py-1.5 text-gray-600">S&amp;P 500 membership and watchlist status</td></tr>
                      <tr><td className="px-3 py-1.5 font-medium">TheirStack</td><td className="px-3 py-1.5 text-gray-600">Job posting tracker status &mdash; Set domain, Enable, or Tracked</td></tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Tips</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Click the <strong>Signals</strong> column to sort by number of active sources, or <strong>Score</strong> to sort by specificity score.</li>
                    <li>Companies are added via pipeline backfills on the Pipeline tab. They cannot be added manually from this tab.</li>
                    <li>After enabling TheirStack, go to the <strong>Hiring Tracker</strong> tab to pull snapshots and view job posting data.</li>
                  </ul>
                </div>

              </div>
              <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-3 rounded-b-xl">
                <button onClick={() => setShowCompaniesHelp(false)} className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800">Got it</button>
              </div>
            </div>
          </div>
        )}

        {/* Domain editing modal */}
        {trackerDomainEditing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-3">Set Company Domain for {trackerDomainEditing.ticker}</h3>
              <p className="text-sm text-gray-500 mb-3">Enter the company's website domain (e.g., "apple.com", "3m.com"). TheirStack uses this to find job postings.</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={trackerDomainEditing.domain}
                  onChange={(e) => setTrackerDomainEditing({ ...trackerDomainEditing, domain: e.target.value })}
                  placeholder="company.com"
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && trackerDomainEditing.domain && handleSetDomain(trackerDomainEditing.ticker, trackerDomainEditing.domain)}
                />
                <button
                  onClick={() => trackerDomainEditing.domain && handleSetDomain(trackerDomainEditing.ticker, trackerDomainEditing.domain)}
                  disabled={!trackerDomainEditing.domain}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  onClick={() => setTrackerDomainEditing(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =============================================
  // Render: Trajectory Tab
  // =============================================

  const renderTrajectory = () => {
    if (!selectedTicker && !trajectoryData) {
      return <EmptyState text="Select a company from the Companies tab to view its trajectory." />;
    }
    if (trajectoryLoading) return <LoadingSpinner text={`Loading trajectory for ${selectedTicker}...`} />;
    if (!trajectoryData) return <EmptyState text="No trajectory data available." />;

    const { profile, scored_chunks, anchor_distribution, executive_roster: roster, transcripts } = trajectoryData;

    // Group chunks by fiscal period
    const chunksByPeriod = {};
    (scored_chunks || []).forEach(c => {
      if (!chunksByPeriod[c.fiscal_period]) chunksByPeriod[c.fiscal_period] = [];
      chunksByPeriod[c.fiscal_period].push(c);
    });

    // Group anchor distribution by period
    const anchorByPeriod = {};
    (anchor_distribution || []).forEach(a => {
      if (!anchorByPeriod[a.fiscal_period]) anchorByPeriod[a.fiscal_period] = {};
      anchorByPeriod[a.fiscal_period][a.anchor || a.primary_anchor] = { avg_score: a.avg_score, chunk_count: a.chunk_count };
    });

    const periods = Object.keys(chunksByPeriod).sort();

    // Section nav items — only show sections that have data
    const sectionNav = [
      periods.length > 0 && { id: 'swee-anchor-heatmap', label: 'Anchor Heatmap', icon: '▦', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
      { id: 'swee-financials', label: 'Financials', icon: '◰', color: 'bg-violet-50 text-violet-700 hover:bg-violet-100' },
      roster && roster.length > 0 && { id: 'swee-exec-roster', label: 'Roster', icon: '◉', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
      periods.length > 0 && { id: 'swee-scored-chunks', label: 'Scored Chunks', icon: '◈', color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
      transcripts && transcripts.length > 0 && { id: 'swee-transcripts', label: 'Transcripts', icon: '◱', color: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
    ].filter(Boolean);

    const scrollToSection = (id) => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
      <div className="space-y-6">
        {/* Company Header + Sticky Section Nav */}
        <div className="sticky top-0 z-20 -mx-1 px-1">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  <span className="font-mono text-emerald-700">{profile?.ticker}</span> — {profile?.company_name}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {profile?.sector}{profile?.sub_industry ? ` / ${profile.sub_industry}` : ''}
                  {profile?.is_sp500 && <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">S&P 500</span>}
                </p>
              </div>
              <div className="flex gap-2">
                {profile?.koyfin_url && (
                  <a href={profile.koyfin_url} target="_blank" rel="noopener noreferrer"
                     className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                    Koyfin
                  </a>
                )}
                {profile?.edgar_cik && (
                  <a href={`https://efts.sec.gov/LATEST/search-index?q=%22${profile.ticker}%22&dateRange=custom&startdt=2024-01-01&forms=10-K,10-Q`}
                     target="_blank" rel="noopener noreferrer"
                     className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                    SEC EDGAR
                  </a>
                )}
              </div>
            </div>
            {/* Section jump nav */}
            {sectionNav.length > 0 && (
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-gray-100">
                <span className="text-xs text-gray-400 mr-1">Jump to:</span>
                {sectionNav.map(s => (
                  <button
                    key={s.id}
                    onClick={() => scrollToSection(s.id)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${s.color}`}
                  >
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Anchor Heatmap across periods */}
        {periods.length > 0 && (
          <div id="swee-anchor-heatmap" className="bg-white rounded-lg shadow-sm border p-6 scroll-mt-36">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Anchor Evolution by Quarter</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-gray-500 font-medium">Anchor</th>
                    {periods.map(p => <th key={p} className="px-3 py-2 text-center text-gray-500 font-medium">{p}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(ANCHOR_COLORS).map(anchor => (
                    <tr key={anchor} className="border-t">
                      <td className="px-3 py-2"><AnchorBadge anchor={anchor} /></td>
                      {periods.map(period => {
                        const cell = anchorByPeriod[period]?.[anchor];
                        if (!cell) return <td key={period} className="px-3 py-2 text-center text-gray-300">—</td>;
                        return (
                          <td key={period} className="px-3 py-2 text-center">
                            <span className={`font-bold px-2 py-0.5 rounded ${getScoreColor(cell.avg_score)}`}>
                              {cell.avg_score?.toFixed(1)}
                            </span>
                            <span className="text-gray-400 ml-1">({cell.chunk_count})</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SEC EDGAR Financial Metrics */}
        <div id="swee-financials" className="bg-white rounded-lg shadow-sm border p-6 scroll-mt-36">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">SEC EDGAR Financial Metrics</h3>
            <button
              onClick={() => pullEdgarFinancials(profile?.ticker)}
              disabled={financialFetching}
              className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50"
            >
              {financialFetching ? 'Fetching from EDGAR...' : (financialData?.has_data ? 'Refresh from EDGAR' : 'Fetch from EDGAR')}
            </button>
          </div>

          {financialLoading ? (
            <div className="py-8 text-center text-gray-400 text-sm">Loading financial data...</div>
          ) : !financialData?.has_data ? (
            <div className="py-8 text-center text-gray-400 text-sm">
              No financial data stored yet. Click "Fetch from EDGAR" to pull SEC filings.
            </div>
          ) : (
            <div>
              {/* Chart mode selector + range toggle */}
              <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                <div className="flex gap-1 flex-wrap">
                  {[
                    { key: 'revenue', label: 'Revenue & Income' },
                    { key: 'profitability', label: 'Profitability' },
                    { key: 'cashflow', label: 'Cash Flow & FCF' },
                    { key: 'efficiency', label: 'Efficiency Ratios' },
                    { key: 'workforce', label: 'Workforce' },
                    { key: 'eps', label: 'EPS' },
                  ].map(mode => (
                    <button
                      key={mode.key}
                      onClick={() => setFinancialChartMode(mode.key)}
                      className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                        financialChartMode === mode.key
                          ? 'bg-violet-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5">
                  <button
                    onClick={() => setFinancialChartRange('recent')}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                      financialChartRange === 'recent'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    2020–Present
                  </button>
                  <button
                    onClick={() => setFinancialChartRange('full')}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                      financialChartRange === 'full'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    2015–Present
                  </button>
                </div>
              </div>

              {/* Chart rendering */}
              <FinancialChart data={financialData.data} mode={financialChartMode} range={financialChartRange} />

              {/* Data table below chart */}
              <details className="mt-4">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                  View raw data ({financialData.total} periods)
                </summary>
                <div className="mt-2 overflow-x-auto max-h-64 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-gray-500">Period</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">Revenue</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">Gross Profit</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">SG&A</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">Net Income</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">Op. Cash Flow</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">FCF</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">Capex</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">Employees</th>
                        <th className="px-2 py-1.5 text-right text-gray-500">EPS (D)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financialData.data.map((d, i) => (
                        <tr key={i} className={`border-t ${d.fiscal_period.endsWith('-FY') ? 'bg-violet-50 font-medium' : ''}`}>
                          <td className="px-2 py-1.5 font-mono">{d.fiscal_period}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.revenue)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.gross_profit)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.sga_expense)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.net_income)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.operating_cash_flow)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.free_cash_flow)}</td>
                          <td className="px-2 py-1.5 text-right">{fmtDollar(d.capital_expenditure)}</td>
                          <td className="px-2 py-1.5 text-right">
                            {d.employee_count ? d.employee_count.toLocaleString() : '—'}
                            {d.employee_count_interpolated && <span className="text-amber-500 ml-0.5" title={`Carried from ${d.employee_count_source_period}`}>*</span>}
                          </td>
                          <td className="px-2 py-1.5 text-right">{d.eps_diluted != null ? `$${d.eps_diluted.toFixed(2)}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
        </div>

        {/* Executive Roster */}
        {roster && roster.length > 0 && (
          <div id="swee-exec-roster" className="bg-white rounded-lg shadow-sm border p-6 scroll-mt-36">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Executive Roster</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {roster.map((exec, idx) => (
                <div key={idx} className={`p-3 rounded-lg border ${exec.is_current ? 'bg-white' : 'bg-gray-50 opacity-70'}`}>
                  <div className="font-medium text-sm text-gray-900">{exec.name}</div>
                  <div className="text-xs text-gray-500">{exec.role}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {exec.first_seen} — {exec.is_current ? 'Present' : exec.last_seen}
                  </div>
                  {exec.turnover_detected && (
                    <div className="text-xs text-red-500 mt-1">Turnover detected {exec.predecessor ? `(replaced ${exec.predecessor})` : ''}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scored Chunks by Period */}
        {periods.map((period, pIdx) => (
          <div key={period} id={pIdx === 0 ? 'swee-scored-chunks' : undefined} className={`bg-white rounded-lg shadow-sm border p-6${pIdx === 0 ? ' scroll-mt-36' : ''}`}>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">{period}</h3>
            <div className="space-y-2">
              {chunksByPeriod[period].map((chunk, idx) => (
                <ChunkCard key={idx} chunk={chunk} />
              ))}
            </div>
          </div>
        ))}

        {/* Transcripts list — clickable to expand full text */}
        {transcripts && transcripts.length > 0 && (
          <div id="swee-transcripts" className="bg-white rounded-lg shadow-sm border p-6 scroll-mt-36">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Earnings Call Transcripts</h3>
            <div className="space-y-2">
              {transcripts.map((t, idx) => {
                const isExpanded = expandedTranscript === `${profile?.ticker}-${t.fiscal_period}`;
                const [tYear, tQStr] = (t.fiscal_period || '').split('-Q');
                const tAudioKey = `${profile?.ticker}-${tYear}-Q${tQStr}`;
                const isAudioActive = audioKey === tAudioKey;
                return (
                  <div key={idx}>
                    <div
                      onClick={() => fetchTranscript(profile?.ticker, t.fiscal_period)}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded text-xs cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                        <span className="font-mono font-medium">{t.fiscal_period}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded ${t.status === 'scored' ? 'bg-green-100 text-green-700' : t.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {t.status}
                      </span>
                      <span className="text-gray-400">{t.chunk_count || 0} chunks</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tYear && tQStr) downloadSlides(profile?.ticker, parseInt(tYear), parseInt(tQStr));
                        }}
                        disabled={downloadingSlides === `${profile?.ticker}-${t.fiscal_period}`}
                        className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded hover:bg-cyan-200 disabled:opacity-50 whitespace-nowrap"
                      >
                        {downloadingSlides === `${profile?.ticker}-${t.fiscal_period}` ? 'Loading...' : 'Slides'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tYear && tQStr) fetchAudio(profile?.ticker, parseInt(tYear), parseInt(tQStr));
                        }}
                        disabled={audioLoading === tAudioKey}
                        className={`px-2 py-0.5 rounded whitespace-nowrap ${
                          isAudioActive ? 'bg-purple-300 text-purple-900' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        } disabled:opacity-50`}
                      >
                        {audioLoading === tAudioKey ? 'Loading...' : isAudioActive ? 'Playing' : 'Audio'}
                      </button>
                      <span className="text-emerald-600 font-medium">Click to read</span>
                    </div>

                    {/* Inline audio player */}
                    {isAudioActive && audioUrl && (
                      <div className="mx-3 mt-2 mb-1 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-purple-800">
                            Earnings Call Audio — {profile?.ticker} {t.fiscal_period}
                          </span>
                          <button
                            onClick={() => { setAudioUrl(null); setAudioKey(null); }}
                            className="text-purple-500 hover:text-purple-700 text-sm font-bold"
                          >&times;</button>
                        </div>
                        <audio controls src={audioUrl} className="w-full" controlsList="nodownload" />
                      </div>
                    )}

                    {/* Expanded transcript viewer */}
                    {isExpanded && (
                      <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
                        {transcriptLoading ? (
                          <div className="p-6 text-center text-gray-400 text-sm">Loading transcript...</div>
                        ) : transcriptData ? (
                          <div>
                            {/* Search bar */}
                            <div className="p-3 bg-gray-50 border-b flex items-center gap-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <input
                                type="text"
                                value={transcriptSearch}
                                onChange={(e) => setTranscriptSearch(e.target.value)}
                                placeholder="Search transcript (e.g., hiring, AI, reskilling)..."
                                className="flex-1 px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                              />
                              {transcriptSearch && (
                                <span className="text-xs text-gray-500">
                                  {(() => {
                                    const regex = new RegExp(transcriptSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                                    const total = (transcriptData.sections || []).reduce((acc, s) => acc + ((s.text || '').match(regex) || []).length, 0);
                                    return `${total} match${total !== 1 ? 'es' : ''}`;
                                  })()}
                                </span>
                              )}
                              {transcriptSearch && (
                                <button onClick={() => setTranscriptSearch('')} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
                              )}
                            </div>

                            {/* Transcript metadata */}
                            <div className="px-4 py-2 bg-white border-b text-xs text-gray-500 flex gap-4">
                              <span>Date: {transcriptData.call_date || 'N/A'}</span>
                              <span>Speakers: {transcriptData.speaker_count || 'N/A'}</span>
                              <span>Chunks: {transcriptData.chunk_count || 0}</span>
                            </div>

                            {/* Sections */}
                            <div className="max-h-[600px] overflow-y-auto">
                              {(transcriptData.sections || []).map((section, sIdx) => (
                                <div key={sIdx} className="border-b last:border-b-0">
                                  <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 sticky top-0 flex items-center gap-2">
                                    <span className={section.section === 'Q&A' ? 'text-blue-600' : 'text-emerald-600'}>
                                      {section.section}
                                    </span>
                                    {section.speaker && (
                                      <span className="font-normal text-gray-500">— {section.speaker}</span>
                                    )}
                                  </div>
                                  <div className="px-4 py-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {transcriptSearch ? (
                                      // Highlight search matches
                                      (() => {
                                        const escaped = transcriptSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                        const regex = new RegExp(`(${escaped})`, 'gi');
                                        const parts = (section.text || '').split(regex);
                                        return parts.map((part, pIdx) =>
                                          regex.test(part)
                                            ? <mark key={pIdx} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
                                            : part
                                        );
                                      })()
                                    ) : (
                                      section.text || ''
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // =============================================
  // Render: Heatmap Tab
  // =============================================

  const renderHeatmap = () => {
    if (heatmapLoading) return <LoadingSpinner text="Loading heatmap..." />;
    if (!heatmapData || !heatmapData.matrix || heatmapData.matrix.length === 0) {
      return <EmptyState text="No heatmap data yet. Companies need scored chunks to appear here." />;
    }

    const { matrix, anchors } = heatmapData;

    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 overflow-x-auto">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Cross-Company Anchor Heatmap (Latest Avg Specificity)</h3>
        <table className="min-w-full text-xs">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-gray-500 font-medium sticky left-0 bg-white z-10">Company</th>
              {(anchors || Object.keys(ANCHOR_COLORS)).map(anchor => (
                <th key={anchor} className="px-3 py-2 text-center text-gray-500 font-medium whitespace-nowrap">
                  {anchor.replace(/ /g, '\n').split('\n').map((w, i) => <div key={i}>{w}</div>)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row) => (
              <tr key={row.ticker} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => fetchTrajectory(row.ticker)}>
                <td className="px-3 py-2 font-mono font-bold text-emerald-700 sticky left-0 bg-white z-10 whitespace-nowrap">
                  {row.ticker}
                  <span className="ml-1 font-normal text-gray-400">{row.company_name?.substring(0, 20)}</span>
                </td>
                {(anchors || Object.keys(ANCHOR_COLORS)).map(anchor => {
                  const score = row.scores?.[anchor];
                  if (score == null) return <td key={anchor} className="px-3 py-2 text-center text-gray-300">—</td>;
                  return (
                    <td key={anchor} className="px-3 py-2 text-center">
                      <span className={`font-bold px-2 py-0.5 rounded ${getScoreColor(score)}`}>
                        {typeof score === 'number' ? score.toFixed(1) : score}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // =============================================
  // Render: Search Tab
  // =============================================

  const renderSearch = () => {
    const allAnchors = Object.keys(ANCHOR_COLORS);
    const allVerdicts = Object.keys(VERDICT_COLORS);

    return (
      <div className="space-y-6">
        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Hybrid Search (Keyword + Semantic)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Keywords (BM25 text search)</label>
              <input type="text" value={searchKeywords} onChange={(e) => setSearchKeywords(e.target.value)}
                placeholder="e.g., reskilling campus hiring" className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Semantic Query (vector similarity)</label>
              <input type="text" value={searchSemantic} onChange={(e) => setSearchSemantic(e.target.value)}
                placeholder="e.g., replacing entry-level with AI tools" className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Tickers (comma-separated)</label>
              <input type="text" value={searchTickers} onChange={(e) => setSearchTickers(e.target.value)}
                placeholder="e.g., IBM, ACN, NOW" className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min Score</label>
              <input type="number" min="1" max="10" value={searchMinScore} onChange={(e) => setSearchMinScore(e.target.value)}
                placeholder="1-10" className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Anchors</label>
              <div className="flex flex-wrap gap-1">
                {allAnchors.map(a => (
                  <button key={a} onClick={() => setSearchAnchors(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a])}
                    className={`text-xs px-2 py-1 rounded border ${searchAnchors.includes(a) ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : 'bg-white border-gray-300 text-gray-600'}`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Verdicts</label>
              <div className="flex flex-wrap gap-1">
                {allVerdicts.map(v => (
                  <button key={v} onClick={() => setSearchVerdicts(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])}
                    className={`text-xs px-2 py-1 rounded border ${searchVerdicts.includes(v) ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : 'bg-white border-gray-300 text-gray-600'}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={runSearch} disabled={searchLoading}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50">
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
            <button onClick={() => { setSearchKeywords(''); setSearchSemantic(''); setSearchTickers(''); setSearchAnchors([]); setSearchMinScore(''); setSearchVerdicts([]); setSearchResults(null); }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              Clear
            </button>
          </div>
        </div>

        {/* Search Results */}
        {searchResults && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Results ({searchResults.total || searchResults.results?.length || 0})
            </h3>
            <div className="space-y-3">
              {(searchResults.results || []).map((r, idx) => (
                <ChunkCard key={idx} chunk={r} showTicker />
              ))}
              {(!searchResults.results || searchResults.results.length === 0) && (
                <p className="text-gray-400 text-sm">No results found.</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // =============================================
  // Render: Workforce Intelligence Tab
  // =============================================

  // ── Hiring Tracker Tab ──────────────────────────────────
  const VELOCITY_COLORS = { UP: 'text-green-600', DOWN: 'text-red-600', FLAT: 'text-gray-500' };
  const TREND_COLORS = {
    ACCELERATING: 'text-green-600', RECOVERING: 'text-blue-600', STABLE: 'text-gray-500',
    DECELERATING: 'text-orange-500', DECLINING: 'text-red-600', INSUFFICIENT_DATA: 'text-gray-400',
    STRONG_GROWTH: 'text-green-700', MODERATE_GROWTH: 'text-green-500', MODERATE_DECLINE: 'text-orange-600', SHARP_DECLINE: 'text-red-700',
  };
  const SENIORITY_COLORS = { SHIFTING_JUNIOR: 'text-blue-600', SHIFTING_SENIOR: 'text-purple-600', STABLE: 'text-gray-500', INSUFFICIENT_DATA: 'text-gray-400' };

  const renderTracker = () => (
    <div>
      {/* Credit Budget Bar */}
      {trackerCredits && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              TheirStack Credits ({trackerCredits.month})
            </span>
            <span className="text-sm font-semibold text-blue-800 dark:text-blue-200">
              {trackerCredits.used} / {trackerCredits.limit} used
            </span>
          </div>
          <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${trackerCredits.used / trackerCredits.limit > 0.9 ? 'bg-red-500' : trackerCredits.used / trackerCredits.limit > 0.7 ? 'bg-yellow-500' : 'bg-blue-600'}`}
              style={{ width: `${Math.min(100, (trackerCredits.used / trackerCredits.limit) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
            {trackerCredits.remaining} credits remaining. Each snapshot costs 25 credits.
          </p>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Tracked Companies ({trackerCompanies.length})
        </h3>
        <div className="flex gap-2">
          <button
            onClick={handleCollectAll}
            disabled={trackerSnapshotLoading || !trackerCompanies.length}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {trackerSnapshotLoading === 'ALL' ? 'Collecting...' : `Collect All (${trackerCompanies.length * 25} credits)`}
          </button>
          <button
            onClick={() => setBackfillTarget('ALL')}
            disabled={backfillLoading || !trackerCompanies.length}
            className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            Backfill All
          </button>
          <button
            onClick={fetchTrackerCompanies}
            className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-300"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowTrackerHelp(true)}
            className="px-3 py-1.5 text-sm bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 border border-gray-200"
            title="How to use Hiring Tracker"
          >
            ? Help
          </button>
        </div>
      </div>

      {trackerLoading ? (
        <div className="text-center py-8 text-gray-500">Loading tracked companies...</div>
      ) : trackerCompanies.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg mb-2">No companies tracked yet</p>
          <p className="text-sm">Go to the <button onClick={() => changeTab('companies')} className="text-blue-600 underline">Companies</button> tab to set a domain and enable TheirStack tracking for companies.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Open Jobs</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">New (30d)</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Refresh %</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">MoM</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">3-Mo Trend</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Velocity</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Seniority</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Months</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {trackerCompanies.map(co => (
                <tr key={co.ticker} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-white">{co.ticker}</div>
                    <div className="text-xs text-gray-500">{co.company_name}</div>
                    <div className="text-xs text-blue-500">{co.company_domain}</div>
                  </td>
                  <td className="px-3 py-2 text-center font-semibold">{co.latest_num_jobs != null ? co.latest_num_jobs.toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-center">{co.latest_num_jobs_last_30 != null ? co.latest_num_jobs_last_30.toLocaleString() : '—'}</td>
                  <td className="px-3 py-2 text-center">{co.latest_refresh_rate != null ? `${co.latest_refresh_rate}%` : '—'}</td>
                  <td className={`px-3 py-2 text-center font-medium ${co.mom_change_pct > 0 ? 'text-green-600' : co.mom_change_pct < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                    {co.mom_change_pct != null ? `${co.mom_change_pct > 0 ? '+' : ''}${co.mom_change_pct}%` : '—'}
                  </td>
                  <td className={`px-3 py-2 text-center text-xs font-medium ${TREND_COLORS[co.three_month_trend] || 'text-gray-400'}`}>
                    {(co.three_month_trend || '—').replace(/_/g, ' ')}
                  </td>
                  <td className={`px-3 py-2 text-center font-medium ${VELOCITY_COLORS[co.velocity_direction] || 'text-gray-400'}`}>
                    {co.velocity_direction === 'UP' ? '▲ UP' : co.velocity_direction === 'DOWN' ? '▼ DOWN' : (co.velocity_direction || '—').replace(/_/g, ' ')}
                  </td>
                  <td className={`px-3 py-2 text-center text-xs ${SENIORITY_COLORS[co.seniority_shift] || 'text-gray-400'}`}>
                    {(co.seniority_shift || '—').replace(/_/g, ' ')}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-500">{co.months_tracked || 0}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex gap-1 justify-center flex-wrap">
                      <button
                        onClick={() => handleTrackerSnapshot(co.ticker)}
                        disabled={trackerSnapshotLoading === co.ticker}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                        title="Collect fresh snapshot (25 credits)"
                      >
                        {trackerSnapshotLoading === co.ticker ? '...' : 'Pull'}
                      </button>
                      <button
                        onClick={() => setBackfillTarget(co.ticker)}
                        disabled={backfillLoading}
                        className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                        title="Backfill historical data"
                      >
                        Backfill
                      </button>
                      <button
                        onClick={() => handleTrackerViewDetail(co.ticker)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        title="View snapshot history"
                      >
                        History
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tracker Help Modal */}
      {showTrackerHelp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowTrackerHelp(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-bold text-gray-900">Hiring Tracker &mdash; How It Works</h2>
              <button onClick={() => setShowTrackerHelp(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-5 text-sm text-gray-700">

              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Overview</h3>
                <p>
                  The Hiring Tracker uses TheirStack to monitor job postings for tracked companies. It collects monthly snapshots
                  of open positions, calculates trends, and feeds hiring data into SWEE signal reports.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Adding Companies to the Tracker</h3>
                <p className="text-gray-600 mb-2">
                  Companies must be set up on the <strong>Companies</strong> tab first. The Hiring Tracker only shows companies
                  that have TheirStack tracking enabled.
                </p>
                <ol className="list-decimal list-inside space-y-1.5 text-gray-600">
                  <li>Go to the <strong>Companies</strong> tab and search for the ticker you want to track.</li>
                  <li>In the <strong>TheirStack</strong> column, click <span className="font-mono text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Set domain</span> if needed and enter the company's website domain (e.g., "chevron.com").</li>
                  <li>Click <span className="font-mono text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Enable</span> to activate tracking. The button turns to <span className="font-mono text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Tracked</span>.</li>
                  <li>Return to the <strong>Hiring Tracker</strong> tab &mdash; the company now appears here.</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Collecting Data</h3>
                <ul className="list-disc list-inside space-y-1.5 text-gray-600">
                  <li><span className="font-mono text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Pull</span> &mdash; Collects a fresh snapshot for one company (25 credits).</li>
                  <li><span className="font-mono text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Collect All</span> &mdash; Pulls snapshots for all tracked companies at once.</li>
                  <li><span className="font-mono text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Backfill</span> &mdash; Pulls historical snapshots going back 1-6 months. Months with existing data are skipped.</li>
                  <li><span className="font-mono text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">History</span> &mdash; View all collected snapshots and detailed job posting breakdowns.</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Table Columns</h3>
                <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">Column</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr><td className="px-3 py-1.5 font-medium">Open Jobs</td><td className="px-3 py-1.5 text-gray-600">Total active job postings from the latest snapshot</td></tr>
                    <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">New (30d)</td><td className="px-3 py-1.5 text-gray-600">Job postings created in the last 30 days</td></tr>
                    <tr><td className="px-3 py-1.5 font-medium">Refresh %</td><td className="px-3 py-1.5 text-gray-600">Percentage of postings refreshed/updated recently &mdash; high = active hiring</td></tr>
                    <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">MoM</td><td className="px-3 py-1.5 text-gray-600">Month-over-month change in total open jobs</td></tr>
                    <tr><td className="px-3 py-1.5 font-medium">3-Mo Trend</td><td className="px-3 py-1.5 text-gray-600">Overall 3-month trend direction (growing, declining, stable, etc.)</td></tr>
                    <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">Velocity</td><td className="px-3 py-1.5 text-gray-600">Rate of change &mdash; accelerating or decelerating hiring</td></tr>
                    <tr><td className="px-3 py-1.5 font-medium">Seniority</td><td className="px-3 py-1.5 text-gray-600">Shift in seniority mix of postings (e.g., trending senior vs. entry-level)</td></tr>
                    <tr className="bg-gray-50"><td className="px-3 py-1.5 font-medium">Months</td><td className="px-3 py-1.5 text-gray-600">How many months of tracking data exists for this company</td></tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Credits</h3>
                <p className="text-gray-600">
                  TheirStack uses a credit-based system. Each snapshot costs 25 credits. The credit budget bar at the top
                  shows your current usage and remaining balance for the month. Credits reset monthly.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Tips</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>TheirStack data feeds into SWEE signal reports as the "TheirStack" source (1 of 9).</li>
                  <li>Pull snapshots monthly to build trend data. More months = better trend analysis.</li>
                  <li>The company domain must be the main website (e.g., "fedex.com"), not a careers subdomain.</li>
                  <li>If a company shows 0 open jobs, verify the domain is correct on the Companies tab.</li>
                </ul>
              </div>

            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-3 rounded-b-xl">
              <button onClick={() => setShowTrackerHelp(false)} className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800">Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* Snapshot Detail Modal */}
      {trackerDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {trackerDetail.ticker} — Snapshot History
              </h3>
              <button onClick={() => setTrackerDetail(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {/* Trends Summary */}
            {trackerDetail.trends && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <div className="text-xs text-blue-600 uppercase">Open Jobs</div>
                  <div className="text-xl font-bold text-blue-800">{trackerDetail.trends.latest_num_jobs?.toLocaleString() || '—'}</div>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <div className="text-xs text-green-600 uppercase">MoM Change</div>
                  <div className={`text-xl font-bold ${trackerDetail.trends.mom_change_pct > 0 ? 'text-green-700' : trackerDetail.trends.mom_change_pct < 0 ? 'text-red-700' : 'text-gray-600'}`}>
                    {trackerDetail.trends.mom_change_pct != null ? `${trackerDetail.trends.mom_change_pct > 0 ? '+' : ''}${trackerDetail.trends.mom_change_pct}%` : '—'}
                  </div>
                </div>
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                  <div className="text-xs text-purple-600 uppercase">Velocity</div>
                  <div className={`text-lg font-bold ${VELOCITY_COLORS[trackerDetail.trends.velocity_direction] || ''}`}>
                    {(trackerDetail.trends.velocity_direction || '—').replace(/_/g, ' ')}
                  </div>
                </div>
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
                  <div className="text-xs text-orange-600 uppercase">3-Mo Trend</div>
                  <div className={`text-sm font-bold ${TREND_COLORS[trackerDetail.trends.three_month_trend] || ''}`}>
                    {(trackerDetail.trends.three_month_trend || '—').replace(/_/g, ' ')}
                  </div>
                </div>
              </div>
            )}

            {/* Snapshots Table */}
            <table className="min-w-full text-sm">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">
                Backfill rows show "—" for aggregate counts (Open Jobs, New 30d) because TheirStack only provides current-day totals. Click "Detail" to see the seniority, technology, and role breakdown for each month.
              </p>
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Open Jobs</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">New (30d)</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Refresh %</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Employees</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Jobs/Employee</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {(trackerDetail.snapshots || []).map((s, i) => {
                  const isBackfill = s.num_jobs == null && s.num_jobs_last_30_days == null;
                  return (
                  <tr key={s.id} className={`${i === 0 ? 'bg-blue-50 dark:bg-blue-900/10' : ''} ${isBackfill ? 'opacity-80' : ''}`}>
                    <td className="px-3 py-2 font-medium">
                      {s.snapshot_date}
                      {isBackfill && <span className="ml-1.5 text-[10px] text-purple-500 font-normal">(backfill)</span>}
                    </td>
                    <td className="px-3 py-2 text-center">{s.num_jobs?.toLocaleString() || '—'}</td>
                    <td className="px-3 py-2 text-center">{s.num_jobs_last_30_days?.toLocaleString() || '—'}</td>
                    <td className="px-3 py-2 text-center">{s.refresh_rate != null ? `${s.refresh_rate}%` : '—'}</td>
                    <td className="px-3 py-2 text-center">{s.employee_count?.toLocaleString() || '—'}</td>
                    <td className="px-3 py-2 text-center">{s.jobs_per_employee || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${BACKEND_URL}/api/swee/tracker/snapshot-detail/${s.id}`, { headers: getHeaders() });
                            setTrackerDetail(prev => ({ ...prev, selectedSnapshot: res.data }));
                          } catch (err) { setError('Failed to load snapshot detail'); }
                        }}
                        className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {(!trackerDetail.snapshots || trackerDetail.snapshots.length === 0) && (
              <p className="text-center py-4 text-gray-500 text-sm">No snapshots yet. Click "Pull" to collect the first snapshot.</p>
            )}

            {/* Snapshot Detail Panel */}
            {trackerDetail.selectedSnapshot && (
              <div className="mt-4 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-700 dark:text-gray-300">
                    Snapshot Detail — {trackerDetail.selectedSnapshot.snapshot?.snapshot_date}
                  </h4>
                  <button onClick={() => setTrackerDetail(prev => ({ ...prev, selectedSnapshot: null }))}
                    className="text-xs text-gray-400 hover:text-gray-600">Close</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Seniority Breakdown */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                    <h5 className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase mb-2">Seniority</h5>
                    {(trackerDetail.selectedSnapshot.seniority || []).length > 0 ? (
                      <div className="space-y-1">
                        {trackerDetail.selectedSnapshot.seniority.map(s => (
                          <div key={s.seniority} className="flex justify-between text-xs">
                            <span className="text-gray-700 dark:text-gray-300 capitalize">{s.seniority}</span>
                            <span className="font-medium">{s.count} ({s.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-gray-400">No data</p>}
                  </div>

                  {/* Top Technologies */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <h5 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase mb-2">Technologies</h5>
                    {(trackerDetail.selectedSnapshot.technologies || []).length > 0 ? (
                      <div className="space-y-1">
                        {trackerDetail.selectedSnapshot.technologies.slice(0, 10).map(t => (
                          <div key={t.slug} className="flex justify-between text-xs">
                            <span className="text-gray-700 dark:text-gray-300">{t.slug}</span>
                            <span className="font-medium">{t.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-gray-400">No data</p>}
                  </div>

                  {/* Top Roles */}
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <h5 className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase mb-2">Sample Roles</h5>
                    {(trackerDetail.selectedSnapshot.roles || []).length > 0 ? (
                      <div className="space-y-1.5">
                        {trackerDetail.selectedSnapshot.roles.slice(0, 8).map((r, idx) => (
                          <div key={idx} className="text-xs">
                            <div className="font-medium text-gray-800 dark:text-gray-200 break-words">{r.job_title}</div>
                            <div className="text-gray-500">
                              {[r.seniority, r.location, r.remote ? 'Remote' : null].filter(Boolean).join(' · ')}
                              {r.min_salary ? ` · $${(r.min_salary/1000).toFixed(0)}k` : ''}
                              {r.max_salary ? `–$${(r.max_salary/1000).toFixed(0)}k` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-gray-400">No data</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backfill Confirmation Modal */}
      {backfillTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
              {backfillTarget === 'ALL' ? 'Backfill All Tracked Companies' : `Backfill ${backfillTarget}`}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How many months back?
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <button
                    key={n}
                    onClick={() => setBackfillMonths(n)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      backfillMonths === n
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-purple-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Credit Cost Preview */}
            <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <div className="text-sm text-purple-700 dark:text-purple-300">
                <span className="font-semibold">Estimated cost: </span>
                {backfillTarget === 'ALL'
                  ? `${trackerCompanies.length} companies × ${backfillMonths} months × 25 = ${trackerCompanies.length * backfillMonths * 25} credits (max)`
                  : `${backfillMonths} months × 25 = ${backfillMonths * 25} credits (max)`
                }
              </div>
              <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                Months with existing snapshots are skipped (0 credits).
              </p>
              {trackerCredits && (
                <p className="text-xs text-purple-500 dark:text-purple-400">
                  Available: {trackerCredits.remaining} credits
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setBackfillTarget(null); setBackfillResults(null); }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => backfillTarget === 'ALL' ? handleBackfillAll() : handleBackfillCompany(backfillTarget)}
                disabled={backfillLoading}
                className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {backfillLoading ? 'Backfilling...' : `Start Backfill (${backfillTarget === 'ALL' ? trackerCompanies.length * backfillMonths * 25 : backfillMonths * 25} credits max)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // =============================================

  const renderWI = () => {
    const allCategories = Object.keys(WI_CATEGORY_LABELS);
    const allSignificances = ['MATERIAL_EVENT', 'NOTABLE_CHANGE', 'ROUTINE'];
    const allDirections = ['POSITIVE', 'NEGATIVE', 'NEUTRAL', 'MIXED'];
    const allTemporal = ['FORWARD', 'CURRENT', 'HISTORICAL'];

    return (
      <div className="space-y-6">
        {/* Help Button */}
        <div className="flex justify-end">
          <button onClick={() => setShowWIHelp(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors border border-indigo-200">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            How to Read WI Chunks
          </button>
        </div>

        {/* WI Help Modal */}
        {showWIHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowWIHelp(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-indigo-700 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
                <h2 className="text-lg font-bold">Workforce Intelligence — How to Read WI Scored Chunks</h2>
                <button onClick={() => setShowWIHelp(false)} className="text-white hover:text-gray-300 text-xl font-bold">&times;</button>
              </div>
              <div className="px-6 py-5 space-y-6 text-sm">

                {/* What is WI? */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    What is Workforce Intelligence?
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Workforce Intelligence (WI) analyzes public company earnings call transcripts for <strong>human capital signals</strong> — hiring plans, layoffs, retention programs, compensation changes, and workforce risks. While SWEE tracks strategic <em>workforce evolution</em> (AI adoption, geographic shifts), WI focuses on <strong>operational workforce data</strong>: the numbers, programs, and decisions that reveal how a company is managing its people.
                  </p>
                </div>

                {/* The Summary Panel */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    The Summary Panel
                  </h3>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3 text-gray-700">
                    <p><strong>Gate Relevance Cards</strong> — Show how many chunks passed the LLM's relevance check:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>Relevant</strong> — The chunk meaningfully discusses workforce topics. Full scoring applied.</li>
                      <li><strong>Adjacent</strong> — Peripherally touches workforce themes (e.g., "revenue per employee" in a financial overview). Scored with a -2 penalty.</li>
                      <li><strong>No Signal</strong> — No workforce content found. Financial results, product announcements, and market commentary get filtered here. These are excluded from the chunk list by default.</li>
                    </ul>
                    <p><strong>NO_SIGNAL Rate</strong> — The percentage of chunks that passed the embedding gate but were rejected by the LLM. A rate above 30% (shown in red) means the embedding threshold may be too low, letting through chunks that aren't actually workforce-related.</p>
                    <p><strong>By Category</strong> — Clickable cards showing how many chunks fell into each of the 7 workforce categories. Click any category to filter the chunk list.</p>
                    <p><strong>Top Companies</strong> — The companies with the most WI signals. The "Material" column counts chunks classified as Material Events — the highest-impact workforce signals.</p>
                  </div>
                </div>

                {/* Reading a Chunk Card */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    Reading a Chunk Card
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3 text-gray-700">
                    <p>Each scored chunk is displayed as a card with several elements. Here's what each one means:</p>

                    <div className="border-l-4 border-indigo-400 pl-3 space-y-2">
                      <p><strong>Header Row (left side):</strong></p>
                      <ul className="list-disc ml-5 space-y-1">
                        <li><span className="font-mono text-indigo-700 font-bold">TICKER</span> — The company symbol. Click to filter all chunks for that company.</li>
                        <li><span className="text-gray-500 text-xs">FY2025-Q1</span> — The fiscal period of the earnings call.</li>
                        <li><span className="inline-flex text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300">Workforce Sizing</span> — The <strong>primary category</strong> — what workforce topic this chunk is primarily about.</li>
                        <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">Retention</span> — <strong>Secondary categories</strong> (smaller, gray) — additional topics mentioned in the same chunk.</li>
                        <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">ADJACENT</span> — Appears when the chunk is workforce-adjacent rather than directly relevant. The score has a -2 penalty applied.</li>
                        <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200">SWEE</span> — This chunk was also scored by the SWEE pipeline. It appeared in both the workforce and strategic analysis.</li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-purple-400 pl-3 space-y-2">
                      <p><strong>Header Row (right side):</strong></p>
                      <ul className="list-disc ml-5 space-y-1">
                        <li>
                          <strong>Significance Badge</strong> — How impactful the workforce signal is:
                          <ul className="list-none ml-3 mt-1 space-y-0.5">
                            <li><span className="inline-flex text-xs px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300">MATERIAL</span> — A major workforce event with financial impact: large-scale layoffs, restructuring charges, regulatory settlements, acquisition-driven integration.</li>
                            <li><span className="inline-flex text-xs px-2 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300">NOTABLE</span> — A departure from the company's prior trajectory: new programs, policy changes, accelerations, reversals.</li>
                            <li><span className="inline-flex text-xs px-2 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-300">ROUTINE</span> — Standard operational reporting: "in line with expectations," "continues to," steady-state metrics.</li>
                          </ul>
                        </li>
                        <li>
                          <strong>Direction Badge</strong> — The signal polarity:
                          <ul className="list-none ml-3 mt-1 space-y-0.5">
                            <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">POSITIVE</span> — Growth, investment, improvement, expansion</li>
                            <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">NEGATIVE</span> — Contraction, cuts, decline, risk</li>
                            <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">NEUTRAL</span> — Status quo, informational context</li>
                            <li><span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">MIXED</span> — Both positive and negative elements (e.g., layoffs funding reinvestment)</li>
                          </ul>
                        </li>
                        <li>
                          <strong>Score</strong> — The Specificity Score (1–10), color-coded:
                          <ul className="list-none ml-3 mt-1 space-y-0.5">
                            <li><span className="font-bold text-green-700">7–10</span> (green) — Metric-driven or structural: specific numbers, named programs, timelines, auditable detail</li>
                            <li><span className="font-bold text-yellow-700">4–6</span> (yellow) — Operational: partial evidence, directional claims with some specifics</li>
                            <li><span className="font-bold text-gray-600">1–3</span> (gray) — Cosmetic: platitudes or unquantified intent ("Our people are our greatest asset")</li>
                          </ul>
                          <p className="mt-1 text-xs text-gray-500">If a second number appears after a slash (e.g., <span className="font-bold">5</span><span className="text-gray-400">/7</span>), the first is the <strong>calibrated</strong> score after penalties and the second is the <strong>raw</strong> score before penalties. Penalties: -1 for forward-looking claims, -2 for adjacent relevance.</p>
                        </li>
                        <li>
                          <strong>Temporal Stance</strong> — When the claim relates to:
                          <ul className="list-none ml-3 mt-1 space-y-0.5">
                            <li><strong>FORWARD</strong> — Stated intention ("we plan to," "going forward"). Score receives -1 penalty.</li>
                            <li><strong>CURRENT</strong> — Active execution ("we are," "this quarter")</li>
                            <li><strong>HISTORICAL</strong> — Reported result ("we achieved," "last quarter")</li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Body of the Card */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    Card Body
                  </h3>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3 text-gray-700">
                    <p><strong>Speaker</strong> — Who said it. The name, their role (CEO, CFO, VP HR, etc.), and which section of the call it appeared in (Prepared Remarks or Q&A). Workforce signals from C-suite executives in prepared remarks are typically more intentional than Q&A responses.</p>
                    <p><strong>Summary</strong> (indigo box) — A one-sentence AI-generated summary of the chunk's workforce content and key evidence. This is the fastest way to understand what the chunk is about without reading the full transcript.</p>
                    <p><strong>Significance Reasoning</strong> (italic text) — A one-sentence explanation of why the significance level (Material / Notable / Routine) was chosen. Useful for understanding the model's logic.</p>
                    <p><strong>Gate Info</strong> (gray text) — Technical metadata:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>Gate:</strong> Which WI centroid (topic embedding) matched this chunk and let it through the cosine gate.</li>
                      <li><strong>Sim:</strong> The cosine similarity score between the chunk and the matching centroid. Higher = stronger topical match.</li>
                    </ul>
                    <p><strong>Transcript Text</strong> — The actual earnings call excerpt. Click "Show more" to expand the full chunk. This is the raw source material the AI analyzed.</p>
                  </div>
                </div>

                {/* The 7 Categories */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                    The 7 Workforce Categories
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-blue-100 text-blue-800 border-blue-300 flex-shrink-0 mt-0.5">Workforce Sizing</span>
                      <span className="text-gray-600">Hiring volume, headcount targets, layoffs, restructuring charges, severance, site closures, net workforce changes.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300 flex-shrink-0 mt-0.5">Retention</span>
                      <span className="text-gray-600">Attrition rates, turnover, engagement scores, talent competition, retention programs, regretted attrition, burnout.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-green-100 text-green-800 border-green-300 flex-shrink-0 mt-0.5">Compensation</span>
                      <span className="text-gray-600">Salary adjustments, bonus structures, equity programs, benefits changes, wage inflation, pay equity, cost-per-employee.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-violet-100 text-violet-800 border-violet-300 flex-shrink-0 mt-0.5">Composition</span>
                      <span className="text-gray-600">Workforce demographics, diversity metrics, inclusion programs, pay equity audits, pipeline programs, board composition.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300 flex-shrink-0 mt-0.5">Risk</span>
                      <span className="text-gray-600">Labor disputes, union activity, regulatory investigations, workplace safety, contractor classification, employment litigation.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-cyan-100 text-cyan-800 border-cyan-300 flex-shrink-0 mt-0.5">Learning</span>
                      <span className="text-gray-600">Training programs, reskilling, upskilling, tuition benefits, internal mobility, promotion rates, L&D investment.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="inline-flex text-xs px-2 py-0.5 rounded border bg-indigo-100 text-indigo-800 border-indigo-300 flex-shrink-0 mt-0.5">Executive Commentary</span>
                      <span className="text-gray-600">High-level talent philosophy, culture commentary, employer brand statements, talent market outlook, board-level workforce governance.</span>
                    </div>
                  </div>
                </div>

                {/* Alert Tiers */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">6</span>
                    Alert Tiers (for Alert Subscriptions)
                  </h3>
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p>WI chunks are automatically classified into alert tiers that determine whether they trigger notifications:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><span className="font-bold text-red-700">CRITICAL</span> — Workforce Sizing + Material Event + score 8+, or any Workforce Risk + Material Event. Major workforce disruptions.</li>
                      <li><span className="font-bold text-orange-700">HIGH</span> — Any category + Notable Change + score 7+. Significant departures from prior trajectory.</li>
                      <li><span className="font-bold text-blue-700">STANDARD</span> — Any category + score 5+. Meaningful operational detail worth monitoring.</li>
                      <li><span className="font-bold text-gray-500">LOW</span> — Routine significance or score 4 and below. Background noise.</li>
                    </ul>
                    <p className="text-xs text-gray-500 mt-2">Configure your alert subscription minimum tier on the Alerts tab to control which signals trigger notifications.</p>
                  </div>
                </div>

                {/* Using Filters */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">7</span>
                    Using the Filters
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p>Use the filter bar to narrow the chunk list. All filters are combined (AND logic):</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>Category</strong> — Show only one workforce topic (e.g., only Compensation chunks).</li>
                      <li><strong>Significance</strong> — Focus on Material Events or Notable Changes only.</li>
                      <li><strong>Direction</strong> — Filter by signal polarity (e.g., only Negative signals to find layoffs and cuts).</li>
                      <li><strong>Temporal</strong> — Forward-looking claims vs. current execution vs. historical results.</li>
                      <li><strong>Ticker</strong> — Show chunks from a specific company. You can also click any ticker in the chunk list or the top companies table.</li>
                      <li><strong>Min Score</strong> — Set a floor (e.g., 5+) to see only chunks with meaningful operational detail.</li>
                    </ul>
                    <p className="text-xs text-gray-500 mt-2">Tip: Start with Significance = Material Event to see the highest-impact signals first, then broaden.</p>
                  </div>
                </div>

                {/* WI vs SWEE */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xs font-bold">8</span>
                    WI vs. SWEE — What's the Difference?
                  </h3>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p><strong>SWEE</strong> (Dashboard / Search / Trajectory tabs) tracks <em>strategic workforce evolution</em>: Is the company pivoting to AI? Reshoring operations? Restructuring teams? SWEE uses 6 anchors (Pipeline Insurance, Skills-First Pivot, etc.) and assigns verdicts like "Genuine Pivot" or "AI-Washing."</p>
                    <p><strong>WI</strong> (this tab) tracks <em>operational workforce data</em>: How many people are they hiring? What's the attrition rate? Are they restructuring? WI uses 7 workforce categories and classifies significance (Routine / Notable / Material) and direction (Positive / Negative / Mixed).</p>
                    <p>The same transcript chunk can appear in <strong>both</strong> systems. When this happens, the chunk card shows a green <span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200">SWEE</span> badge, meaning it has both a strategic assessment (on the Search tab) and an operational workforce assessment (here).</p>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        {wiSummaryLoading ? (
          <div className="text-center py-8 text-gray-400">Loading WI summary...</div>
        ) : wiSummary ? (
          <>
            {/* Gate Relevance Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(wiSummary.by_gate_relevance || []).map(g => (
                <div key={g.gate} className="bg-white rounded-lg shadow-sm border p-4">
                  <div className="text-2xl font-bold text-gray-900">{g.count}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {g.gate === 'WORKFORCE_RELEVANT' ? 'Relevant' :
                     g.gate === 'WORKFORCE_ADJACENT' ? 'Adjacent' :
                     g.gate === 'NO_SIGNAL' ? 'No Signal' : g.gate}
                  </div>
                </div>
              ))}
              {/* NO_SIGNAL Rate */}
              {(() => {
                const totalGate = (wiSummary.by_gate_relevance || []).reduce((s, g) => s + g.count, 0);
                const noSig = (wiSummary.by_gate_relevance || []).find(g => g.gate === 'NO_SIGNAL');
                const rate = totalGate > 0 && noSig ? ((noSig.count / totalGate) * 100).toFixed(1) : '0.0';
                return (
                  <div className={`bg-white rounded-lg shadow-sm border p-4 ${parseFloat(rate) > 30 ? 'border-red-300' : ''}`}>
                    <div className={`text-2xl font-bold ${parseFloat(rate) > 30 ? 'text-red-600' : 'text-gray-900'}`}>{rate}%</div>
                    <div className="text-xs text-gray-500 mt-1">NO_SIGNAL Rate {parseFloat(rate) > 30 && '(Target: <30%)'}</div>
                  </div>
                );
              })()}
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">By Category</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(wiSummary.by_category || []).map(c => {
                  const colors = WI_CATEGORY_COLORS[c.category] || { bg: 'bg-gray-100', text: 'text-gray-800' };
                  return (
                    <div key={c.category}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${colors.bg} ${colors.border}`}
                      onClick={() => { setWiFilterCategory(c.category); setWiPage(1); fetchWIChunks(1); }}>
                      <div>
                        <div className={`text-sm font-medium ${colors.text}`}>{WI_CATEGORY_LABELS[c.category] || c.category}</div>
                        <div className="text-xs text-gray-500">Avg score: {c.avg_score || 'N/A'}</div>
                      </div>
                      <div className={`text-xl font-bold ${colors.text}`}>{c.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Significance + Direction Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">By Significance</h3>
                <div className="space-y-2">
                  {(wiSummary.by_significance || []).map(s => (
                    <div key={s.significance} className="flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-1 rounded border ${WI_SIGNIFICANCE_COLORS[s.significance] || 'bg-gray-100 text-gray-600'}`}>
                        {s.significance === 'MATERIAL_EVENT' ? 'Material Event' :
                         s.significance === 'NOTABLE_CHANGE' ? 'Notable Change' :
                         s.significance === 'ROUTINE' ? 'Routine' : s.significance}
                      </span>
                      <span className="font-bold text-gray-700">{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">By Signal Direction</h3>
                <div className="space-y-2">
                  {(wiSummary.by_direction || []).map(d => (
                    <div key={d.direction} className="flex items-center justify-between">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${WI_DIRECTION_COLORS[d.direction] || 'bg-gray-100 text-gray-600'}`}>
                        {d.direction}
                      </span>
                      <span className="font-bold text-gray-700">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top Tickers */}
            {wiSummary.top_tickers?.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Companies by WI Signal Count</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="pb-2 pr-4">Ticker</th>
                        <th className="pb-2 pr-4 text-right">Signals</th>
                        <th className="pb-2 pr-4 text-right">Material</th>
                        <th className="pb-2 text-right">Avg Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wiSummary.top_tickers.map(t => (
                        <tr key={t.ticker} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => { setWiFilterTicker(t.ticker); setWiPage(1); fetchWIChunks(1); }}>
                          <td className="py-1.5 pr-4 font-mono font-bold text-emerald-700">{t.ticker}</td>
                          <td className="py-1.5 pr-4 text-right">{t.count}</td>
                          <td className="py-1.5 pr-4 text-right">
                            {t.material_events > 0 ? (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-800">{t.material_events}</span>
                            ) : '—'}
                          </td>
                          <td className="py-1.5 text-right">{t.avg_score || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-400">
            <p className="text-lg mb-2">No Workforce Intelligence data yet</p>
            <p className="text-sm">Run the pipeline with WI enabled to start scoring chunks.</p>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Filter WI Chunks</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={wiFilterCategory} onChange={(e) => setWiFilterCategory(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm">
                <option value="">All</option>
                {allCategories.map(c => (
                  <option key={c} value={c}>{WI_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Significance</label>
              <select value={wiFilterSignificance} onChange={(e) => setWiFilterSignificance(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm">
                <option value="">All</option>
                {allSignificances.map(s => (
                  <option key={s} value={s}>{s === 'MATERIAL_EVENT' ? 'Material Event' : s === 'NOTABLE_CHANGE' ? 'Notable Change' : 'Routine'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Direction</label>
              <select value={wiFilterDirection} onChange={(e) => setWiFilterDirection(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm">
                <option value="">All</option>
                {allDirections.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Temporal</label>
              <select value={wiFilterTemporal} onChange={(e) => setWiFilterTemporal(e.target.value)}
                className="w-full px-2 py-1.5 border rounded text-sm">
                <option value="">All</option>
                {allTemporal.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Ticker</label>
              <input type="text" value={wiFilterTicker} onChange={(e) => setWiFilterTicker(e.target.value)}
                placeholder="e.g., IBM" className="w-full px-2 py-1.5 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Min Score</label>
              <input type="number" min="1" max="10" value={wiFilterMinScore} onChange={(e) => setWiFilterMinScore(e.target.value)}
                placeholder="1-10" className="w-full px-2 py-1.5 border rounded text-sm" />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => { setWiPage(1); fetchWIChunks(1); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
              Apply Filters
            </button>
            <button onClick={() => {
              setWiFilterCategory(''); setWiFilterSignificance(''); setWiFilterDirection('');
              setWiFilterTicker(''); setWiFilterMinScore(''); setWiFilterTemporal('');
              setWiPage(1); fetchWIChunks(1);
            }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              Clear
            </button>
          </div>
        </div>

        {/* WI Chunks List */}
        {wiChunksLoading ? (
          <div className="text-center py-8 text-gray-400">Loading WI chunks...</div>
        ) : wiChunks && wiChunks.chunks?.length > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">
                WI Scored Chunks ({wiChunks.total} total)
              </h3>
              <div className="text-xs text-gray-500">
                Page {wiChunks.page} of {wiChunks.pages}
              </div>
            </div>
            <div className="space-y-3">
              {wiChunks.chunks.map((chunk) => (
                <WIChunkCard key={chunk.id} chunk={chunk}
                  expanded={wiExpandedChunk === chunk.id}
                  onToggle={() => setWiExpandedChunk(wiExpandedChunk === chunk.id ? null : chunk.id)}
                  onTickerClick={(t) => { setWiFilterTicker(t); setWiPage(1); fetchWIChunks(1); }}
                />
              ))}
            </div>
            {/* Pagination */}
            {wiChunks.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button onClick={() => fetchWIChunks(wiPage - 1)} disabled={wiPage <= 1}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-30">Prev</button>
                {Array.from({ length: Math.min(wiChunks.pages, 7) }, (_, i) => {
                  let p;
                  if (wiChunks.pages <= 7) p = i + 1;
                  else if (wiPage <= 4) p = i + 1;
                  else if (wiPage >= wiChunks.pages - 3) p = wiChunks.pages - 6 + i;
                  else p = wiPage - 3 + i;
                  return (
                    <button key={p} onClick={() => fetchWIChunks(p)}
                      className={`px-3 py-1.5 text-sm border rounded ${p === wiPage ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-gray-50'}`}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => fetchWIChunks(wiPage + 1)} disabled={wiPage >= wiChunks.pages}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-30">Next</button>
              </div>
            )}
          </div>
        ) : wiChunks ? (
          <div className="bg-white rounded-lg shadow-sm border p-6 text-center text-gray-400">
            No chunks match current filters.
          </div>
        ) : null}
      </div>
    );
  };

  // =============================================
  // Render: External Signals Tab
  // =============================================

  const renderSignals = () => {
    const directionColor = (d) => {
      if (d === 'NEGATIVE') return 'text-red-600 bg-red-50';
      if (d === 'POSITIVE') return 'text-green-600 bg-green-50';
      return 'text-gray-600 bg-gray-50';
    };
    const sourceColor = (s) => {
      if (s === 'WARN') return 'bg-orange-100 text-orange-700';
      if (s === 'SEC_8K') return 'bg-blue-100 text-blue-700';
      if (s === 'FMP_NEWS') return 'bg-emerald-100 text-emerald-700';
      if (s === 'FMP_PRESS_RELEASE') return 'bg-teal-100 text-teal-700';
      if (s === 'LAYOFFS_FYI') return 'bg-rose-100 text-rose-700';
      return 'bg-gray-100 text-gray-700';
    };
    const relevanceColor = (r) => {
      if (r === 'CRITICAL') return 'bg-red-100 text-red-700 border-red-200';
      if (r === 'SECONDARY') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      return 'bg-gray-50 text-gray-500 border-gray-200';
    };
    const relevanceLabel = (r) => {
      if (r === 'CRITICAL') return 'CRITICAL';
      if (r === 'SECONDARY') return 'SECONDARY';
      return '';
    };
    const categoryLabel = (c) => (c || '').replace(/_/g, ' ');

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-11 gap-3">
          {signalsSummaryLoading ? (
            <div className="col-span-11 text-center py-4 text-gray-500">Loading summary...</div>
          ) : signalsSummary ? (
            <>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-orange-600">{signalsSummary.warn_total}</div>
                <div className="text-xs text-gray-500 mt-1">WARN</div>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{signalsSummary.sec_8k_total}</div>
                <div className="text-xs text-gray-500 mt-1">SEC 8-K</div>
              </div>
              <div className="bg-white rounded-lg border-2 border-red-200 p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{signalsSummary.critical_count || 0}</div>
                <div className="text-xs text-red-500 mt-1">Critical</div>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-yellow-600">{signalsSummary.secondary_count || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Secondary</div>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-indigo-600">{signalsSummary.last_7_days}</div>
                <div className="text-xs text-gray-500 mt-1">Last 7d</div>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-purple-600">{signalsSummary.last_30_days}</div>
                <div className="text-xs text-gray-500 mt-1">Last 30d</div>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-gray-700">{signalsSummary.companies_affected}</div>
                <div className="text-xs text-gray-500 mt-1">Companies</div>
              </div>
              <div className="bg-white rounded-lg border p-3 text-center">
                <div className="text-2xl font-bold text-amber-600">{signalsSummary.unresolved_count}</div>
                <div className="text-xs text-gray-500 mt-1">Unresolved</div>
              </div>
              <div className="bg-white rounded-lg border border-emerald-200 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-600">{signalsSummary.fmp_news_total || 0}</div>
                <div className="text-xs text-gray-500 mt-1">FMP News</div>
              </div>
              <div className="bg-white rounded-lg border border-teal-200 p-3 text-center">
                <div className="text-2xl font-bold text-teal-600">{signalsSummary.fmp_press_release_total || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Press Rel.</div>
              </div>
              <div className="bg-white rounded-lg border border-rose-200 p-3 text-center">
                <div className="text-2xl font-bold text-rose-600">{signalsSummary.layoffs_fyi_total || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Layoffs.fyi</div>
              </div>
            </>
          ) : null}
        </div>

        {/* Scan Controls */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-sm font-semibold text-gray-700">Manual Scan</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-gray-500">SEC scope:</label>
              <select value={secScope} onChange={(e) => setSecScope(e.target.value)}
                className="px-2 py-1 text-xs border rounded" disabled={scanRunning}>
                <option value="tracked">S&P 500 + Watchlist</option>
                <option value="sp500">S&P 500 only</option>
                <option value="watchlist">Watchlist only</option>
                <option value="all">All companies</option>
              </select>
              <button onClick={() => handleManualScan('warn')} disabled={scanRunning}
                className="px-3 py-1.5 text-xs font-medium rounded bg-orange-100 text-orange-700 hover:bg-orange-200 disabled:opacity-50">
                {scanRunning ? 'Scanning...' : 'Scan WARN'}
              </button>
              <button onClick={() => handleManualScan('sec_8k')} disabled={scanRunning}
                className="px-3 py-1.5 text-xs font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-50">
                {scanRunning ? 'Scanning...' : 'Scan SEC 8-K'}
              </button>
              <button onClick={() => handleManualScan('fmp_news')} disabled={scanRunning}
                className="px-3 py-1.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-50">
                {scanRunning ? 'Scanning...' : 'Scan FMP News'}
              </button>
              <button onClick={() => handleManualScan('layoffs_fyi')} disabled={scanRunning}
                className="px-3 py-1.5 text-xs font-medium rounded bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-50">
                {scanRunning ? 'Scanning...' : 'Scan Layoffs.fyi'}
              </button>
              <button onClick={() => handleManualScan('all')} disabled={scanRunning}
                className="px-3 py-1.5 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                {scanRunning ? 'Scanning...' : 'Scan All'}
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {scanRunning && scanProgress && scanProgress.running && scanProgress.total > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>{scanProgress.phase}: {scanProgress.current_ticker}</span>
                <span>{scanProgress.current} / {scanProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((scanProgress.current / scanProgress.total) * 100)}%` }} />
              </div>
            </div>
          )}
          {scanRunning && (!scanProgress || !scanProgress.running || !scanProgress.total) && (
            <div className="mt-3 text-xs text-gray-500 animate-pulse">Scanning...</div>
          )}

          {/* Results */}
          {scanResults && (
            <div className="mt-3 p-3 bg-gray-50 rounded text-xs">
              {scanResults.warn && (
                <div className="mb-1">
                  <span className="font-medium text-orange-700">WARN:</span>{' '}
                  {scanResults.warn.status === 'disabled' ? 'Pipeline disabled' :
                   scanResults.warn.status === 'error' ? `Error: ${scanResults.warn.message}` :
                   `${scanResults.warn.matched || 0} matched, ${scanResults.warn.unresolved || 0} unresolved, ${scanResults.warn.duplicates || 0} duplicates`}
                </div>
              )}
              {scanResults.sec_8k && (
                <div>
                  <span className="font-medium text-blue-700">SEC 8-K:</span>{' '}
                  {scanResults.sec_8k.status === 'disabled' ? 'Pipeline disabled' :
                   scanResults.sec_8k.status === 'error' ? `Error: ${scanResults.sec_8k.message}` :
                   `${scanResults.sec_8k.total_signals_created || 0} signals, ${scanResults.sec_8k.total_duplicates || 0} duplicates, ${scanResults.sec_8k.companies_scanned || 0} companies scanned`}
                </div>
              )}
              {scanResults.fmp_news && (
                <div>
                  <span className="font-medium text-emerald-700">FMP News:</span>{' '}
                  {scanResults.fmp_news.status === 'error' ? `Error: ${scanResults.fmp_news.message}` :
                   scanResults.fmp_news.message || 'Scan started'}
                </div>
              )}
              {scanResults.layoffs_fyi && (
                <div>
                  <span className="font-medium text-rose-700">Layoffs.fyi:</span>{' '}
                  {scanResults.layoffs_fyi.status === 'disabled' ? 'Pipeline disabled' :
                   scanResults.layoffs_fyi.status === 'error' ? `Error: ${scanResults.layoffs_fyi.message}` :
                   `${scanResults.layoffs_fyi.matched || 0} matched, ${scanResults.layoffs_fyi.unresolved || 0} unresolved, ${scanResults.layoffs_fyi.duplicates || 0} duplicates, ${scanResults.layoffs_fyi.skipped_international || 0} intl skipped`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Layoffs.fyi CSV Upload */}
        <div className="bg-white rounded-lg border border-rose-200 p-4">
          <h3 className="text-sm font-semibold text-rose-700 mb-2">Layoffs.fyi CSV Backfill</h3>
          <p className="text-xs text-gray-500 mb-3">
            Upload a Layoffs.fyi CSV (e.g., from Kaggle) to backfill historical layoff data. Records are deduplicated automatically.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvFileSelect}
              className="text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100"
            />
            {csvFile && csvPreviewCount !== null && (
              <span className="text-xs text-gray-500">{csvPreviewCount.toLocaleString()} rows</span>
            )}
            <button
              onClick={handleCsvUpload}
              disabled={!csvFile || csvUploading}
              className="px-3 py-1.5 text-xs font-medium rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            >
              {csvUploading ? 'Uploading...' : 'Upload & Ingest'}
            </button>
          </div>

          {csvUploading && (
            <div className="mt-3 text-xs text-rose-600 animate-pulse">
              Processing records in batches of 500... This may take a minute for large files.
            </div>
          )}

          {csvResults && (
            <div className={`mt-3 p-3 rounded text-xs ${csvResults.status === 'error' ? 'bg-red-50' : 'bg-rose-50'}`}>
              {csvResults.status === 'error' ? (
                <span className="text-red-700">Error: {csvResults.message}</span>
              ) : (
                <div className="space-y-1">
                  <div className="font-medium text-rose-800">
                    Backfill complete — {csvResults.total?.toLocaleString()} records processed
                  </div>
                  <div className="text-gray-700">
                    <span className="text-green-700 font-medium">{csvResults.matched?.toLocaleString()} matched</span>
                    {' · '}
                    <span className="text-amber-700">{csvResults.unresolved?.toLocaleString()} unresolved</span>
                    {' · '}
                    <span className="text-gray-500">{csvResults.duplicates?.toLocaleString()} duplicates</span>
                    {' · '}
                    <span className="text-red-500">{csvResults.errors?.toLocaleString()} errors</span>
                    {csvResults.skipped_international > 0 && (
                      <span className="text-gray-400"> · {csvResults.skipped_international?.toLocaleString()} intl skipped</span>
                    )}
                  </div>
                  {csvResults.unresolved_companies?.length > 0 && (
                    <div className="mt-1 text-gray-500">
                      <span className="font-medium">Unresolved: </span>
                      {csvResults.unresolved_companies.slice(0, 20).join(', ')}
                      {csvResults.unresolved_companies.length > 20 && ` ... and ${csvResults.unresolved_companies.length - 20} more`}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Unresolved Queue */}
        {unresolvedList.length > 0 && (
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-amber-700 mb-3">
              Unresolved Companies ({unresolvedList.length})
            </h3>
            <div className="space-y-2">
              {unresolvedList.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-2 bg-amber-50 rounded text-sm">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{item.company_name}</span>
                    {item.state && <span className="text-gray-500 ml-2">({item.state})</span>}
                    {item.employees_affected && <span className="text-gray-500 ml-2">{item.employees_affected.toLocaleString()} employees</span>}
                    {item.notice_date && <span className="text-gray-400 ml-2">{item.notice_date}</span>}
                    {item.suggested_ticker && (
                      <span className="ml-2 text-xs text-indigo-600">
                        Suggested: {item.suggested_ticker} ({Math.round((item.suggestion_score || 0) * 100)}%)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      placeholder={item.suggested_ticker || 'TICKER'}
                      className="w-20 px-2 py-1 text-xs border rounded uppercase"
                      value={resolveTickerInputs[item.id] ?? (item.suggested_ticker || '')}
                      onChange={(e) => setResolveTickerInputs(prev => ({ ...prev, [item.id]: e.target.value }))}
                    />
                    <button
                      onClick={() => handleResolveUnresolved(item.id)}
                      disabled={resolvingId === item.id}
                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {resolvingId === item.id ? '...' : 'Resolve'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <select value={signalsSourceFilter} onChange={(e) => setSignalsSourceFilter(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded">
              <option value="">All Sources</option>
              <option value="WARN">WARN Act</option>
              <option value="SEC_8K">SEC 8-K</option>
              <option value="FMP_NEWS">FMP News</option>
              <option value="FMP_PRESS_RELEASE">FMP Press Releases</option>
              <option value="LAYOFFS_FYI">Layoffs.fyi</option>
            </select>
            <select value={signalsRelevanceFilter} onChange={(e) => setSignalsRelevanceFilter(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded">
              <option value="">All Relevance</option>
              <option value="CRITICAL">Critical Only</option>
              <option value="SECONDARY">Secondary Only</option>
            </select>
            <select value={signalsCategoryFilter} onChange={(e) => setSignalsCategoryFilter(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded">
              <option value="">All Categories</option>
              {(signalsSummary?.categories || []).map(cat => (
                <option key={cat} value={cat}>{(cat || '').replace(/_/g, ' ')}</option>
              ))}
            </select>
            <select value={signalsDirectionFilter} onChange={(e) => setSignalsDirectionFilter(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded">
              <option value="">All Directions</option>
              <option value="NEGATIVE">Negative</option>
              <option value="POSITIVE">Positive</option>
              <option value="NEUTRAL">Neutral</option>
            </select>
            <input type="text" placeholder="Ticker..." value={signalsTickerFilter}
              onChange={(e) => setSignalsTickerFilter(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded w-24 uppercase" />
            <select value={signalsDaysBack} onChange={(e) => setSignalsDaysBack(Number(e.target.value))}
              className="px-2 py-1.5 text-xs border rounded">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={365}>1 year</option>
            </select>
            <button onClick={fetchSignalsList}
              className="px-3 py-1.5 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700">
              Apply
            </button>
            {(signalsSourceFilter || signalsRelevanceFilter || signalsCategoryFilter || signalsDirectionFilter || signalsTickerFilter) && (
              <button onClick={() => {
                setSignalsSourceFilter(''); setSignalsRelevanceFilter(''); setSignalsCategoryFilter('');
                setSignalsDirectionFilter(''); setSignalsTickerFilter('');
              }}
                className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Signals Timeline */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Signals Timeline ({signalsList.length})
          </h3>
          {signalsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading signals...</div>
          ) : signalsList.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No signals found. Run a scan or adjust filters.
            </div>
          ) : (
            <div className="space-y-2">
              {signalsList.map(signal => {
                const rel = signal.structured_data?.relevance;
                const borderClass = rel === 'CRITICAL' ? 'border-l-4 border-l-red-500' :
                                    rel === 'SECONDARY' ? 'border-l-4 border-l-yellow-400' : '';
                return (
                  <div key={signal.id} className={`border rounded p-3 hover:bg-gray-50 ${borderClass}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${sourceColor(signal.source_type)}`}>
                            {signal.source_type === 'WARN' ? 'WARN' : signal.source_type === 'SEC_8K' ? '8-K' :
                             signal.source_type === 'FMP_NEWS' ? 'News' : signal.source_type === 'FMP_PRESS_RELEASE' ? 'Press Rel.' :
                             signal.source_type === 'LAYOFFS_FYI' ? 'Layoffs.fyi' : signal.source_type}
                          </span>
                          {rel && rel !== 'NOISE' && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${relevanceColor(rel)}`}>
                              {relevanceLabel(rel)}
                            </span>
                          )}
                          <span className="text-sm font-semibold text-gray-900">{signal.ticker}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs ${directionColor(signal.signal_direction)}`}>
                            {(signal.signal_direction || '').replace(/_/g, ' ')}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                            {categoryLabel(signal.signal_category)}
                          </span>
                          <span className="text-xs text-gray-400">{signal.signal_date}</span>
                        </div>
                        <div className="text-sm text-gray-700 mt-1">{signal.headline}</div>
                        {signal.detail_text && (
                          <div className="text-xs text-gray-500 mt-0.5">{signal.detail_text}</div>
                        )}
                        {/* WARN-specific: employees affected */}
                        {signal.structured_data?.employees_affected && (
                          <div className="text-xs text-red-600 mt-0.5 font-medium">
                            {signal.structured_data.employees_affected.toLocaleString()} employees affected
                            {signal.structured_data.facility_location && ` — ${signal.structured_data.facility_location}`}
                          </div>
                        )}
                        {/* FMP News: show source site */}
                        {(signal.source_type === 'FMP_NEWS' || signal.source_type === 'FMP_PRESS_RELEASE') && signal.structured_data?.source_site && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            via {signal.structured_data.source_site}
                            {signal.structured_data.claude_summary && signal.structured_data.claude_summary !== signal.detail_text && (
                              <span className="ml-2 text-gray-500">{signal.structured_data.claude_summary}</span>
                            )}
                          </div>
                        )}
                        {/* Layoffs.fyi: laid off count, percentage, corroboration */}
                        {signal.source_type === 'LAYOFFS_FYI' && (
                          <div className="text-xs mt-0.5">
                            {signal.structured_data?.laid_off_count && (
                              <span className="text-red-600 font-medium">
                                {signal.structured_data.laid_off_count.toLocaleString()} laid off
                              </span>
                            )}
                            {signal.structured_data?.percentage && (
                              <span className="text-red-500 ml-1">
                                ({signal.structured_data.percentage}% of workforce)
                              </span>
                            )}
                            {signal.structured_data?.country && signal.structured_data?.is_international && (
                              <span className="text-gray-500 ml-2">[{signal.structured_data.country}]</span>
                            )}
                            {signal.structured_data?.warn_corroborated && (
                              <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">
                                WARN Corroborated
                              </span>
                            )}
                            {signal.confidence === 'UNVERIFIED' && !signal.structured_data?.warn_corroborated && (
                              <span className="ml-2 px-1.5 py-0.5 bg-yellow-50 text-yellow-600 rounded text-[10px]">
                                Unverified
                              </span>
                            )}
                          </div>
                        )}
                        {/* SEC 8-K: show individual items if multiple */}
                        {signal.source_type === 'SEC_8K' && signal.structured_data?.item_details?.length > 1 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {signal.structured_data.item_details
                              .filter(i => i.relevance !== 'NOISE')
                              .map((item, idx) => (
                              <span key={idx} className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                item.relevance === 'CRITICAL' ? 'bg-red-50 text-red-600 border-red-200' :
                                'bg-yellow-50 text-yellow-700 border-yellow-200'
                              }`}>
                                Item {item.item}: {item.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      {signal.source_url && (
                        <a href={signal.source_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-indigo-600 hover:underline whitespace-nowrap shrink-0">
                          {signal.source_type === 'FMP_NEWS' || signal.source_type === 'FMP_PRESS_RELEASE' ? 'View Article' :
                           signal.source_type === 'LAYOFFS_FYI' ? 'View Source' : 'View Filing'}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // =============================================
  // Render: Alerts / Subscribers Tab
  // =============================================

  const renderAlerts = () => {
    // Get current admin ID for ownership checks
    let currentAdminId = null;
    try {
      const token = getValidToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        currentAdminId = payload.admin_user_id || payload.user_id;
      }
    } catch (e) {}

    const mySubscriptions = subscribers.filter(s => s.admin_user_id === currentAdminId);
    const otherSubscriptions = subscribers.filter(s => s.admin_user_id !== currentAdminId);

    return (
      <div className="space-y-6">
        {/* My Alerts */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">My Alert Subscriptions</h3>
            <button onClick={() => { setSubForm(defaultSubForm); setEditingSubId(null); setShowSubForm(true); }}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs hover:bg-emerald-700">
              + New Alert
            </button>
          </div>

          {subscribersLoading ? <LoadingSpinner text="Loading..." /> : (
            mySubscriptions.length > 0 ? (
              <div className="space-y-3">
                {mySubscriptions.map(sub => (
                  <div key={sub.id} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm text-emerald-900">{sub.label || 'Default'}</span>
                      <div className="flex gap-2">
                        <button onClick={() => editSubscription(sub)}
                          className="text-xs px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 text-gray-600">
                          Edit
                        </button>
                        <button onClick={() => deleteSubscription(sub.id)}
                          className="text-xs px-2 py-1 bg-white border border-red-300 rounded hover:bg-red-50 text-red-600">
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {sub.watch_all_sp500 && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">S&P 500</span>}
                      {sub.watch_alumni_companies && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Alumni Cos</span>}
                      {sub.watch_tickers && (Array.isArray(sub.watch_tickers) ? sub.watch_tickers : []).map(t => (
                        <span key={t} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-mono">{t}</span>
                      ))}
                      {sub.watch_sectors && (Array.isArray(sub.watch_sectors) ? sub.watch_sectors : []).map(s => (
                        <span key={s} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">{s}</span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                      <span>Tier: {sub.min_alert_tier}</span>
                      <span>Freq: {sub.digest_frequency}</span>
                      {sub.delivery_email && <span className="text-blue-600">Email</span>}
                      {sub.delivery_sms && <span className="text-purple-600">SMS</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No alerts configured. Click "+ New Alert" to create one.</p>
            )
          )}
        </div>

        {/* Add/Edit Form (shown when toggled) */}
        {showSubForm && (
          <div className="bg-white rounded-lg shadow-sm border border-emerald-300 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700">
                {editingSubId ? 'Edit Alert' : 'New Alert'}
              </h3>
              <button onClick={() => { setShowSubForm(false); setEditingSubId(null); setSubForm(defaultSubForm); }}
                className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Alert Label</label>
                <input type="text" value={subForm.label}
                  onChange={(e) => setSubForm(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="e.g. SNOW Alerts, Tech Sector Watch, S&P 500 CRITICAL"
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={subForm.watch_all_sp500}
                  onChange={(e) => setSubForm(prev => ({ ...prev, watch_all_sp500: e.target.checked }))}
                  className="rounded border-gray-300 text-emerald-600" />
                <span className="text-sm text-gray-700">Watch all S&P 500</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={subForm.watch_alumni_companies}
                  onChange={(e) => setSubForm(prev => ({ ...prev, watch_alumni_companies: e.target.checked }))}
                  className="rounded border-gray-300 text-emerald-600" />
                <span className="text-sm text-gray-700">Watch alumni companies</span>
              </label>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Specific Tickers</label>
                <input type="text" value={subForm.watch_tickers}
                  onChange={(e) => setSubForm(prev => ({ ...prev, watch_tickers: e.target.value }))}
                  placeholder="AAPL, MSFT, GOOGL" className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sectors</label>
                <input type="text" value={subForm.watch_sectors}
                  onChange={(e) => setSubForm(prev => ({ ...prev, watch_sectors: e.target.value }))}
                  placeholder="Technology, Healthcare" className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Alert Tier</label>
                <select value={subForm.min_alert_tier}
                  onChange={(e) => setSubForm(prev => ({ ...prev, min_alert_tier: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm">
                  <option value="CRITICAL">CRITICAL only</option>
                  <option value="HIGH">HIGH and above</option>
                  <option value="STANDARD">STANDARD and above</option>
                  <option value="LOW">ALL (including LOW)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Digest Frequency</label>
                <select value={subForm.digest_frequency}
                  onChange={(e) => setSubForm(prev => ({ ...prev, digest_frequency: e.target.value }))}
                  className="w-full px-3 py-2 border rounded text-sm">
                  <option value="immediate">Immediate</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={subForm.delivery_email}
                    onChange={(e) => setSubForm(prev => ({ ...prev, delivery_email: e.target.checked }))}
                    className="rounded border-gray-300 text-emerald-600" />
                  <span className="text-sm text-gray-700">Email</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={subForm.delivery_sms}
                    onChange={(e) => setSubForm(prev => ({ ...prev, delivery_sms: e.target.checked }))}
                    className="rounded border-gray-300 text-emerald-600" />
                  <span className="text-sm text-gray-700">SMS</span>
                </label>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={saveSubscription} disabled={subSaving}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50">
                {subSaving ? 'Saving...' : (editingSubId ? 'Update Alert' : 'Create Alert')}
              </button>
              <button onClick={() => { setShowSubForm(false); setEditingSubId(null); setSubForm(defaultSubForm); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Other Subscribers (read-only) */}
        {otherSubscriptions.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Other Staff Alerts</h3>
            <div className="space-y-2">
              {otherSubscriptions.map(sub => (
                <div key={sub.id} className="p-3 bg-gray-50 rounded-lg border text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{sub.admin_name || sub.admin_email}</span>
                    <span className="text-xs text-gray-500">{sub.label || 'Default'}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs">
                    {sub.watch_all_sp500 && <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded">S&P 500</span>}
                    {sub.watch_tickers && (Array.isArray(sub.watch_tickers) ? sub.watch_tickers : []).map(t => (
                      <span key={t} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded font-mono">{t}</span>
                    ))}
                    <span className="text-gray-400">{sub.min_alert_tier} / {sub.digest_frequency}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Alerts */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Recent Alerts</h3>
            <button onClick={fetchRecentAlerts} disabled={alertsLoading}
              className="px-3 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 disabled:opacity-50">
              {alertsLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          {alertsLoading ? <LoadingSpinner text="Loading alerts..." /> : (
            recentAlerts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500 uppercase">
                      <th className="px-3 py-2">Tier</th>
                      <th className="px-3 py-2">Ticker</th>
                      <th className="px-3 py-2">Period</th>
                      <th className="px-3 py-2">Anchor</th>
                      <th className="px-3 py-2">Verdict</th>
                      <th className="px-3 py-2">Score</th>
                      <th className="px-3 py-2">Channel</th>
                      <th className="px-3 py-2">Subscriber</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentAlerts.map((alert, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${TIER_COLORS[alert.alert_tier] || 'bg-gray-400 text-white'}`}>
                            {alert.alert_tier}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono font-bold text-emerald-700">{alert.ticker}</td>
                        <td className="px-3 py-2 text-gray-500">{alert.fiscal_period}</td>
                        <td className="px-3 py-2">{alert.anchor_name}</td>
                        <td className="px-3 py-2">{alert.verdict}</td>
                        <td className="px-3 py-2 font-semibold">{alert.specificity_score}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${alert.delivery_channel === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {alert.delivery_channel}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600">{alert.subscriber_name}</td>
                        <td className="px-3 py-2">
                          {alert.delivered ? (
                            <span className="text-green-600 font-medium">Sent</span>
                          ) : (
                            <span className="text-amber-600 font-medium">Pending</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-400">
                          {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No alerts generated yet. Alerts are created when chunks are scored and match subscriber preferences.</p>
            )
          )}
        </div>
      </div>
    );
  };

  // =============================================
  // Render: Pipeline Tab
  // =============================================

  const renderPipeline = () => {
    return (
      <div className="space-y-6">
        {/* How to Use button */}
        <div className="flex justify-end">
          <button onClick={() => setShowPipelineHelp(true)}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-800 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            How to Use SWEE
          </button>
        </div>

        {/* Pipeline Instructions Modal */}
        {showPipelineHelp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowPipelineHelp(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-slate-700 text-white px-6 py-4 rounded-t-xl flex items-center justify-between">
                <h2 className="text-lg font-bold">SWEE Pipeline — Operating Guide</h2>
                <button onClick={() => setShowPipelineHelp(false)} className="text-white hover:text-gray-300 text-xl font-bold">&times;</button>
              </div>
              <div className="px-6 py-5 space-y-6 text-sm">
                {/* Overview */}
                <div>
                  <p className="text-gray-600 leading-relaxed">
                    The Strategic Workforce Evolution Engine (SWEE) analyzes public company earnings transcripts to detect workforce strategy signals — AI adoption, re-skilling initiatives, geographic shifts, and more. The pipeline runs in four stages: <strong>Ingest → Chunk → Embed → Score</strong>.
                  </p>
                </div>

                {/* First-Time Setup */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    First-Time Setup
                  </h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p><strong>a) Populate Company List</strong> — Click <span className="font-mono text-xs bg-blue-100 px-1.5 py-0.5 rounded">Populate S&P 500</span> and/or <span className="font-mono text-xs bg-indigo-100 px-1.5 py-0.5 rounded">Populate Alumni Employers</span> to seed the company list. These are idempotent — safe to run multiple times.</p>
                    <p><strong>b) Initialize Centroids</strong> — Scroll down and click <span className="font-mono text-xs bg-purple-100 px-1.5 py-0.5 rounded">Initialize Centroids</span>. Centroids are the embedding anchors that detect strategy signals (e.g., "Pipeline Insurance," "Skills-First Pivot"). You should see 6 anchors listed after initialization.</p>
                  </div>
                </div>

                {/* Running a Backfill */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    Running a Backfill (Single Company)
                  </h3>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p><strong>a)</strong> Enter one or more ticker symbols in the <strong>Backfill Transcripts</strong> section (e.g., <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">IBM, ACN</span>).</p>
                    <p><strong>b)</strong> Set the fiscal quarter range (e.g., 2024 Q1 to 2025 Q4).</p>
                    <p><strong>c)</strong> Click <span className="font-mono text-xs bg-emerald-100 px-1.5 py-0.5 rounded">Start Backfill</span>. The system will fetch transcripts from the SEC EDGAR API, then automatically chunk, embed, and score them in the background.</p>
                    <p><strong>d)</strong> Click <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Refresh Status</span> to see progress. The Pipeline Status cards show how many transcripts have been ingested, chunked, and scored.</p>
                  </div>
                </div>

                {/* Background Backfill */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    Background Backfill (Bulk — All Companies)
                  </h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p>For processing all tracked companies at once (S&P 500, watchlist, or both), use the <strong>Background Backfill</strong> section.</p>
                    <p><strong>a)</strong> Select target (All, S&P 500 Only, or Watchlist Only) and set the date range.</p>
                    <p><strong>b)</strong> Click <span className="font-mono text-xs bg-amber-100 px-1.5 py-0.5 rounded">Start Background Backfill</span>. This runs entirely in the background — you can navigate away and come back later.</p>
                    <p><strong>c)</strong> Progress is tracked via the job cards with progress bars. Click <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Refresh Jobs</span> to update.</p>
                    <p className="text-amber-800 font-medium">Note: A full S&P 500 backfill can take several hours depending on the date range.</p>
                  </div>
                </div>

                {/* Run Pipeline Now */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                    Run Pipeline Now
                  </h3>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p>If chunks remain unprocessed (e.g., after a partial run or an error), click <span className="font-mono text-xs bg-purple-100 px-1.5 py-0.5 rounded">Run Pipeline Now</span> in the Pipeline Status section.</p>
                    <p>This picks up any unchunked transcripts, unembedded chunks, or unscored chunks and processes them in the background. It runs until there is nothing left to process.</p>
                    <p>The button will pulse while processing. Status auto-refreshes until complete.</p>
                  </div>
                </div>

                {/* Setting Up Alerts */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-rose-600 text-white rounded-full flex items-center justify-center text-xs font-bold">5</span>
                    Setting Up Alerts
                  </h3>
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p>Switch to the <strong>Alerts</strong> tab to configure alert subscriptions.</p>
                    <p><strong>a)</strong> Click <span className="font-mono text-xs bg-rose-100 px-1.5 py-0.5 rounded">+ New Alert</span> and give it a label (e.g., "My S&P 500 Alerts").</p>
                    <p><strong>b)</strong> Set watch filters — watch all S&P 500, specific tickers, sectors, or alumni employer companies.</p>
                    <p><strong>c)</strong> Set a minimum alert tier (CRITICAL, HIGH, STANDARD, or LOW).</p>
                    <p><strong>d)</strong> Choose delivery: Email, SMS, or both. Digest runs daily at 9 PM ET.</p>
                    <p><strong>e)</strong> You can create multiple alert subscriptions with different filters and thresholds.</p>
                  </div>
                </div>

                {/* Understanding Scores */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-gray-700 text-white rounded-full flex items-center justify-center text-xs font-bold">6</span>
                    Understanding the Scores
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p><strong>Specificity Score (1–10)</strong> — How specific and actionable the workforce signal is. A score of 7+ indicates a concrete strategy shift with named programs, headcounts, or timelines.</p>
                    <p><strong>Verdict</strong> — The model's assessment of the signal:</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><span className="font-medium text-green-700">Genuine Pivot</span> — Real workforce transformation underway</li>
                      <li><span className="font-medium text-yellow-700">Developing Strategy</span> — Concrete plans but early stage</li>
                      <li><span className="font-medium text-red-700">AI-Washing</span> — Buzzwords without substance</li>
                      <li><span className="font-medium text-red-800">Strategy Reversal</span> — Company reversing a previous strategy</li>
                      <li><span className="text-gray-600">Cosmetic / Insufficient Data</span> — Not enough signal</li>
                    </ul>
                    <p><strong>Alert Tiers</strong> — Based on specificity score: CRITICAL (8–10), HIGH (6–7), STANDARD (4–5), LOW (1–3).</p>
                  </div>
                </div>

                {/* Automatic Daily Updates */}
                <div>
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 bg-cyan-600 text-white rounded-full flex items-center justify-center text-xs font-bold">7</span>
                    Automatic Daily Updates (Scheduler)
                  </h3>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-2 text-gray-700">
                    <p>Once the initial backfill is done, <strong>SWEE updates itself automatically</strong> via two scheduled jobs:</p>
                    <div className="ml-2 space-y-2">
                      <p><strong>Daily Scan (8 PM ET)</strong> — Checks the earnings calendar each day. For every tracked company (S&P 500 or watchlist) that filed a new transcript, it auto-ingests and runs the full pipeline (chunk → embed → score). No manual action needed.</p>
                      <p><strong>Alert Digest (9 PM ET)</strong> — One hour after the scan, sends email and/or SMS digests to all subscribers with matching unsent alerts from the last 48 hours.</p>
                    </div>
                    <p className="text-cyan-800 font-medium mt-2">Prerequisite: The SWEE feature must be enabled in Admin Settings (<span className="font-mono text-xs bg-cyan-100 px-1.5 py-0.5 rounded">swee_enabled = true</span>). Both jobs check this setting and skip if disabled.</p>
                    <p>New companies added to the S&P 500 list or alumni employer watchlist are automatically picked up by the next daily scan — no need to re-run a backfill for future quarters.</p>
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-700 mb-2">Tips</h4>
                  <ul className="list-disc ml-5 space-y-1 text-gray-600">
                    <li>Start with a small backfill (1–2 companies) to verify everything works before running a bulk backfill.</li>
                    <li>The Pipeline Status cards show real-time counts — if "Scored" equals "Total Chunks," all processing is complete.</li>
                    <li>Use the <strong>Dashboard</strong> tab to see top-scoring companies and the <strong>Search</strong> tab for cross-company queries.</li>
                    <li>After a backfill, check the <strong>Trajectory</strong> tab to see how a company's strategy signals evolve across quarters.</li>
                    <li>Centroids only need to be initialized once. Re-running updates them to the latest version if the fixture file changes.</li>
                    <li>The daily scan only picks up transcripts for companies already in your tracked list. To add new companies, use "Populate S&P 500" or "Populate Alumni Employers," then backfill historical data if needed.</li>
                  </ul>
                </div>
              </div>

              <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex justify-end">
                <button onClick={() => setShowPipelineHelp(false)}
                  className="px-5 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-800">
                  Got It
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Backfill Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Backfill Transcripts</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Tickers (comma-separated)</label>
              <input type="text" value={backfillTickers}
                onChange={(e) => setBackfillTickers(e.target.value)}
                placeholder="IBM, ACN, NOW, EPAM, GIB, NEE"
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Year</label>
              <input type="number" value={backfillStartYear}
                onChange={(e) => setBackfillStartYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Quarter</label>
              <select value={backfillStartQ} onChange={(e) => setBackfillStartQ(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm">
                <option value={1}>Q1</option><option value={2}>Q2</option>
                <option value={3}>Q3</option><option value={4}>Q4</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Year</label>
              <input type="number" value={backfillEndYear}
                onChange={(e) => setBackfillEndYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Quarter</label>
              <select value={backfillEndQ} onChange={(e) => setBackfillEndQ(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm">
                <option value={1}>Q1</option><option value={2}>Q2</option>
                <option value={3}>Q3</option><option value={4}>Q4</option>
              </select>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={startBackfill} disabled={backfillRunning}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 disabled:opacity-50">
              {backfillRunning ? 'Starting...' : 'Start Backfill'}
            </button>
            <button onClick={fetchBackfillStatus} disabled={backfillStatusLoading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              Refresh Status
            </button>
          </div>
        </div>

        {/* Populate Companies */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Populate Company List</h3>
          <p className="text-xs text-gray-500 mb-4">
            Seed the SWEE company list from S&P 500 index and/or alumni employer companies. These are idempotent — safe to run multiple times.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={populateSp500} disabled={populatingSp500 || populatingAlumni}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {populatingSp500 && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
              {populatingSp500 ? 'Loading S&P 500...' : 'Populate S&P 500'}
            </button>
            <button onClick={populateAlumniEmployers} disabled={populatingAlumni || populatingSp500}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
              {populatingAlumni && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
              {populatingAlumni ? 'Finding Alumni Employers...' : 'Populate Alumni Employers'}
            </button>
          </div>
          {(populatingSp500 || populatingAlumni) && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 animate-pulse">
              {populatingSp500 ? 'Fetching S&P 500 companies from EarningsCall API and inserting into database... This may take 10-30 seconds.' : 'Scanning alumni employers and matching to public companies... This may take 10-30 seconds.'}
            </div>
          )}
          {populateResult && !populatingSp500 && !populatingAlumni && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 font-medium">
              {populateResult.type === 'sp500'
                ? `S&P 500: ${populateResult.added} added, ${populateResult.updated} updated — ${populateResult.total_sp500} total S&P 500 companies tracked.`
                : `Alumni Employers: ${populateResult.added} added, ${populateResult.updated} updated — ${populateResult.alumni_employer_matches} matches found, ${populateResult.total_watchlist} total watchlist companies.`
              }
            </div>
          )}
        </div>

        {/* Background Backfill */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Background Backfill</h3>
          <p className="text-xs text-gray-500 mb-4">
            Backfill transcripts for all tracked companies in the background. This can take hours for 500+ companies — progress is tracked below.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Target</label>
              <select value={bgBackfillTarget} onChange={(e) => setBgBackfillTarget(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm">
                <option value="all">All Tracked Companies</option>
                <option value="sp500">S&P 500 Only</option>
                <option value="watchlist">Watchlist Only</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start</label>
              <div className="flex gap-2">
                <input type="number" value={bgBackfillStartYear}
                  onChange={(e) => setBgBackfillStartYear(parseInt(e.target.value))}
                  className="w-20 px-2 py-2 border rounded text-sm" />
                <select value={bgBackfillStartQ} onChange={(e) => setBgBackfillStartQ(parseInt(e.target.value))}
                  className="px-2 py-2 border rounded text-sm">
                  <option value={1}>Q1</option><option value={2}>Q2</option>
                  <option value={3}>Q3</option><option value={4}>Q4</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End</label>
              <div className="flex gap-2">
                <input type="number" value={bgBackfillEndYear}
                  onChange={(e) => setBgBackfillEndYear(parseInt(e.target.value))}
                  className="w-20 px-2 py-2 border rounded text-sm" />
                <select value={bgBackfillEndQ} onChange={(e) => setBgBackfillEndQ(parseInt(e.target.value))}
                  className="px-2 py-2 border rounded text-sm">
                  <option value={1}>Q1</option><option value={2}>Q2</option>
                  <option value={3}>Q3</option><option value={4}>Q4</option>
                </select>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={startBackgroundBackfill} disabled={bgBackfillStarting || bgBackfillJobs.some(j => j.status === 'running' || j.status === 'queued')}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50">
              {bgBackfillStarting ? 'Starting...' : bgBackfillJobs.some(j => j.status === 'running') ? 'Job Running...' : 'Start Background Backfill'}
            </button>
            <button onClick={fetchBackfillJobs}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
              Refresh Jobs
            </button>
          </div>

          {/* Background Backfill Jobs */}
          {bgBackfillJobs.length > 0 && (
            <div className="mt-4 space-y-3">
              <h4 className="text-xs font-semibold text-gray-600">Backfill Jobs</h4>
              {bgBackfillJobs.map((job) => (
                <div key={job.id} className={`p-4 rounded-lg border ${
                  job.status === 'running' ? 'bg-amber-50 border-amber-200' :
                  job.status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                  job.status === 'failed' ? 'bg-red-50 border-red-200' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        job.status === 'running' ? 'bg-amber-200 text-amber-800' :
                        job.status === 'completed' ? 'bg-emerald-200 text-emerald-800' :
                        job.status === 'failed' ? 'bg-red-200 text-red-800' :
                        job.status === 'queued' ? 'bg-blue-200 text-blue-800' :
                        'bg-gray-200 text-gray-800'
                      }`}>
                        {job.status.toUpperCase()}
                      </span>
                      <span className="text-xs text-gray-500">Target: {job.target}</span>
                      {job.current_ticker && job.status === 'running' && (
                        <span className="text-xs text-amber-700 font-mono">Processing: {job.current_ticker}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      {job.created_at ? new Date(job.created_at).toLocaleString() : ''}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                    <div className={`h-2.5 rounded-full ${
                      job.status === 'completed' ? 'bg-emerald-500' :
                      job.status === 'failed' ? 'bg-red-500' : 'bg-amber-500'
                    }`} style={{ width: `${job.progress_pct || 0}%` }}></div>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>Completed: {job.completed_companies}/{job.total_companies}</span>
                    {job.failed_companies > 0 && (
                      <span className="text-red-600">Failed: {job.failed_companies}</span>
                    )}
                    <span>{job.progress_pct}%</span>
                    {job.started_by && <span className="text-gray-400">by {job.started_by}</span>}
                  </div>

                  {job.error_message && (
                    <div className="mt-2 text-xs text-red-700 bg-red-100 rounded p-2">{job.error_message}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pipeline Status */}
        {backfillStatus && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700">Pipeline Status</h3>
              <div className="flex gap-2">
                <button onClick={runPipeline} disabled={pipelineRunning}
                  className={`px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50 ${pipelineRunning ? 'bg-purple-500 animate-pulse' : 'bg-purple-600 hover:bg-purple-700'}`}>
                  {pipelineRunning ? 'Processing in Background...' : 'Run Pipeline Now'}
                </button>
                <button onClick={fetchBackfillStatus} disabled={backfillStatusLoading}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">
                  Refresh
                </button>
              </div>
            </div>
            {pipelineResult && (
              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                {pipelineResult.message || `Pipeline: ${pipelineResult.total_chunked || 0} chunked, ${pipelineResult.total_embedded || 0} embedded, ${pipelineResult.total_scored || 0} scored`}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <StatCard label="Total Transcripts" value={backfillStatus.transcripts?.total || 0} color="emerald" />
              <StatCard label="Ingested" value={backfillStatus.transcripts?.ingested || 0} color="blue" />
              <StatCard label="Chunked" value={backfillStatus.transcripts?.chunked || 0} color="purple" />
              <StatCard label="Failed" value={backfillStatus.transcripts?.failed || 0} color="red" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Chunks" value={backfillStatus.chunks?.total || 0} color="emerald" />
              <StatCard label="Embedded" value={backfillStatus.chunks?.embedded || 0} color="blue" />
              <StatCard label="Scored" value={backfillStatus.chunks?.scored || 0} color="purple" />
              <StatCard label="Above Threshold" value={backfillStatus.chunks?.above_threshold || 0} color="cyan" />
            </div>
            {backfillStatus.failed_transcripts && backfillStatus.failed_transcripts.length > 0 && (
              <div className="mt-4">
                <h4 className="text-xs font-semibold text-red-700 mb-2">Failed Transcripts</h4>
                <div className="space-y-1">
                  {backfillStatus.failed_transcripts.map((f, idx) => (
                    <div key={idx} className="text-xs p-2 bg-red-50 rounded border border-red-200">
                      <span className="font-mono font-bold">{f.ticker}</span> {f.fiscal_period}: {f.error_message || 'Unknown error'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Centroid Management */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Centroid Management</h3>
          <p className="text-xs text-gray-500 mb-3">
            Centroids are the embedding anchors that detect strategy and workforce signals. Initialize from fixture files to create/update them.
          </p>

          {/* SWEE Centroids */}
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-purple-700 uppercase tracking-wider mb-3">SWEE Strategy Anchors</h4>
            <div className="flex gap-2 mb-3">
              <button onClick={initializeCentroids} disabled={centroidInitRunning}
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs hover:bg-purple-700 disabled:opacity-50">
                {centroidInitRunning ? 'Initializing...' : 'Initialize SWEE'}
              </button>
              <button onClick={fetchCentroids} disabled={centroidsLoading}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200">
                Refresh
              </button>
            </div>
            {centroidsLoading ? <LoadingSpinner text="Loading..." /> : centroids && centroids.length > 0 ? (
              <div className="space-y-2">
                {centroids.map((c, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg border text-sm flex items-center justify-between">
                    <div>
                      <AnchorBadge anchor={c.anchor_name} />
                      <span className="text-xs text-gray-400 ml-2">v{c.version} — {Array.isArray(c.source_paragraphs) ? c.source_paragraphs.length : (c.paragraph_count || '?')} paragraphs</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-xs">No SWEE centroids initialized.</p>
            )}
          </div>

          {/* WI Centroids */}
          <div className="border-t pt-4">
            <h4 className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-3">Workforce Intelligence Categories</h4>
            <div className="flex gap-2 mb-3">
              <button onClick={initializeWiCentroids} disabled={wiCentroidInitRunning}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs hover:bg-indigo-700 disabled:opacity-50">
                {wiCentroidInitRunning ? 'Initializing...' : 'Initialize WI'}
              </button>
              <button onClick={fetchWiCentroids} disabled={wiCentroidsLoading}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200">
                Refresh
              </button>
            </div>
            {wiCentroidsLoading ? <LoadingSpinner text="Loading..." /> : wiCentroids && wiCentroids.length > 0 ? (
              <div className="space-y-2">
                {wiCentroids.map((c, idx) => {
                  const colors = WI_CATEGORY_COLORS[c.centroid_name] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
                  const paraCount = Array.isArray(c.source_paragraphs) ? c.source_paragraphs.length : '?';
                  return (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border text-sm flex items-center justify-between">
                      <div>
                        <span className={`inline-flex text-xs px-2 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                          {c.centroid_name}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">v{c.version} — {paraCount} paragraphs</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-xs">No WI centroids initialized. Click "Initialize WI" to create them from the fixture file.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // =============================================
  // Render: Reports Tab (Phase 4c)
  // =============================================

  const renderReports = () => {
    const urgencyColor = (u) => ({
      CRITICAL: 'bg-red-100 text-red-800 border-red-300',
      HIGH: 'bg-orange-100 text-orange-800 border-orange-300',
      STANDARD: 'bg-blue-100 text-blue-800 border-blue-300',
      LOW: 'bg-gray-100 text-gray-600 border-gray-300',
    }[u] || 'bg-gray-100 text-gray-600 border-gray-300');

    const sourceIcon = (s) => ({
      swee: '◈', wi: '⊞', warn: '⚠', sec_8k: '📄',
      swee_transcript: '◈', wi_signal: '⊞', warn_notice: '⚠', sec_filing: '📄',
    }[s] || '•');

    const evStats = reportStats?.events || {};
    const rpStats = reportStats?.reports || {};

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Signal Events</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{evStats.total_events ?? '—'}</div>
            <div className="text-xs text-gray-400 mt-1">{evStats.last_24h ?? 0} last 24h · {evStats.last_7d ?? 0} last 7d</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Pending</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{evStats.pending ?? '—'}</div>
            <div className="text-xs text-gray-400 mt-1">{evStats.critical ?? 0} critical · {evStats.high ?? 0} high</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Reports Generated</div>
            <div className="text-2xl font-bold text-emerald-600 mt-1">{rpStats.total_reports ?? '—'}</div>
            <div className="text-xs text-gray-400 mt-1">{rpStats.delivered ?? 0} delivered · {rpStats.undelivered ?? 0} pending</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Generation Cost</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">${Number(rpStats.total_cost || 0).toFixed(4)}</div>
            <div className="text-xs text-gray-400 mt-1">{evStats.tickers_with_events ?? 0} tickers with events</div>
          </div>
        </div>

        {/* Enrich Company */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-purple-700 font-semibold text-sm">Enrich Company Data</span>
            <span className="text-xs text-purple-500">Pull TheirStack, FMP financials, and FMP news for a ticker before generating reports. You may need to run a Trajectory check and a Workforce Intelligence check, if that has not been done. Use the green button to generate a report for any ticker in the system. No signals are required to generate a report with the 'Generate Report' button.</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={enrichTicker}
              onChange={(e) => setEnrichTicker(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleEnrichCompany()}
              placeholder="Enter ticker (e.g. FDX)"
              className="px-3 py-1.5 text-sm border border-purple-300 rounded-lg w-40 bg-white"
            />
            <button
              onClick={handleEnrichCompany}
              disabled={enriching || !enrichTicker.trim()}
              className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {enriching ? 'Enriching...' : 'Enrich'}
            </button>
            <button
              onClick={handleGenerateReportOnDemand}
              disabled={generatingReport || !enrichTicker.trim()}
              className="px-4 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {generatingReport ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
          {enrichResult && (
            <div className="mt-3 text-xs space-y-1">
              {(enrichResult.enriched || []).map((e, i) => (
                <div key={i} className="text-emerald-700">+ {e}</div>
              ))}
              {(enrichResult.skipped || []).map((s, i) => (
                <div key={i} className="text-gray-500">~ {s}</div>
              ))}
              {(enrichResult.errors || []).map((e, i) => (
                <div key={i} className="text-red-600">! {e}</div>
              ))}
              <div className="mt-1 pt-1 border-t border-purple-200">
                <span className="text-emerald-600 font-medium">Available: </span>
                <span className="text-gray-700">{(enrichResult.sources_available || []).join(', ') || 'none'}</span>
                {(enrichResult.sources_missing || []).length > 0 && (
                  <>
                    <span className="text-gray-400 mx-2">|</span>
                    <span className="text-purple-600 font-medium">Still missing: </span>
                    <span className="text-gray-500">{(enrichResult.sources_missing || []).join(', ')}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Controls Row */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={reportsTickerFilter}
            onChange={(e) => setReportsTickerFilter(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && fetchReportsList()}
            placeholder="Filter by ticker..."
            className="px-3 py-1.5 text-sm border rounded-lg w-40"
          />
          <button
            onClick={() => { fetchReportsList(); fetchRecentEventsList(); }}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Refresh
          </button>
          <button
            onClick={handleProcessEvents}
            disabled={processingEvents}
            className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {processingEvents ? 'Processing...' : 'Process Events Now'}
          </button>
          <button
            onClick={() => setShowReportsHelp(true)}
            className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 border border-green-200"
            title="How to use Reports"
          >
            ? Help
          </button>
        </div>

        {/* Recent Events */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Recent Signal Events</h3>
          </div>
          {recentEventsLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading events...</div>
          ) : recentEvents.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No signal events yet. Events are emitted when pipelines run.</div>
          ) : (
            <div className="divide-y max-h-96 overflow-y-auto">
              {recentEvents.map((ev) => (
                <div key={ev.id} className="px-4 py-2.5 flex items-center gap-3 text-sm hover:bg-gray-50">
                  <span className="text-lg" title={ev.event_source}>{sourceIcon(ev.event_source)}</span>
                  <span className="font-mono font-semibold text-gray-900 w-16">{ev.ticker}</span>
                  <span className="text-gray-600 truncate flex-1" title={ev.company_name}>{ev.company_name || ev.ticker}</span>
                  <span className="text-xs text-gray-500 truncate max-w-48">{ev.event_type}</span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded border ${urgencyColor(ev.urgency)}`}>{ev.urgency}</span>
                  <span className="text-xs text-gray-400 w-28 text-right whitespace-nowrap">
                    {ev.created_at ? new Date(ev.created_at).toLocaleDateString() : '—'}
                  </span>
                  {ev.report_generated ? (
                    <span className="text-xs text-emerald-600" title="Report generated">✓</span>
                  ) : (
                    <span className="text-xs text-gray-300" title="Pending">○</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Generated Reports */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">Generated Reports</h3>
          </div>
          {reportsLoading ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading reports...</div>
          ) : reportsList.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No reports generated yet. Reports are created when the Event Processor runs.</div>
          ) : (
            <div className="divide-y">
              {reportsList.map((rpt) => {
                const evSummary = rpt.evidence_summary || {};
                const srcAvail = evSummary.sources_available || rpt.sources_available || [];
                const srcMissing = evSummary.sources_missing || [];
                const hasMissing = srcMissing.length > 0;
                const isSuperseded = !!rpt.superseded_by;
                const ALL_SOURCES = ['SWEE', 'WI', 'TheirStack', 'FMP Financial', 'FMP Employee Count', 'FMP Stock News', 'FMP Press Releases', 'WARN', 'SEC 8-K', 'Layoffs.fyi'];
                const completeness = ALL_SOURCES.length > 0 ? Math.round((srcAvail.length / ALL_SOURCES.length) * 100) : 0;

                return (
                <div key={rpt.id} className={`px-4 py-3 ${isSuperseded ? 'opacity-50' : ''}`}>
                  {/* Report header row */}
                  <div
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 -mx-4 px-4 py-1 rounded"
                    onClick={() => setExpandedReportId(expandedReportId === rpt.id ? null : rpt.id)}
                  >
                    <span className="text-xs text-gray-400">{expandedReportId === rpt.id ? '▼' : '▶'}</span>
                    <span className="font-mono font-semibold text-gray-900 w-16">{rpt.ticker}</span>
                    <span className="text-sm text-gray-700 flex-1 truncate">{rpt.headline || rpt.report_type || 'Signal Report'}</span>

                    {/* Data completeness indicator */}
                    {hasMissing && !isSuperseded ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700 border border-purple-200" title={`Missing: ${srcMissing.join(', ')}`}>
                        {srcAvail.length}/{ALL_SOURCES.length} sources
                      </span>
                    ) : !isSuperseded ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                        Full data
                      </span>
                    ) : null}

                    {isSuperseded && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-500">Superseded</span>
                    )}

                    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${urgencyColor(rpt.urgency)}`}>{rpt.urgency}</span>
                    {rpt.delivered_at ? (
                      <span className="text-xs text-emerald-600">Delivered</span>
                    ) : (
                      <span className="text-xs text-gray-400">Pending delivery</span>
                    )}
                    <span className="text-xs text-gray-400 w-28 text-right whitespace-nowrap">
                      {rpt.created_at ? new Date(rpt.created_at).toLocaleDateString() : '—'}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); window.open(`/admin/swee/report/${rpt.id}`, '_blank'); }}
                      className="px-2 py-0.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100 whitespace-nowrap"
                      title="Open full report in new tab"
                    >
                      View
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const { data } = await axios.get(`${BACKEND_URL}/api/swee/reports/${rpt.id}/audio`, { headers: getHeaders() });
                          if (data?.url) window.open(data.url, '_blank');
                          else setError('No audio URL returned');
                        } catch (err) {
                          setError(err.response?.data?.detail || 'Earnings call audio not available');
                        }
                      }}
                      className="px-2 py-0.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100 whitespace-nowrap"
                      title="Listen to most recent earnings call"
                    >
                      Audio
                    </button>
                  </div>

                  {/* Expanded report content */}
                  {expandedReportId === rpt.id && (
                    <div className="mt-3 ml-6 space-y-4">

                      {/* Missing data warning + Enrich & Regenerate button */}
                      {hasMissing && !isSuperseded && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-3">
                          <div className="flex-1">
                            <div className="text-xs font-semibold text-purple-800 mb-1">Missing data sources</div>
                            <div className="text-xs text-purple-700">{srcMissing.join(' · ')}</div>
                            <div className="text-xs text-purple-600 mt-1">
                              Available: {srcAvail.length > 0 ? srcAvail.join(', ') : 'none'}
                            </div>
                          </div>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              setError(null);
                              setSuccessMsg(null);
                              const btn = e.currentTarget;
                              btn.disabled = true;
                              btn.textContent = 'Enriching...';
                              try {
                                const { data } = await axios.post(
                                  `${BACKEND_URL}/api/swee/reports/${rpt.id}/regenerate`,
                                  {},
                                  { headers: getHeaders() }
                                );
                                setSuccessMsg(`New report generated for ${rpt.ticker} with enriched data`);
                                fetchReportsList();
                              } catch (err) {
                                setError('Regenerate failed: ' + (err.response?.data?.detail || err.message));
                                btn.disabled = false;
                                btn.textContent = 'Enrich & Regenerate';
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                          >
                            Enrich & Regenerate
                          </button>
                        </div>
                      )}

                      {/* Findings badges */}
                      {rpt.findings && rpt.findings.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {rpt.findings.map((f, i) => {
                            const findingColor = {
                              CONTRADICTION: 'bg-red-100 text-red-700',
                              AI_WASHING: 'bg-purple-100 text-purple-700',
                              OMISSION: 'bg-yellow-100 text-yellow-700',
                              LEADING_INDICATOR: 'bg-blue-100 text-blue-700',
                              CORROBORATION: 'bg-green-100 text-green-700',
                              CONTEXT: 'bg-cyan-100 text-cyan-700',
                              CLEAR: 'bg-gray-100 text-gray-500',
                            }[f.finding_type] || 'bg-gray-100 text-gray-600';
                            return (
                              <span key={i} className={`px-2 py-1 text-xs rounded ${findingColor}`} title={f.detail || f.description || ''}>
                                {f.finding_type}{f.sources ? ` (${f.sources.join(', ')})` : ''}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Markdown report body */}
                      {rpt.markdown && (
                        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto border">
                          {rpt.markdown}
                        </div>
                      )}

                      {/* Meta info */}
                      <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                        {rpt.model_used && <span>Model: {rpt.model_used}</span>}
                        {rpt.generation_cost_usd != null && <span>Cost: ${Number(rpt.generation_cost_usd).toFixed(4)}</span>}
                        {rpt.delivered_via && rpt.delivered_via.length > 0 && (
                          <span>Delivered via: {rpt.delivered_via.join(', ')}</span>
                        )}
                        {rpt.event_ids && <span>Events: {Array.isArray(rpt.event_ids) ? rpt.event_ids.length : '—'}</span>}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Reports Help Modal */}
        {showReportsHelp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowReportsHelp(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-xl">
                <h2 className="text-lg font-bold text-gray-900">Reports &mdash; How It Works</h2>
                <button onClick={() => setShowReportsHelp(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
              </div>
              <div className="px-6 py-5 space-y-5 text-sm text-gray-700">

                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Overview</h3>
                  <p>
                    Reports are AI-generated analyses produced when signal events are detected for a tracked company.
                    Each report synthesizes data from up to 9 sources to provide a workforce strategy assessment.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Workflow</h3>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-600">
                    <li><strong>Signal events</strong> are emitted when pipelines run (e.g., transcript scoring, FMP news scan, WARN notices).</li>
                    <li>Click <span className="font-mono text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">Process Events Now</span> to generate reports from pending events.</li>
                    <li>The system auto-enriches each company with missing data before generating the report.</li>
                    <li>Reports appear below with a <span className="font-mono text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">X/10 sources</span> completeness badge.</li>
                    <li>If sources are missing, expand the report and click <span className="font-mono text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Enrich &amp; Regenerate</span> to pull fresh data and create a new version.</li>
                    <li>The old report is marked <span className="font-mono text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">Superseded</span> and dimmed. The new report replaces it.</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Enrich Company Data</h3>
                  <p className="text-gray-600">
                    Use the purple ticker input at the top to manually pull TheirStack, FMP financials, and FMP news
                    for any company <em>before</em> processing events. This is useful if you want full data coverage from the start.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Data Sources (9)</h3>
                  <p className="text-gray-600 mb-2">
                    Each report tracks which of these sources contributed data at generation time.
                    A report with all 9 sources has the most complete picture.
                  </p>
                  <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">#</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">Source</th>
                        <th className="text-left px-3 py-2 font-semibold text-gray-700 border-b">What It Provides</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr><td className="px-3 py-1.5 text-gray-400">1</td><td className="px-3 py-1.5 font-medium">SWEE</td><td className="px-3 py-1.5 text-gray-600">Earnings call transcript scoring &mdash; strategy anchors, signal direction, specificity</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 text-gray-400">2</td><td className="px-3 py-1.5 font-medium">WI</td><td className="px-3 py-1.5 text-gray-600">Workforce Intelligence &mdash; sizing, retention, compensation, composition, risk signals</td></tr>
                      <tr><td className="px-3 py-1.5 text-gray-400">3</td><td className="px-3 py-1.5 font-medium">WARN</td><td className="px-3 py-1.5 text-gray-600">WARN Act layoff notices &mdash; facility closures, mass layoffs, employees affected</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 text-gray-400">4</td><td className="px-3 py-1.5 font-medium">SEC 8-K</td><td className="px-3 py-1.5 text-gray-600">SEC 8-K filings &mdash; material events (restructuring, leadership changes, M&amp;A)</td></tr>
                      <tr><td className="px-3 py-1.5 text-gray-400">5</td><td className="px-3 py-1.5 font-medium">FMP Stock News</td><td className="px-3 py-1.5 text-gray-600">Financial news articles mentioning workforce-relevant keywords</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 text-gray-400">6</td><td className="px-3 py-1.5 font-medium">FMP Press Releases</td><td className="px-3 py-1.5 text-gray-600">Company press releases &mdash; expansion, layoffs, hiring announcements</td></tr>
                      <tr><td className="px-3 py-1.5 text-gray-400">7</td><td className="px-3 py-1.5 font-medium">FMP Financial</td><td className="px-3 py-1.5 text-gray-600">Financial metrics &mdash; revenue, cash flow, margins, EPS, capex trends</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 text-gray-400">8</td><td className="px-3 py-1.5 font-medium">FMP Employee Count</td><td className="px-3 py-1.5 text-gray-600">Historical headcount from 10-K filings &mdash; YoY changes and trends</td></tr>
                      <tr><td className="px-3 py-1.5 text-gray-400">9</td><td className="px-3 py-1.5 font-medium">TheirStack</td><td className="px-3 py-1.5 text-gray-600">Job posting tracker &mdash; active openings, 30-day velocity, seniority mix</td></tr>
                      <tr className="bg-gray-50"><td className="px-3 py-1.5 text-gray-400">10</td><td className="px-3 py-1.5 font-medium">Layoffs.fyi</td><td className="px-3 py-1.5 text-gray-600">Crowdsourced layoff tracker &mdash; headcount, percentage, cross-validated with WARN filings</td></tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Tips</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Not every company will have all 10 sources &mdash; some sources only appear when relevant data exists (e.g., WARN notices are rare for most companies).</li>
                    <li>Reports with fewer sources are still useful but carry lower confidence. The report text notes which sources are missing.</li>
                    <li>Enriching pulls the latest available data. If a source still shows as missing after enrichment, the data simply doesn't exist for that company.</li>
                    <li>Superseded reports are kept for audit history but dimmed in the list. Only the latest version is considered current.</li>
                  </ul>
                </div>

              </div>
              <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-3 rounded-b-xl">
                <button
                  onClick={() => setShowReportsHelp(false)}
                  className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  };

  // =============================================
  // Render: LinkedIn Insights Tab
  // =============================================

  const renderLinkedInInsights = () => {
    return (
      <div className="space-y-6">
        {/* Help / Info Toggle */}
        {showLiHelp && (
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-sky-800">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold mb-1">LinkedIn Company Insights (RICEMAN Actor)</p>
                <ul className="list-disc ml-4 space-y-1 text-xs">
                  <li>Fetches headcount, growth trends, hiring breakdown, tenure, job openings from LinkedIn</li>
                  <li>Cost: ~$0.19 per company lookup (company data + jobs + insights)</li>
                  <li>URL resolved via <code>linkedin_company_directory</code> (7M+ companies) or <code>swee_company_profile.linkedin_company_url</code></li>
                  <li>Monthly refresh re-fetches tracked companies not updated in 25+ days</li>
                  <li>Signals emitted when 6-month growth exceeds ±5%</li>
                </ul>
              </div>
              <button onClick={() => setShowLiHelp(false)} className="text-sky-400 hover:text-sky-600 ml-3">✕</button>
            </div>
          </div>
        )}

        {/* Stats Row */}
        {liStatsLoading ? <LoadingSpinner /> : liStats && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Pipeline Overview</h3>
              <button onClick={() => setShowLiHelp(!showLiHelp)}
                className="text-xs text-sky-600 hover:text-sky-800">
                {showLiHelp ? 'Hide Help' : 'How It Works'}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <StatCard label="Companies Tracked" value={liStats.companies_tracked} color="sky" />
              <StatCard label="Total Snapshots" value={liStats.total_snapshots} color="sky" />
              <StatCard label="Total Cost" value={`$${liStats.total_cost_usd?.toFixed(2) || '0.00'}`} color="sky" />
              <StatCard label="Companies w/ URL" value={liStats.companies_with_linkedin_url} color="sky" />
              <StatCard label="Pipeline" value={liStats.enabled ? 'Enabled' : 'Disabled'}
                color={liStats.enabled ? 'emerald' : 'gray'} />
              <StatCard label="Monthly Refresh" value={liStats.monthly_refresh_enabled ? 'On' : 'Off'}
                color={liStats.monthly_refresh_enabled ? 'emerald' : 'gray'} />
            </div>
            {liStats.last_refresh && (
              <p className="text-xs text-gray-400 mt-2">
                Last refresh: {new Date(liStats.last_refresh).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* On-Demand Lookup (tracked companies) */}
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-sky-700 mb-3">Fetch Company Insights (Tracked)</h3>
          <p className="text-xs text-gray-500 mb-3">Enter a ticker from the SWEE watchlist. Costs ~$0.19 per lookup.</p>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              value={liLookupTicker}
              onChange={(e) => setLiLookupTicker(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && fetchLiLookup()}
              placeholder="e.g. AAPL"
              className="border rounded px-3 py-2 text-sm w-32 uppercase"
            />
            <button
              onClick={fetchLiLookup}
              disabled={liLookupLoading || !liLookupTicker.trim()}
              className="bg-sky-600 text-white px-4 py-2 rounded text-sm hover:bg-sky-700 disabled:opacity-50"
            >
              {liLookupLoading ? 'Fetching...' : 'Fetch Insights'}
            </button>
          </div>
          {liLookupResult && (
            <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded text-sm">
              {liLookupResult.status === 'success' ? (
                <div className="space-y-1">
                  <p className="font-semibold text-sky-800">
                    {liLookupResult.ticker || liLookupTicker} — {liLookupResult.employee_count?.toLocaleString() || 'N/A'} employees
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-sky-700">
                    <span>6m Growth: {liLookupResult.growth_6m_pct != null ? `${liLookupResult.growth_6m_pct}%` : 'N/A'}</span>
                    <span>1y Growth: {liLookupResult.growth_1y_pct != null ? `${liLookupResult.growth_1y_pct}%` : 'N/A'}</span>
                    <span>Job Openings: {liLookupResult.total_job_openings ?? 'N/A'}</span>
                    <span>Tenure: {liLookupResult.median_tenure_months ? `${liLookupResult.median_tenure_months}mo` : 'N/A'}</span>
                  </div>
                  <button
                    onClick={() => fetchLiHistory(liLookupResult.ticker || liLookupTicker)}
                    className="mt-2 text-xs text-sky-600 hover:text-sky-800 underline"
                  >
                    View History & Breakdown →
                  </button>
                </div>
              ) : (
                <p className="text-red-600">{liLookupResult.message || 'Lookup failed'}</p>
              )}
            </div>
          )}
        </div>

        {/* Untracked Company Lookup */}
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-sky-700 mb-3">Lookup Untracked Company</h3>
          <p className="text-xs text-gray-500 mb-3">
            Look up a company NOT on the watchlist. Resolves LinkedIn URL from the 7M company directory. Results are not stored.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              value={liUntrackedName}
              onChange={(e) => setLiUntrackedName(e.target.value)}
              placeholder="Company name (required)"
              className="border rounded px-3 py-2 text-sm w-48"
            />
            <input
              type="text"
              value={liUntrackedDomain}
              onChange={(e) => setLiUntrackedDomain(e.target.value)}
              placeholder="Domain (optional, e.g. apple.com)"
              className="border rounded px-3 py-2 text-sm w-52"
            />
            <button
              onClick={fetchLiUntracked}
              disabled={liUntrackedLoading || !liUntrackedName.trim()}
              className="bg-sky-600 text-white px-4 py-2 rounded text-sm hover:bg-sky-700 disabled:opacity-50"
            >
              {liUntrackedLoading ? 'Searching...' : 'Lookup'}
            </button>
          </div>
          {liUntrackedResult && (
            <div className="mt-4 p-3 bg-sky-50 border border-sky-200 rounded text-sm">
              {liUntrackedResult.status === 'success' ? (
                <div className="space-y-1">
                  <p className="font-semibold text-sky-800">
                    {liUntrackedResult.company_name} — {liUntrackedResult.snapshot?.employee_count?.toLocaleString() || 'N/A'} employees
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-sky-700">
                    <span>6m Growth: {liUntrackedResult.snapshot?.growth_6m_pct != null ? `${liUntrackedResult.snapshot.growth_6m_pct}%` : 'N/A'}</span>
                    <span>1y Growth: {liUntrackedResult.snapshot?.growth_1y_pct != null ? `${liUntrackedResult.snapshot.growth_1y_pct}%` : 'N/A'}</span>
                    <span>Job Openings: {liUntrackedResult.snapshot?.total_job_openings ?? 'N/A'}</span>
                    <span>Tenure: {liUntrackedResult.snapshot?.median_tenure_months ? `${liUntrackedResult.snapshot.median_tenure_months}mo` : 'N/A'}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">LinkedIn: {liUntrackedResult.linkedin_url}</p>
                </div>
              ) : (
                <p className="text-amber-700">{liUntrackedResult.message}</p>
              )}
            </div>
          )}
        </div>

        {/* Snapshot History & Hiring Breakdown */}
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-sky-700 mb-3">Snapshot History & Hiring Breakdown</h3>
          <div className="flex gap-2 items-center mb-4">
            <input
              type="text"
              value={liHistoryTicker}
              onChange={(e) => setLiHistoryTicker(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && fetchLiHistory()}
              placeholder="Ticker"
              className="border rounded px-3 py-2 text-sm w-32 uppercase"
            />
            <button
              onClick={() => fetchLiHistory()}
              disabled={liHistoryLoading || !liHistoryTicker.trim()}
              className="bg-sky-600 text-white px-4 py-2 rounded text-sm hover:bg-sky-700 disabled:opacity-50"
            >
              {liHistoryLoading ? 'Loading...' : 'Load History'}
            </button>
          </div>

          {(liHistoryLoading || liBreakdownLoading) && <LoadingSpinner />}

          {/* Employee Count Trend Chart */}
          {liHistory && liHistory.snapshots && liHistory.snapshots.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-gray-600 mb-2">
                Employee Count Trend — {liHistory.ticker} ({liHistory.count} snapshots)
              </h4>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={[...liHistory.snapshots].reverse().map(s => ({
                  date: new Date(s.snapshot_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                  employees: s.employee_count,
                  jobOpenings: s.total_job_openings,
                  growth6m: s.growth_6m_pct,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="employees" stroke="#0ea5e9" name="Employees" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="jobOpenings" stroke="#8b5cf6" name="Job Openings" strokeWidth={1.5} dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Snapshot Data Table */}
          {liHistory && liHistory.snapshots && liHistory.snapshots.length > 0 && (
            <div className="mb-6 overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-sky-50 text-sky-800">
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-right">Employees</th>
                    <th className="px-3 py-2 text-right">6m Growth</th>
                    <th className="px-3 py-2 text-right">1y Growth</th>
                    <th className="px-3 py-2 text-right">Jobs Open</th>
                    <th className="px-3 py-2 text-right">New Hires</th>
                    <th className="px-3 py-2 text-right" title="Median employee tenure in months — how long employees typically stay at this company">
                      Tenure (mo) <span className="text-sky-400 text-[10px] cursor-help">&#9432;</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {liHistory.snapshots.map((s, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-1.5">{new Date(s.snapshot_date).toLocaleDateString()}</td>
                      <td className="px-3 py-1.5 text-right">{s.employee_count?.toLocaleString() ?? '—'}</td>
                      <td className={`px-3 py-1.5 text-right ${s.growth_6m_pct > 0 ? 'text-emerald-600' : s.growth_6m_pct < 0 ? 'text-red-600' : ''}`}>
                        {s.growth_6m_pct != null ? `${s.growth_6m_pct > 0 ? '+' : ''}${s.growth_6m_pct}%` : '—'}
                      </td>
                      <td className={`px-3 py-1.5 text-right ${s.growth_1y_pct > 0 ? 'text-emerald-600' : s.growth_1y_pct < 0 ? 'text-red-600' : ''}`}>
                        {s.growth_1y_pct != null ? `${s.growth_1y_pct > 0 ? '+' : ''}${s.growth_1y_pct}%` : '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right">{s.total_job_openings ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right">{s.new_hires_count ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right">{s.median_tenure_months ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {liHistory && liHistory.snapshots && liHistory.snapshots.length === 0 && (
            <EmptyState message={`No snapshots found for ${liHistory.ticker}. Fetch insights first.`} />
          )}

          {/* Hiring Breakdown Chart */}
          {liBreakdown && liBreakdown.breakdown && liBreakdown.breakdown.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 mb-2">
                Headcount by Function — {liBreakdown.ticker}
              </h4>
              <ResponsiveContainer width="100%" height={Math.max(200, liBreakdown.breakdown.length * 28)}>
                <BarChart
                  layout="vertical"
                  data={liBreakdown.breakdown.map(b => ({
                    name: b.function_name,
                    count: b.employee_count,
                    pct: b.percentage,
                  }))}
                  margin={{ left: 10, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={140} />
                  <Tooltip formatter={(val) => val != null ? val.toLocaleString() : '—'} />
                  <Bar dataKey="count" fill="#0ea5e9" name="Headcount" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Monthly Refresh */}
        <div className="bg-white rounded-lg shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-sky-700 mb-2">Monthly Refresh</h3>
          <p className="text-xs text-gray-500 mb-3">
            Re-fetch insights for all tracked companies not updated in 25+ days. Requires both pipeline and monthly refresh toggles enabled in Platform Settings.
          </p>
          <button
            onClick={handleLiMonthlyRefresh}
            disabled={liRefreshRunning}
            className="bg-sky-600 text-white px-4 py-2 rounded text-sm hover:bg-sky-700 disabled:opacity-50"
          >
            {liRefreshRunning ? 'Refreshing...' : 'Run Monthly Refresh'}
          </button>
          {liRefreshResult && (
            <div className="mt-3 p-3 bg-sky-50 border border-sky-200 rounded text-sm">
              <p className="font-semibold text-sky-800 mb-1">
                Refresh Complete: {liRefreshResult.refreshed}/{liRefreshResult.total_companies} companies
              </p>
              {liRefreshResult.errors > 0 && (
                <p className="text-red-600 text-xs">{liRefreshResult.errors} errors</p>
              )}
              {liRefreshResult.total_cost > 0 && (
                <p className="text-xs text-sky-700">Total cost: ${liRefreshResult.total_cost?.toFixed(2)}</p>
              )}
              {liRefreshResult.details && liRefreshResult.details.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-sky-600 cursor-pointer">Show details ({liRefreshResult.details.length})</summary>
                  <div className="mt-1 max-h-48 overflow-y-auto">
                    {liRefreshResult.details.map((d, i) => (
                      <div key={i} className="text-xs py-0.5 flex gap-2">
                        <span className="font-mono">{d.ticker}</span>
                        <span className={d.status === 'success' ? 'text-emerald-600' : d.status === 'error' ? 'text-red-600' : 'text-gray-500'}>
                          {d.status}{d.employee_count ? ` (${d.employee_count.toLocaleString()} emp)` : ''}
                          {d.growth_6m != null ? ` ${d.growth_6m > 0 ? '+' : ''}${d.growth_6m}%` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // =============================================
  // Render: Calibration Tab
  // =============================================

  const renderCalibration = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Human-in-the-Loop Calibration</h3>
          <p className="text-sm text-gray-500 mb-4">
            Compare model scores against human judgment. Select scored chunks for manual review, rate them 1-10,
            and submit to build a calibration dataset for quality tracking.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              Calibration UI will be fully functional after the first backfill is completed and chunks are scored.
              Use the Pipeline tab to initiate a backfill first.
            </p>
          </div>
        </div>

        {/* Centroid Cross-Overlap Check */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Centroid Health Check</h3>
          <p className="text-xs text-gray-500 mb-3">
            After initializing centroids, verify that no two anchors are too similar (pairwise cosine similarity should be &lt; 0.75).
          </p>
          {centroids && centroids.length > 0 ? (
            <div className="text-sm text-green-700 bg-green-50 p-3 rounded">
              {centroids.length} centroids loaded. Cross-overlap validation available after embedding generation.
            </div>
          ) : (
            <div className="text-sm text-gray-400">Initialize centroids first.</div>
          )}
        </div>
      </div>
    );
  };

  // =============================================
  // Main Render
  // =============================================

  if (permLoading) return <LoadingSpinner text="Loading permissions..." />;

  // Permission gate: redirect if admin doesn't have SWEE access
  if (!canAccessSectionSync(ADMIN_SECTIONS.SWEE, permissions)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 max-w-md text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Access Restricted</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">You do not have permission to access SWEE. Contact a system administrator for access.</p>
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SWEE</h1>
            <p className="text-sm text-gray-500">Strategic Workforce Evolution Engine</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/admin')}
              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              Admin Home
            </button>
            <button onClick={() => navigate('/admin/executive-intelligence')}
              className="px-3 py-1.5 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200">
              Exec Intelligence
            </button>
            <button onClick={() => navigate('/admin/crm')}
              className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
              CRM
            </button>
          </div>
        </div>

        {/* Error / Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">x</button>
          </div>
        )}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex justify-between items-center">
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg(null)} className="text-green-500 hover:text-green-700 ml-2">x</button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-1 overflow-x-auto pb-0 -mb-px">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => changeTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-1.5">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'calendar' && renderCalendar()}
        {activeTab === 'companies' && renderCompanies()}
        {activeTab === 'trajectory' && renderTrajectory()}
        {activeTab === 'heatmap' && renderHeatmap()}
        {activeTab === 'search' && renderSearch()}
        {activeTab === 'wi' && renderWI()}
        {activeTab === 'tracker' && renderTracker()}
        {activeTab === 'signals' && renderSignals()}
        {activeTab === 'alerts' && renderAlerts()}
        {activeTab === 'pipeline' && renderPipeline()}
        {activeTab === 'reports' && renderReports()}
        {activeTab === 'linkedin-insights' && renderLinkedInInsights()}
        {activeTab === 'calibration' && renderCalibration()}
      </div>
    </div>
  );
};


// =============================================
// Shared Sub-Components
// =============================================

const LoadingSpinner = ({ text }) => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mr-3"></div>
    <span className="text-gray-500 text-sm">{text}</span>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="text-center py-12">
    <p className="text-gray-400 text-sm">{text}</p>
  </div>
);

const StatCard = ({ label, value, sub, color = 'gray' }) => {
  const colorMap = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
  };
  return (
    <div className={`rounded-lg border p-4 ${colorMap[color] || colorMap.gray}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium mt-1">{label}</div>
      {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

// Format large dollar amounts (e.g., 5200000000 → "$5.2B")
const fmtDollar = (val) => {
  if (val == null) return '—';
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(1)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${val}`;
};

// Custom tooltip formatter for charts
const dollarTooltip = (value) => {
  if (value == null) return '—';
  return `$${Math.abs(value).toLocaleString()}`;
};

const pctTooltip = (value) => {
  if (value == null) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

const FinancialChart = ({ data, mode, range = 'recent' }) => {
  if (!data || data.length === 0) return null;

  // Apply range filter — 'recent' shows 2020+, 'full' shows all (2015+)
  const minYear = range === 'full' ? 2015 : 2020;
  const rangeFiltered = data.filter(d => {
    const year = parseInt(d.fiscal_period.split('-')[0], 10);
    return year >= minYear;
  });

  // Prepare chart data — filter to quarterly only for cleaner trend lines
  // (FY periods would overlap with Q4 and distort the chart)
  const chartData = rangeFiltered
    .filter(d => d.fiscal_period.includes('-Q'))
    .map(d => ({
      period: d.fiscal_period,
      revenue: d.revenue,
      gross_profit: d.gross_profit,
      net_income: d.net_income,
      cost_of_revenue: d.cost_of_revenue,
      sga_expense: d.sga_expense,
      operating_cash_flow: d.operating_cash_flow,
      free_cash_flow: d.free_cash_flow,
      capital_expenditure: d.capital_expenditure,
      dividends_paid: d.dividends_paid,
      share_buybacks: d.share_buybacks,
      fcf_yield: d.fcf_yield,
      fcf_conversion_ratio: d.fcf_conversion_ratio,
      capex_coverage_ratio: d.capex_coverage_ratio,
      fcf_per_employee: d.fcf_per_employee,
      employee_count: d.employee_count,
      eps_diluted: d.eps_diluted,
      eps_basic: d.eps_basic,
      sga_revenue_ratio: d.sga_revenue_ratio,
      revenue_per_employee: d.revenue_per_employee,
      ocf_per_employee: d.ocf_per_employee,
      cash_and_equivalents: d.cash_and_equivalents,
      emp_interpolated: d.employee_count_interpolated,
    }));

  if (chartData.length === 0) return <div className="text-xs text-gray-400 py-4">No quarterly data available for charting.</div>;

  const CHART_HEIGHT = 320;
  const VIOLET = '#7c3aed';
  const BLUE = '#2563eb';
  const EMERALD = '#059669';
  const AMBER = '#d97706';
  const RED = '#dc2626';
  const GRAY = '#6b7280';

  const formatYAxis = (val) => fmtDollar(val);
  const formatPctAxis = (val) => val != null ? `${(val * 100).toFixed(0)}%` : '';
  const formatCountAxis = (val) => val != null ? val.toLocaleString() : '';

  if (mode === 'revenue') {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-2">Revenue, Gross Profit, and Net Income over time (quarterly)</p>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={60} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => dollarTooltip(v)} labelStyle={{ fontSize: 11, fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="revenue" name="Revenue" fill={BLUE} radius={[2,2,0,0]} />
            <Bar dataKey="gross_profit" name="Gross Profit" fill={EMERALD} radius={[2,2,0,0]} />
            <Bar dataKey="net_income" name="Net Income" fill={VIOLET} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (mode === 'profitability') {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-2">Cost structure: COGS, SG&A, and Operating Cash Flow (quarterly)</p>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={60} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => dollarTooltip(v)} labelStyle={{ fontSize: 11, fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="#ccc" />
            <Line type="monotone" dataKey="revenue" name="Revenue" stroke={BLUE} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="cost_of_revenue" name="COGS" stroke={RED} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="sga_expense" name="SG&A" stroke={AMBER} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="operating_cash_flow" name="Op. Cash Flow" stroke={EMERALD} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (mode === 'cashflow') {
    // Use annual (FY) data for FCF — FMP only provides annual cash flow data
    const annualCfData = rangeFiltered
      .filter(d => d.fiscal_period.includes('-FY') && (d.free_cash_flow != null || d.operating_cash_flow != null || d.cash_and_equivalents != null))
      .map(d => ({
        period: d.fiscal_period.replace('-FY', ''),
        operating_cash_flow: d.operating_cash_flow,
        free_cash_flow: d.free_cash_flow,
        capital_expenditure: d.capital_expenditure ? Math.abs(d.capital_expenditure) : null,
        dividends_paid: d.dividends_paid ? Math.abs(d.dividends_paid) : null,
        share_buybacks: d.share_buybacks ? Math.abs(d.share_buybacks) : null,
        cash_and_equivalents: d.cash_and_equivalents,
        fcf_yield: d.fcf_yield,
        fcf_conversion_ratio: d.fcf_conversion_ratio,
        fcf_per_employee: d.fcf_per_employee,
      }));

    if (annualCfData.length === 0) {
      return <div className="text-xs text-gray-400 py-4">No annual cash flow data available. Click "Fetch from EDGAR" to pull data including FMP cash flow metrics.</div>;
    }

    const TEAL = '#0d9488';
    const ROSE = '#e11d48';
    const INDIGO = '#4f46e5';

    return (
      <div>
        <p className="text-xs text-gray-500 mb-2">Cash Position, Free Cash Flow, Operating Cash Flow, and Capital Expenditure (annual)</p>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={annualCfData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => dollarTooltip(v)} labelStyle={{ fontSize: 11, fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="#ccc" />
            <Bar dataKey="cash_and_equivalents" name="Cash & Equivalents" fill={VIOLET} radius={[2,2,0,0]} />
            <Bar dataKey="operating_cash_flow" name="Operating Cash Flow" fill={BLUE} radius={[2,2,0,0]} />
            <Bar dataKey="free_cash_flow" name="Free Cash Flow" fill={EMERALD} radius={[2,2,0,0]} />
            <Bar dataKey="capital_expenditure" name="Capex (abs)" fill={AMBER} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>

        {/* Capital allocation and ratios */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* Capital allocation: dividends + buybacks */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Capital Allocation: Dividends & Buybacks (abs values)</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={annualCfData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 9 }} interval={0} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => dollarTooltip(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="dividends_paid" name="Dividends" fill={TEAL} radius={[2,2,0,0]} />
                <Bar dataKey="share_buybacks" name="Buybacks" fill={INDIGO} radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* FCF Yield & Conversion */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">FCF Yield & Conversion Ratio</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={annualCfData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 9 }} interval={0} />
                <YAxis tickFormatter={formatPctAxis} tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => pctTooltip(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="fcf_yield" name="FCF Yield" stroke={EMERALD} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="fcf_conversion_ratio" name="FCF Conversion" stroke={ROSE} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* FCF per Employee */}
        {annualCfData.some(d => d.fcf_per_employee != null) && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-600 mb-1">Free Cash Flow per Employee</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={annualCfData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => dollarTooltip(v)} />
                <ReferenceLine y={0} stroke="#ccc" />
                <Bar dataKey="fcf_per_employee" name="FCF / Employee" fill={EMERALD} radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 italic mt-1">* Declining FCF/employee can signal upcoming headcount reductions; growing FCF/employee can signal hiring capacity.</p>
          </div>
        )}
      </div>
    );
  }

  if (mode === 'efficiency') {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-2">Efficiency ratios: SG&A (Sales, General & Administrative Expense)/Revenue, Revenue/Employee, OCF/Employee (quarterly)</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* SG&A / Revenue ratio */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">SG&A as % of Revenue</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 9 }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={50} />
                <YAxis tickFormatter={formatPctAxis} tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => pctTooltip(v)} />
                <Line type="monotone" dataKey="sga_revenue_ratio" name="SG&A / Revenue" stroke={AMBER} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Revenue per Employee */}
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">Revenue per Employee</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" tick={{ fontSize: 9 }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={50} />
                <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 9 }} />
                <Tooltip formatter={(v) => dollarTooltip(v)} />
                <Line type="monotone" dataKey="revenue_per_employee" name="Rev / Employee" stroke={BLUE} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 italic mt-1">* Quarterly figures are annualized (quarterly revenue x 4 / employee count) to produce a comparable annual rate.</p>
          </div>
        </div>
        {/* OCF per Employee */}
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-600 mb-1">Operating Cash Flow per Employee</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} interval="preserveStartEnd" angle={-45} textAnchor="end" height={50} />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => dollarTooltip(v)} />
              <ReferenceLine y={0} stroke="#ccc" />
              <Line type="monotone" dataKey="ocf_per_employee" name="OCF / Employee" stroke={EMERALD} strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  if (mode === 'workforce') {
    // Use annual (FY) data only — employee count is reported once per year in 10-K filings
    const annualData = rangeFiltered
      .filter(d => d.fiscal_period.includes('-FY') && d.employee_count)
      .map(d => ({
        period: d.fiscal_period.replace('-FY', ''),
        employee_count: d.employee_count,
      }));

    if (annualData.length === 0) return <div className="text-xs text-gray-400 py-4">No annual employee data available.</div>;

    return (
      <div>
        <p className="text-xs text-gray-500 mb-2">
          Employee headcount over time (annual 10-K filings).
        </p>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={annualData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} />
            <YAxis tickFormatter={formatCountAxis} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => v ? v.toLocaleString() : '—'} labelStyle={{ fontSize: 11, fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="employee_count"
              name="Employee Count"
              fill={VIOLET}
              radius={[2,2,0,0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (mode === 'eps') {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-2">Earnings Per Share — diluted and basic (quarterly)</p>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${v?.toFixed(2) || '0'}`} />
            <Tooltip formatter={(v) => v != null ? `$${v.toFixed(2)}` : '—'} labelStyle={{ fontSize: 11, fontWeight: 600 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="#ccc" />
            <Line type="monotone" dataKey="eps_diluted" name="EPS (Diluted)" stroke={VIOLET} strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line type="monotone" dataKey="eps_basic" name="EPS (Basic)" stroke={GRAY} strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
};

const AnchorBadge = ({ anchor }) => {
  const colors = ANCHOR_COLORS[anchor] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
  return (
    <span className={`inline-flex text-xs px-2 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
      {anchor}
    </span>
  );
};

const SignalSourceBadge = ({ signal }) => {
  const colorClass = SOURCE_COLORS[signal.source] || 'bg-gray-100 text-gray-700 border-gray-300';
  const tooltip = signal.headline
    ? `${signal.label}${signal.date ? ` (${signal.date})` : ''}\n${signal.headline}`
    : `${signal.label}${signal.date ? ` (${signal.date})` : ''}`;
  return (
    <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded border ${colorClass} whitespace-nowrap`} title={tooltip}>
      {signal.source}
    </span>
  );
};

const VerdictBadge = ({ verdict }) => {
  const colorClass = VERDICT_COLORS[verdict] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex text-xs px-2 py-0.5 rounded ${colorClass}`}>
      {verdict}
    </span>
  );
};

const WIChunkCard = ({ chunk, expanded, onToggle, onTickerClick }) => {
  const catColors = WI_CATEGORY_COLORS[chunk.primary_category] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300' };
  const dirColors = WI_DIRECTION_COLORS[chunk.signal_direction] || 'bg-gray-100 text-gray-600';
  const sigColors = WI_SIGNIFICANCE_COLORS[chunk.operational_significance] || 'bg-gray-100 text-gray-600';

  return (
    <div className="p-4 bg-white rounded-lg border shadow-sm text-sm">
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => onTickerClick(chunk.ticker)}
            className="font-mono font-bold text-indigo-700 hover:underline cursor-pointer">{chunk.ticker}</button>
          <span className="text-xs text-gray-500">{chunk.fiscal_period}</span>
          {/* Category badge */}
          <span className={`inline-flex text-xs px-2 py-0.5 rounded border font-medium ${catColors.bg} ${catColors.text} ${catColors.border}`}>
            {WI_CATEGORY_LABELS[chunk.primary_category] || chunk.primary_category}
          </span>
          {/* Secondary categories */}
          {chunk.secondary_categories?.length > 0 && chunk.secondary_categories.map(sc => (
            <span key={sc} className="inline-flex text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 border border-gray-200">
              {WI_CATEGORY_LABELS[sc] || sc}
            </span>
          ))}
          {/* Gate relevance */}
          {chunk.gate_relevance === 'WORKFORCE_ADJACENT' && (
            <span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200">ADJACENT</span>
          )}
          {/* SWEE cross-ref */}
          {chunk.has_swee_match && (
            <span className="inline-flex text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200">SWEE</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Significance badge */}
          <span className={`text-xs font-medium px-2 py-0.5 rounded border ${sigColors}`}>
            {chunk.operational_significance === 'MATERIAL_EVENT' ? 'MATERIAL' :
             chunk.operational_significance === 'NOTABLE_CHANGE' ? 'NOTABLE' :
             chunk.operational_significance === 'ROUTINE' ? 'ROUTINE' : chunk.operational_significance}
          </span>
          {/* Direction badge */}
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${dirColors}`}>
            {chunk.signal_direction}
          </span>
          {/* Score */}
          <span className={`font-bold px-2 py-0.5 rounded ${getScoreColor(chunk.specificity_score)}`}>
            {chunk.specificity_score}
            {chunk.raw_score && chunk.raw_score !== chunk.specificity_score && (
              <span className="text-xs font-normal text-gray-400 ml-0.5">/{chunk.raw_score}</span>
            )}
          </span>
          {/* Temporal */}
          {chunk.temporal_stance && (
            <span className="text-xs text-gray-400">{chunk.temporal_stance}</span>
          )}
        </div>
      </div>

      {/* Speaker */}
      {chunk.speaker_name && (
        <div className="flex items-center gap-1.5 mb-2 text-xs">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="font-medium text-gray-700">{chunk.speaker_name}</span>
          {chunk.speaker_role && <span className="text-gray-500">({chunk.speaker_role})</span>}
          {chunk.call_section && <span className="text-gray-400 ml-1">| {chunk.call_section}</span>}
        </div>
      )}

      {/* Summary */}
      {chunk.summary && (
        <div className="mb-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded text-xs text-indigo-800">
          <span className="font-semibold">Summary:</span> {chunk.summary}
        </div>
      )}

      {/* Significance reasoning */}
      {chunk.significance_reasoning && (
        <div className="mb-2 text-xs text-gray-500 italic">
          {chunk.significance_reasoning}
        </div>
      )}

      {/* Centroid match info */}
      <div className="flex items-center gap-3 mb-2 text-xs text-gray-400">
        <span>Gate: {chunk.wi_centroid_match}</span>
        {chunk.wi_anchor_similarity && <span>Sim: {chunk.wi_anchor_similarity}</span>}
      </div>

      {/* Chunk text (truncated or full) */}
      {chunk.chunk_text && (
        <div className="cursor-pointer" onClick={onToggle}>
          <p className={`text-xs text-gray-600 ${expanded ? '' : 'line-clamp-3'}`}>{chunk.chunk_text}</p>
          <span className="text-xs text-indigo-500 hover:underline">{expanded ? 'Show less' : 'Show more'}</span>
        </div>
      )}
    </div>
  );
};


const ChunkCard = ({ chunk, showTicker = false }) => {
  // Parse evidence into structured array
  const evidenceItems = (() => {
    if (!chunk.evidence) return [];
    try {
      const parsed = typeof chunk.evidence === 'string' ? JSON.parse(chunk.evidence) : chunk.evidence;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  })();

  // Evidence type icons
  const typeIcons = {
    NUMBERS: '\u{1F4CA}',    // bar chart
    PROGRAMS: '\u{1F3AF}',   // target
    MONEY: '\u{1F4B0}',      // money bag
    TOOLS: '\u{1F527}',      // wrench
    TIMELINES: '\u{1F4C5}',  // calendar
    COMPARISONS: '\u{1F504}' // arrows
  };

  return (
    <div className="p-4 bg-gray-50 rounded-lg border text-sm">
      {/* Header row: badges + score */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {showTicker && <span className="font-mono font-bold text-emerald-700">{chunk.ticker}</span>}
          <span className="text-xs text-gray-500">{chunk.fiscal_period}</span>
          <AnchorBadge anchor={chunk.primary_anchor} />
          <VerdictBadge verdict={chunk.verdict} />
          {chunk.anchor_relevant && chunk.anchor_relevant !== 'RELEVANT' && (
            <span className={`inline-flex text-xs px-1.5 py-0.5 rounded ${
              chunk.anchor_relevant === 'IRRELEVANT' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'
            }`}>{chunk.anchor_relevant}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`font-bold px-2 py-0.5 rounded ${getScoreColor(chunk.specificity_score)}`}>
            {chunk.specificity_score}
          </span>
          {chunk.signal_direction && (
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              chunk.signal_direction === 'POSITIVE' ? 'bg-green-100 text-green-700' :
              chunk.signal_direction === 'NEGATIVE' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-600'
            }`}>{chunk.signal_direction}</span>
          )}
          {chunk.temporal_stance && (
            <span className="text-xs text-gray-400">{chunk.temporal_stance}</span>
          )}
        </div>
      </div>

      {/* Speaker attribution */}
      {chunk.speaker_name && (
        <div className="flex items-center gap-1.5 mb-2 text-xs">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="font-medium text-gray-700">{chunk.speaker_name}</span>
          {chunk.speaker_role && <span className="text-gray-500">({chunk.speaker_role})</span>}
          {chunk.call_section && (
            <span className="text-gray-400 ml-1">| {chunk.call_section}</span>
          )}
        </div>
      )}

      {/* AI Summary */}
      {chunk.summary && (
        <div className="mb-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
          <span className="font-semibold">Analysis:</span> {chunk.summary}
        </div>
      )}

      {/* Transcript excerpt */}
      {chunk.chunk_text && (
        <p className="text-xs text-gray-600 line-clamp-3 mb-2">{chunk.chunk_text}</p>
      )}

      {/* Structured evidence */}
      {evidenceItems.length > 0 && (
        <div className="mt-2 border-t border-gray-200 pt-2">
          <div className="text-xs font-medium text-gray-500 mb-1">Key Evidence</div>
          <div className="space-y-1">
            {evidenceItems.map((item, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs">
                <span className="text-gray-400 flex-shrink-0 w-4 text-center">
                  {typeIcons[item.type] || '\u{2022}'}
                </span>
                <span className="text-gray-400 font-mono flex-shrink-0 w-24">
                  {item.type}
                </span>
                <span className="text-gray-700">{item.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification links */}
      {(chunk.koyfin_link || chunk.edgar_link) && (
        <div className="mt-2 pt-2 border-t border-gray-200 flex items-center gap-2">
          <span className="text-xs text-gray-400">Verify:</span>
          {chunk.koyfin_link && (
            <a href={chunk.koyfin_link} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Koyfin
            </a>
          )}
          {chunk.edgar_link && (
            <a href={chunk.edgar_link} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              SEC EDGAR
            </a>
          )}
        </div>
      )}
    </div>
  );
};


export default AdminSWEE;
